import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, Box, Layers, Users, Settings, Shield, Truck, 
  Plus, Edit, Trash2, ChevronDown, ChevronRight, Save, X, Ticket,
  Upload, Image as ImageIcon, Loader2, FolderOpen, ImagePlay, FileText,
  Phone, Mail, MapPin, Instagram, Facebook, MessageCircle, ShoppingBag,
  Menu, LogOut, Home, Wallet, Package, CreditCard, Globe, Palette,
  Database, Bell, Search, MoreVertical, Eye, EyeOff, Copy, ExternalLink,
  TrendingUp, DollarSign, Target, Award, ArrowLeft, Clock, Coins, Crown, Gift,
  Gavel, ArrowUpRight, UserCheck, UserX, AlertTriangle, Ban, Star, Percent,
  Link, Lock, Unlock, Code, Gem, Check, RefreshCw, Play, Square, Send, Paperclip,
  User, ArrowUp, ArrowDown, Languages, Download, Sparkles, Zap, AlertCircle, CheckCircle, XCircle,
  HardDrive, Cloud, CloudOff, GripVertical
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteContext';
import { toast } from 'sonner';
import { AdminLevelCard, LevelBadge } from '../components/LevelCard';
import { MultiLanguageInput, getLocalizedText } from '../components/MultiLanguageInput';
import { 
  SUPPORTED_LANGUAGES, 
  exportLanguage, 
  exportAllLanguages, 
  saveCustomTranslations,
  reloadTranslations 
} from '../i18n';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Menu Configuration
const menuGroups = [
  {
    title: 'Overview',
    items: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    ]
  },
  {
    title: 'Content',
    items: [
      { id: 'banners', icon: ImagePlay, label: 'Banners' },
      { id: 'categories', icon: FolderOpen, label: 'Categories' },
      { id: 'pools', icon: Box, label: 'Pools' },
      { id: 'shop', icon: ShoppingBag, label: 'Shop Products' },
      { id: 'pages', icon: FileText, label: 'Custom Pages' },
    ]
  },
  {
    title: 'Users & Orders',
    items: [
      { id: 'users', icon: Users, label: 'All Users' },
      { id: 'support-tickets', icon: MessageCircle, label: 'Support Tickets' },
      { id: 'deposits', icon: CreditCard, label: 'Deposit History' },
      { id: 'vouchers', icon: Ticket, label: 'Vouchers' },
      { id: 'token-redemptions', icon: Gift, label: 'Token Redemptions' },
      { id: 'shipping', icon: Truck, label: 'Shipping Requests' },
      { id: 'swaps', icon: RefreshCw, label: 'Swap Requests' },
    ]
  },
  {
    title: 'Settings',
    items: [
      { id: 'site', icon: Palette, label: 'Site Settings' },
      { id: 'levels', icon: Crown, label: 'Level Management' },
      { id: 'gacha', icon: Target, label: 'Gacha Algorithm' },
      { id: 'seo', icon: Globe, label: 'SEO Settings' },
      { id: 'solana', icon: Wallet, label: 'Solana Payments' },
      { id: 'token', icon: Coins, label: 'Token Settings' },
      { id: 'storage', icon: HardDrive, label: 'Cloud Storage' },
      { id: 'staking', icon: Gavel, label: 'Staking' },
      { id: 'nft', icon: Gem, label: 'NFT Minting' },
      { id: 'mail', icon: Mail, label: 'Mail Settings' },
      { id: 'footer', icon: FileText, label: 'Footer & Branding' },
      { id: 'admins', icon: Shield, label: 'Admin Users' },
      { id: 'developer', icon: Code, label: 'Developer' },
    ]
  },
];

const tierConfig = {
  THE_ONE_ONLY: { label: 'THE ONE AND ONLY', color: 'text-pink-400', border: 'border-pink-500', bg: 'bg-pink-500/10', headerBg: 'linear-gradient(135deg, #ec4899, #f97316)', maxItems: 1 },
  LEGEND: { label: 'LEGEND', color: 'text-yellow-400', border: 'border-yellow-500', bg: 'bg-yellow-500/10', headerBg: 'linear-gradient(135deg, #eab308, #f97316)', maxItems: null },
  EPIC: { label: 'EPIC', color: 'text-purple-400', border: 'border-purple-500', bg: 'bg-purple-500/10', headerBg: 'linear-gradient(135deg, #a855f7, #ec4899)', maxItems: null },
  RARE: { label: 'RARE', color: 'text-blue-400', border: 'border-blue-500', bg: 'bg-blue-500/10', headerBg: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', maxItems: null },
};

const tierOrder = ['THE_ONE_ONLY', 'LEGEND', 'EPIC', 'RARE'];

// ==================== SORTABLE ITEM CARD COMPONENT ====================
const SortableItemCard = ({ item, config, onEdit, onDelete, getLocalizedText }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-zinc-800/50 border ${config.border} rounded-lg p-3 flex gap-3 ${isDragging ? 'shadow-lg shadow-cyan-500/20' : ''}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="flex items-center cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className="w-4 h-4 text-zinc-500 hover:text-zinc-300" />
      </div>
      
      <img src={item.image_url} alt="" className="w-16 h-20 rounded object-cover" />
      <div className="flex-1 min-w-0">
        <h5 className="text-white text-sm font-medium truncate">{getLocalizedText(item.name)}</h5>
        <p className="text-zinc-500 text-xs mt-1">
          Stock: {item.remaining}/{item.quantity}
        </p>
        <p className="text-yellow-400 text-xs">Sell: {item.sell_value} coins</p>
        {/* Win Chance & Special Indicators */}
        <div className="flex flex-wrap gap-1 mt-1">
          {item.win_chance_percent !== undefined && item.win_chance_percent < 100 && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
              item.win_chance_percent > 50 ? 'bg-green-900/50 text-green-400' :
              item.win_chance_percent > 20 ? 'bg-yellow-900/50 text-yellow-400' :
              'bg-red-900/50 text-red-400'
            }`}>
              {item.win_chance_percent}%
            </span>
          )}
          {(item.is_the_one_and_only || item.rarity === 'THE_ONE_ONLY') && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-400 flex items-center gap-0.5">
              <Crown className="w-2.5 h-2.5" /> Special
            </span>
          )}
          {item.is_pack && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-900/50 text-cyan-400 flex items-center gap-0.5">
              <Package className="w-2.5 h-2.5" /> Pack
            </span>
          )}
        </div>
        <div className="flex gap-1 mt-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(item)}
            className="h-6 px-2 text-xs text-zinc-400 hover:text-white"
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(item)}
            className="h-6 px-2 text-xs text-zinc-400 hover:text-cyan-400"
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== IMAGE UPLOADER COMPONENT ====================
const ImageUploader = ({ value, onChange, token, label = "Image", compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const fileInputRef = React.useRef(null);

  React.useEffect(() => {
    setPreviewUrl(value || '');
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid format. Use: jpg, png, webp, gif');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max file size is 5MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      const imageUrl = res.data.url;
      setPreviewUrl(imageUrl);
      onChange(imageUrl);
      toast.success('Image uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input to allow re-uploading same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Compact mode for multi-image grid
  if (compact) {
    return (
      <div 
        onClick={() => !uploading && fileInputRef.current?.click()}
        className="w-full h-full flex flex-col items-center justify-center cursor-pointer"
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
            <span className="text-[10px] text-purple-400 mt-1">{uploadProgress}%</span>
          </div>
        ) : (
          <>
            <Plus className="w-5 h-5 text-purple-400 mb-1" />
            <span className="text-[10px] text-purple-400">Add Card</span>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-zinc-300">{label}</label>
      <div className="flex gap-3">
        <div 
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`w-24 h-24 rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors overflow-hidden relative ${uploading ? 'opacity-80' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center justify-center bg-zinc-900/80 absolute inset-0">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
              <span className="text-cyan-400 text-sm font-bold mt-1">{uploadProgress}%</span>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <Upload className="w-6 h-6 text-zinc-500" />
          )}
          
          {/* Progress bar overlay */}
          {uploading && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-700">
              <div 
                className="h-full bg-cyan-500 transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          )}
        </div>
        <div className="flex-1">
          <Input
            value={previewUrl}
            onChange={(e) => {
              setPreviewUrl(e.target.value);
              onChange(e.target.value);
            }}
            placeholder="Or enter image URL..."
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
          />
          <p className="text-zinc-500 text-xs mt-1">
            {uploading ? `Uploading... ${uploadProgress}%` : 'JPG, PNG, WEBP, GIF (max 5MB)'}
          </p>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
};

// ==================== SIDEBAR COMPONENT ====================
const Sidebar = ({ activeTab, setActiveTab, sidebarOpen, setSidebarOpen, ticketStats, siteSettings, settingsLoading }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const appName = settingsLoading ? null : (siteSettings?.app_name || 'App');

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - Fixed position */}
      <aside className={`
        fixed inset-y-0 left-0 z-50
        w-64 bg-zinc-900 border-r border-zinc-800
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col h-screen
      `}>
        {/* Logo - Fixed at top */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-700 flex items-center justify-center">
              <Box className="w-4 h-4 text-white" />
            </div>
            {settingsLoading ? (
              <div className="w-24 h-5 bg-zinc-800 rounded animate-pulse" />
            ) : (
              <span className="text-white font-bold text-lg">{appName} Admin</span>
            )}
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu - Scrollable */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
          {menuGroups.map((group, idx) => (
            <div key={group.title} className={idx > 0 ? 'mt-6' : ''}>
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider px-3 mb-2">
                {group.title}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  const hasNotification = item.id === 'support-tickets' && ticketStats?.unread > 0;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveTab(item.id);
                        setSidebarOpen(false);
                      }}
                      className={`
                        w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-150
                        ${isActive 
                          ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/20' 
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        {item.label}
                      </div>
                      {hasNotification && (
                        <span className="px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[20px] text-center">
                          {ticketStats.unread}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Info - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-zinc-800 bg-zinc-900">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex-1 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <Home className="w-4 h-4 mr-1" />
              Site
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="flex-1 border-zinc-700 text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/50"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// ==================== HEADER COMPONENT ====================
const AdminHeader = ({ title, subtitle, sidebarOpen, setSidebarOpen, children }) => {
  return (
    <header className="h-16 bg-zinc-900 border-b border-zinc-800 flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden text-zinc-400 hover:text-white"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-white font-bold text-lg">{title}</h1>
          {subtitle && <p className="text-zinc-500 text-xs">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {children}
      </div>
    </header>
  );
};

// ==================== DASHBOARD TAB ====================
const DashboardTab = ({ token }) => {
  const [stats, setStats] = useState({ users: 0, pools: 0, items: 0, transactions: 0 });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, poolsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pools`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/items`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats({
        users: statsRes.data.total_users || 0,
        pools: poolsRes.data.length,
        items: itemsRes.data.length,
        transactions: statsRes.data.total_transactions || 0
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Users', value: stats.users, icon: Users, color: 'from-blue-500 to-blue-600' },
    { label: 'Active Pools', value: stats.pools, icon: Box, color: 'from-green-500 to-green-600' },
    { label: 'Total Items', value: stats.items, icon: Package, color: 'from-purple-500 to-purple-600' },
    { label: 'Transactions', value: stats.transactions, icon: CreditCard, color: 'from-orange-500 to-orange-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-5"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-zinc-500 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold text-white mt-1">
                    {loading ? '-' : stat.value.toLocaleString()}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Add Pool', icon: Box, color: 'bg-green-600 hover:bg-green-700' },
            { label: 'Add Banner', icon: ImagePlay, color: 'bg-blue-600 hover:bg-blue-700' },
            { label: 'Add Category', icon: FolderOpen, color: 'bg-purple-600 hover:bg-purple-700' },
            { label: 'Add Product', icon: ShoppingBag, color: 'bg-orange-600 hover:bg-orange-700' },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                className={`${action.color} text-white rounded-lg p-4 flex flex-col items-center gap-2 transition-colors`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-sm font-medium">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ==================== BANNERS TAB ====================
const BannersTab = ({ token }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [deleteBanner, setDeleteBanner] = useState(null);
  const [formData, setFormData] = useState({ title: '', image_url: '', link_url: '', is_active: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchBanners(); }, []);

  const fetchBanners = async () => {
    try {
      const res = await axios.get(`${API}/admin/banners`, { headers: { Authorization: `Bearer ${token}` } });
      setBanners(res.data);
    } catch (err) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.image_url) {
      toast.error('Title and Image are required');
      return;
    }
    setSaving(true);
    try {
      if (editingBanner) {
        await axios.put(`${API}/admin/banners/${editingBanner.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/admin/banners`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Banner created');
      }
      fetchBanners();
      resetForm();
    } catch (err) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/banners/${deleteBanner.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Banner deleted');
      fetchBanners();
      setDeleteBanner(null);
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingBanner(null);
    setFormData({ title: '', image_url: '', link_url: '', is_active: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-zinc-400 text-sm">Manage homepage banners</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Add Banner
        </Button>
      </div>

      {/* Banners Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <ImagePlay className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No banners yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {banners.map((banner) => (
            <div key={banner.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group">
              <div className="aspect-[2/1] relative">
                <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingBanner(banner);
                      setFormData(banner);
                      setShowForm(true);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeleteBanner(banner)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {!banner.is_active && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-400">
                    Inactive
                  </div>
                )}
              </div>
              <div className="p-4">
                <h4 className="text-white font-medium truncate">{banner.title}</h4>
                {banner.link_url && (
                  <p className="text-zinc-500 text-xs truncate mt-1">{banner.link_url}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={resetForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingBanner ? 'Edit Banner' : 'Add Banner'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Banner Size Instructions */}
            <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3">
              <p className="text-cyan-400 text-sm font-medium">Banner Requirements:</p>
              <ul className="text-cyan-300/80 text-xs mt-1 space-y-1">
                <li>• Recommended size: <span className="font-semibold">984px x 408px</span></li>
                <li>• Maximum file size: <span className="font-semibold">500KB</span></li>
                <li>• Supported formats: JPG, PNG, WebP</li>
              </ul>
            </div>
            <ImageUploader
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
              token={token}
              label="Banner Image"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Banner title"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Link URL (optional)</label>
              <Input
                value={formData.link_url}
                onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                placeholder="https://..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded bg-zinc-800 border-zinc-700"
              />
              <label className="text-sm text-zinc-300">Active</label>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingBanner ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteBanner} onOpenChange={() => setDeleteBanner(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Banner?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== CATEGORIES TAB ====================
const CategoriesTab = ({ token }) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [deleteCategory, setDeleteCategory] = useState(null);
  const [deleteSubcategory, setDeleteSubcategory] = useState(null);
  const [selectedCategoryForSub, setSelectedCategoryForSub] = useState(null);
  const [formData, setFormData] = useState({ name: {}, slug: '', description: {}, icon_url: '' });
  const [subFormData, setSubFormData] = useState({ name: {}, slug: '', description: {}, image_url: '', category_id: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCategories(); fetchSubcategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`, { headers: { Authorization: `Bearer ${token}` } });
      setCategories(res.data);
    } catch (err) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/subcategories`, { headers: { Authorization: `Bearer ${token}` } });
      setSubcategories(res.data);
    } catch (err) {
      console.error('Failed to load subcategories');
    }
  };

  const handleSave = async () => {
    const nameValue = typeof formData.name === 'object' ? formData.name.en : formData.name;
    if (!nameValue) {
      toast.error('Category name is required');
      return;
    }
    setSaving(true);
    try {
      const dataToSend = { ...formData, slug: formData.slug || nameValue.toLowerCase().replace(/\s+/g, '-') };
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/categories`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category created');
      }
      fetchCategories();
      resetForm();
    } catch (err) {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubcategory = async () => {
    const nameValue = typeof subFormData.name === 'object' ? subFormData.name.en : subFormData.name;
    if (!nameValue || !subFormData.category_id) {
      toast.error('Subcategory name and category are required');
      return;
    }
    setSaving(true);
    try {
      const dataToSend = { ...subFormData, slug: subFormData.slug || nameValue.toLowerCase().replace(/\s+/g, '-') };
      if (editingSubcategory) {
        await axios.put(`${API}/admin/subcategories/${editingSubcategory.id}`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subcategory updated');
      } else {
        await axios.post(`${API}/admin/subcategories`, dataToSend, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Subcategory created');
      }
      fetchSubcategories();
      resetSubForm();
    } catch (err) {
      toast.error('Failed to save subcategory');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/categories/${deleteCategory.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category deleted');
      fetchCategories();
      fetchSubcategories();
      setDeleteCategory(null);
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const handleDeleteSubcategory = async () => {
    try {
      await axios.delete(`${API}/admin/subcategories/${deleteSubcategory.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Subcategory deleted');
      fetchSubcategories();
      setDeleteSubcategory(null);
    } catch (err) {
      toast.error('Failed to delete subcategory');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingCategory(null);
    setFormData({ name: {}, slug: '', description: {}, icon_url: '' });
  };

  const resetSubForm = () => {
    setShowSubcategoryForm(false);
    setEditingSubcategory(null);
    setSubFormData({ name: {}, slug: '', description: {}, image_url: '', category_id: selectedCategoryForSub || '' });
  };

  const handleMoveCategory = async (index, direction) => {
    const newOrder = [...categories];
    const target = index + direction;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    setCategories(newOrder);
    try {
      await axios.put(`${API}/admin/categories/reorder`, { ordered_ids: newOrder.map(c => c.id) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Category order updated');
    } catch (err) {
      toast.error('Failed to update order');
      fetchCategories();
    }
  };

  const getSubcategoriesForCategory = (categoryId) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm">Organize pools into categories and subcategories</p>
        <Button onClick={() => setShowForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <FolderOpen className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No categories yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((cat, catIndex) => {
            const catSubcategories = getSubcategoriesForCategory(cat.id);
            return (
              <div key={cat.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                {/* Category Header */}
                <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Reorder controls */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMoveCategory(catIndex, -1)}
                        disabled={catIndex === 0}
                        className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`category-move-up-${cat.id}`}
                        title="Move up"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleMoveCategory(catIndex, 1)}
                        disabled={catIndex === categories.length - 1}
                        className="w-6 h-6 rounded bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        data-testid={`category-move-down-${cat.id}`}
                        title="Move down"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {cat.icon_url ? (
                      <img src={cat.icon_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <FolderOpen className="w-6 h-6 text-zinc-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-white font-semibold">{getLocalizedText(cat.name)}</h3>
                      <p className="text-zinc-500 text-sm">{cat.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedCategoryForSub(cat.id);
                        setSubFormData({ name: {}, slug: '', description: {}, image_url: '', category_id: cat.id });
                        setShowSubcategoryForm(true);
                      }}
                      className="border-cyan-600 text-cyan-400 hover:bg-cyan-600/20"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Subcategory
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingCategory(cat);
                        setFormData({
                          ...cat,
                          name: typeof cat.name === 'object' ? cat.name : { en: cat.name || '' },
                          description: typeof cat.description === 'object' ? cat.description : { en: cat.description || '' }
                        });
                        setShowForm(true);
                      }}
                      className="text-zinc-400 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteCategory(cat)}
                      className="text-zinc-400 hover:text-cyan-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Subcategories */}
                {catSubcategories.length > 0 && (
                  <div className="p-4 bg-zinc-800/30">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Subcategories ({catSubcategories.length})</p>
                    <div className="flex flex-wrap gap-2">
                      {catSubcategories.map((sub) => (
                        <div 
                          key={sub.id} 
                          className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 group hover:border-cyan-600 transition-colors"
                        >
                          {sub.image_url ? (
                            <img src={sub.image_url} alt="" className="w-6 h-6 rounded object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded bg-zinc-700 flex items-center justify-center">
                              <Layers className="w-3 h-3 text-zinc-500" />
                            </div>
                          )}
                          <span className="text-white text-sm">{getLocalizedText(sub.name)}</span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => {
                                setEditingSubcategory(sub);
                                setSubFormData({
                                  ...sub,
                                  name: typeof sub.name === 'object' ? sub.name : { en: sub.name || '' },
                                  description: typeof sub.description === 'object' ? sub.description : { en: sub.description || '' }
                                });
                                setShowSubcategoryForm(true);
                              }}
                              className="p-1 hover:bg-zinc-700 rounded"
                            >
                              <Edit className="w-3 h-3 text-zinc-400" />
                            </button>
                            <button
                              onClick={() => setDeleteSubcategory(sub)}
                              className="p-1 hover:bg-red-500/20 rounded"
                            >
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={resetForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <ImageUploader
              value={formData.icon_url}
              onChange={(url) => setFormData({ ...formData, icon_url: url })}
              token={token}
              label="Category Icon"
            />
            <MultiLanguageInput
              label="Name"
              value={formData.name}
              onChange={(name) => setFormData({ ...formData, name })}
              placeholder="Category name"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Slug</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="category-slug"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <MultiLanguageInput
              label="Description"
              value={formData.description}
              onChange={(description) => setFormData({ ...formData, description })}
              placeholder="Category description"
              multiline
              rows={3}
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subcategory Form Dialog */}
      <Dialog open={showSubcategoryForm} onOpenChange={resetSubForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <ImageUploader
              value={subFormData.image_url}
              onChange={(url) => setSubFormData({ ...subFormData, image_url: url })}
              token={token}
              label="Subcategory Image/Logo"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Parent Category</label>
              <Select
                value={subFormData.category_id}
                onValueChange={(value) => setSubFormData({ ...subFormData, category_id: value })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-zinc-700">
                      {getLocalizedText(cat.name)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <MultiLanguageInput
              label="Name"
              value={subFormData.name}
              onChange={(name) => setSubFormData({ ...subFormData, name })}
              placeholder="Subcategory name"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Slug</label>
              <Input
                value={subFormData.slug}
                onChange={(e) => setSubFormData({ ...subFormData, slug: e.target.value })}
                placeholder="subcategory-slug"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <MultiLanguageInput
              label="Description"
              value={subFormData.description}
              onChange={(description) => setSubFormData({ ...subFormData, description })}
              placeholder="Subcategory description"
              multiline
              rows={2}
            />
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetSubForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSaveSubcategory} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingSubcategory ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <AlertDialog open={!!deleteCategory} onOpenChange={() => setDeleteCategory(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Category?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will also delete all subcategories. Pools will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Subcategory Confirmation */}
      <AlertDialog open={!!deleteSubcategory} onOpenChange={() => setDeleteSubcategory(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Subcategory?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Pools in this subcategory will have their subcategory removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSubcategory} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== POOLS TAB ====================
const PoolsTab = ({ token }) => {
  const { t } = useTranslation();
  const [pools, setPools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPool, setSelectedPool] = useState(null);
  const [showPoolForm, setShowPoolForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [deletePool, setDeletePool] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [poolFormData, setPoolFormData] = useState({
    name: {}, description: {}, image_url: '', price: 10, category_id: '', subcategory_id: '', is_active: true
  });
  const [itemFormData, setItemFormData] = useState({
    name: {}, image_url: '', card_variants: [], tier: 'RARE', quantity: 1, sell_value: 100, ticket_reward: 1, description: '', drop_rate: 10, is_mintable: false
  });
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [uploadingVariants, setUploadingVariants] = useState({}); // Track uploading state per variant index: { index: progress% }

  // Function to upload card variant image without blocking other uploads
  const uploadCardVariantImage = async (file, index) => {
    if (!file) return;
    
    // Mark this variant as uploading with 0% progress
    setUploadingVariants(prev => ({ ...prev, [index]: 0 }));
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadingVariants(prev => ({ ...prev, [index]: percent }));
        }
      });
      
      // Update only the image_url for this specific variant, preserve all other fields
      setItemFormData(prevData => {
        const newVariants = [...(prevData.card_variants || [])];
        if (newVariants[index]) {
          newVariants[index] = { 
            ...newVariants[index], 
            image_url: res.data.url 
          };
        }
        return { ...prevData, card_variants: newVariants };
      });
      
      toast.success(`Gambar card ${index + 1} berhasil diupload`);
    } catch (err) {
      toast.error(`Upload card ${index + 1} gagal`);
    } finally {
      // Mark upload as complete
      setUploadingVariants(prev => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
    }
  };

  // DnD Kit sensors for better UX
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPools();
    fetchCategories();
    fetchSubcategories();
  }, []);

  useEffect(() => {
    if (selectedPool) {
      fetchItems(selectedPool.id);
    }
  }, [selectedPool]);

  const fetchPools = async () => {
    try {
      const res = await axios.get(`${API}/admin/pools`, { headers: { Authorization: `Bearer ${token}` } });
      setPools(res.data);
    } catch (err) {
      toast.error('Failed to load pools');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error('Failed to load categories');
    }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await axios.get(`${API}/admin/subcategories`, { headers: { Authorization: `Bearer ${token}` } });
      setSubcategories(res.data);
    } catch (err) {
      console.error('Failed to load subcategories');
    }
  };

  const getSubcategoriesForCategory = (categoryId) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };

  const fetchItems = async (poolId) => {
    try {
      const res = await axios.get(`${API}/admin/items`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.filter(item => item.pool_id === poolId));
    } catch (err) {
      toast.error('Failed to load items');
    }
  };

  const handleSavePool = async () => {
    const nameValue = typeof poolFormData.name === 'object' ? poolFormData.name.en : poolFormData.name;
    if (!nameValue || !poolFormData.image_url) {
      toast.error('Name and image are required');
      return;
    }
    setSaving(true);
    try {
      if (editingPool) {
        await axios.put(`${API}/admin/pools/${editingPool.id}`, poolFormData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Pool updated');
      } else {
        await axios.post(`${API}/admin/pools`, poolFormData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Pool created');
      }
      fetchPools();
      resetPoolForm();
    } catch (err) {
      toast.error('Failed to save pool');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePool = async () => {
    try {
      await axios.delete(`${API}/admin/pools/${deletePool.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pool deleted');
      fetchPools();
      setDeletePool(null);
      if (selectedPool?.id === deletePool.id) {
        setSelectedPool(null);
      }
    } catch (err) {
      toast.error('Failed to delete pool');
    }
  };

  const handleSaveItem = async () => {
    if (!itemFormData.name || !itemFormData.image_url) {
      toast.error('Name and image are required');
      return;
    }
    setSaving(true);
    try {
      // Map frontend 'tier' to backend 'rarity' field
      const poolNameValue = typeof selectedPool.name === 'object' ? selectedPool.name.en : selectedPool.name;
      const data = { 
        pool_id: selectedPool.id, 
        name: itemFormData.name,
        description: itemFormData.description || `Item from ${poolNameValue}`,
        image_url: itemFormData.image_url,
        card_variants: itemFormData.card_variants || [],  // Card variants with name & image
        rarity: itemFormData.tier, // Backend uses 'rarity' not 'tier'
        quantity: itemFormData.quantity,
        remaining: itemFormData.quantity,
        drop_rate: itemFormData.drop_rate || 10,
        sell_value: itemFormData.sell_value,
        ticket_reward: itemFormData.ticket_reward,
        is_mintable: itemFormData.is_mintable || false,
        win_chance_percent: itemFormData.win_chance_percent ?? 100,
        is_the_one_and_only: itemFormData.is_the_one_and_only || itemFormData.tier === 'THE_ONE_ONLY',
        is_pack: itemFormData.is_pack || false
      };
      if (editingItem) {
        await axios.put(`${API}/admin/items/${editingItem.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/admin/items`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Item created');
      }
      fetchItems(selectedPool.id);
      fetchPools();
      resetItemForm();
    } catch (err) {
      console.error('Failed to save item:', err);
      toast.error('Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async () => {
    try {
      await axios.delete(`${API}/admin/items/${deleteItem.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Item deleted');
      fetchItems(selectedPool.id);
      fetchPools();
      setDeleteItem(null);
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const resetPoolForm = () => {
    setShowPoolForm(false);
    setEditingPool(null);
    setPoolFormData({ name: {}, description: {}, image_url: '', price: 10, category_id: '', is_active: true, stock_threshold: 0 });
  };

  const resetItemForm = () => {
    setShowItemForm(false);
    setEditingItem(null);
    setItemFormData({ name: {}, image_url: '', card_variants: [], tier: 'RARE', quantity: 1, sell_value: 100, ticket_reward: 1, description: '', drop_rate: 10, is_mintable: false, win_chance_percent: 100, is_the_one_and_only: false, is_pack: false });
  };

  // Handle drag end for item reordering
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update UI
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    
    // Prepare positions data for backend
    const positions = newItems.map((item, index) => ({
      item_id: item.id,
      position: index
    }));
    
    setReordering(true);
    try {
      await axios.post(`${API}/admin/items/reorder`, positions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Urutan item berhasil disimpan');
    } catch (err) {
      // Revert on error
      setItems(items);
      toast.error('Gagal menyimpan urutan item');
    } finally {
      setReordering(false);
    }
  };

  // Handle edit item click
  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemFormData({
      ...item,
      name: typeof item.name === 'object' ? item.name : { en: item.name || '' },
      tier: item.rarity || item.tier || 'RARE',
      is_mintable: item.is_mintable || false,
      card_variants: item.card_variants || [],
      win_chance_percent: item.win_chance_percent ?? 100,
      is_the_one_and_only: item.is_the_one_and_only || false,
      is_pack: item.is_pack || false
    });
    setShowItemForm(true);
  };

  // Group items by tier/rarity (backend uses 'rarity' field)
  const groupedItems = tierOrder.reduce((acc, tier) => {
    acc[tier] = items.filter(item => (item.tier === tier || item.rarity === tier));
    return acc;
  }, {});

  return (
    <div className="flex gap-6 h-[calc(100vh-180px)]">
      {/* Pools List */}
      <div className="w-80 flex-shrink-0 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <p className="text-zinc-400 text-sm">{pools.length} pools</p>
          <Button size="sm" onClick={() => setShowPoolForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        
        <div className="flex-1 overflow-y-auto space-y-2 pr-2">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
            </div>
          ) : pools.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
              <Box className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">No pools yet</p>
            </div>
          ) : (
            pools.map((pool) => (
              <div
                key={pool.id}
                onClick={() => setSelectedPool(pool)}
                className={`
                  flex gap-3 p-3 rounded-xl cursor-pointer transition-all
                  ${selectedPool?.id === pool.id 
                    ? 'bg-cyan-600/20 border border-cyan-500/50' 
                    : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-700'
                  }
                `}
              >
                <img src={pool.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-sm truncate">{getLocalizedText(pool.name)}</h4>
                  <p className="text-zinc-500 text-xs">{pool.price} coins</p>
                  <p className="text-zinc-600 text-xs">{pool.remaining_quantity || 0} / {pool.total_quantity || 0} remaining</p>
                  {pool.stock_threshold > 0 && (
                    <p className="text-amber-500 text-[10px] flex items-center gap-1 mt-0.5">
                      <Crown className="w-3 h-3" />
                      Special unlock at ≤{pool.stock_threshold}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingPool(pool);
                      setPoolFormData({
                        ...pool,
                        name: typeof pool.name === 'object' ? pool.name : { en: pool.name || '' },
                        description: typeof pool.description === 'object' ? pool.description : { en: pool.description || '' },
                        stock_threshold: pool.stock_threshold || 0
                      });
                      setShowPoolForm(true);
                    }}
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-white"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletePool(pool);
                    }}
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-cyan-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Items Panel */}
      <div className="flex-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col">
        {selectedPool ? (
          <>
            {/* Pool Header */}
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src={selectedPool.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                <div>
                  <h3 className="text-white font-semibold">{getLocalizedText(selectedPool.name)}</h3>
                  <p className="text-zinc-500 text-sm">{items.length} items</p>
                </div>
              </div>
              <Button onClick={() => setShowItemForm(true)} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {reordering && (
                <div className="flex items-center gap-2 text-cyan-400 text-sm mb-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Menyimpan urutan...
                </div>
              )}
              <p className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
                <GripVertical className="w-3 h-3" /> Drag & drop untuk mengubah urutan item
              </p>
              <DndContext 
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext 
                  items={items.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {tierOrder.map((tier) => {
                    const tierItems = groupedItems[tier];
                    if (tierItems.length === 0) return null;
                    const config = tierConfig[tier];
                    
                    return (
                      <div key={tier}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-3 h-3 rounded-full`} style={{ background: config.headerBg }} />
                          <h4 className={`font-semibold ${config.color}`}>{config.label}</h4>
                          <span className="text-zinc-500 text-sm">({tierItems.length})</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {tierItems.map((item) => (
                            <SortableItemCard
                              key={item.id}
                              item={item}
                              config={config}
                              onEdit={handleEditItem}
                              onDelete={setDeleteItem}
                              getLocalizedText={getLocalizedText}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </SortableContext>
              </DndContext>
              
              {items.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">No items in this pool</p>
                  <p className="text-zinc-600 text-sm">Click "Add Item" to add items</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Box className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">Select a pool to manage items</p>
            </div>
          </div>
        )}
      </div>

      {/* Pool Form Dialog */}
      <Dialog open={showPoolForm} onOpenChange={resetPoolForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{editingPool ? 'Edit Pool' : 'Add Pool'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto">
            <ImageUploader
              value={poolFormData.image_url}
              onChange={(url) => setPoolFormData({ ...poolFormData, image_url: url })}
              token={token}
              label="Pool Image"
            />
            <MultiLanguageInput
              label="Name"
              value={poolFormData.name}
              onChange={(name) => setPoolFormData({ ...poolFormData, name })}
              placeholder="Pool name"
            />
            <MultiLanguageInput
              label="Description"
              value={poolFormData.description}
              onChange={(description) => setPoolFormData({ ...poolFormData, description })}
              placeholder="Pool description"
              multiline
              rows={3}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-300">Price (coins)</label>
                <Input
                  type="number"
                  value={poolFormData.price}
                  onChange={(e) => setPoolFormData({ ...poolFormData, price: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-300">Category</label>
                <Select
                  value={poolFormData.category_id || "none"}
                  onValueChange={(v) => setPoolFormData({ ...poolFormData, category_id: v === "none" ? "" : v, subcategory_id: "" })}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none">None</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{getLocalizedText(cat.name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Subcategory - Only show if category is selected */}
            {poolFormData.category_id && getSubcategoriesForCategory(poolFormData.category_id).length > 0 && (
              <div>
                <label className="text-sm font-medium text-zinc-300">Subcategory (Optional)</label>
                <Select
                  value={poolFormData.subcategory_id || "none"}
                  onValueChange={(v) => setPoolFormData({ ...poolFormData, subcategory_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select subcategory..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none">None</SelectItem>
                    {getSubcategoriesForCategory(poolFormData.category_id).map((sub) => (
                      <SelectItem key={sub.id} value={sub.id}>
                        <div className="flex items-center gap-2">
                          {sub.image_url && <img src={sub.image_url} alt="" className="w-4 h-4 rounded" />}
                          {getLocalizedText(sub.name)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={poolFormData.is_active}
                onChange={(e) => setPoolFormData({ ...poolFormData, is_active: e.target.checked })}
                className="rounded bg-zinc-800 border-zinc-700"
              />
              <label className="text-sm text-zinc-300">Active</label>
            </div>
            {/* Stock Threshold for "The One and Only" */}
            <div className="p-3 bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border border-yellow-700/30 rounded-lg">
              <label className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                <Crown className="w-4 h-4" />
                "The One and Only" Unlock Threshold
              </label>
              <p className="text-xs text-zinc-500 mt-1 mb-2">
                Special items only appear when total pool stock falls below this number
              </p>
              <Input
                type="number"
                value={poolFormData.stock_threshold || 0}
                onChange={(e) => setPoolFormData({ ...poolFormData, stock_threshold: parseInt(e.target.value) || 0 })}
                placeholder="e.g., 1000"
                className="bg-zinc-800 border-zinc-700 text-white"
                min={0}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetPoolForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSavePool} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingPool ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Form Dialog */}
      <Dialog open={showItemForm} onOpenChange={resetItemForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-white">{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[80vh] overflow-y-auto pr-2">
            <ImageUploader
              value={itemFormData.image_url}
              onChange={(url) => setItemFormData({ ...itemFormData, image_url: url })}
              token={token}
              label="Item Image"
            />
            <MultiLanguageInput
              label="Name"
              value={itemFormData.name}
              onChange={(name) => setItemFormData({ ...itemFormData, name })}
              placeholder="Item name"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Tier</label>
              <Select
                value={itemFormData.tier}
                onValueChange={(v) => setItemFormData({ ...itemFormData, tier: v })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {tierOrder.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      <span className={tierConfig[tier].color}>{t(`rarity.${tier}`)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-zinc-300">Quantity</label>
                <Input
                  type="number"
                  value={
                    (itemFormData.card_variants && itemFormData.card_variants.length > 0)
                      ? itemFormData.card_variants.reduce((sum, cv) => sum + (parseInt(cv.stock) || 0), 0)
                      : itemFormData.quantity
                  }
                  onChange={(e) => setItemFormData({ ...itemFormData, quantity: parseInt(e.target.value) || 1 })}
                  disabled={itemFormData.card_variants && itemFormData.card_variants.length > 0}
                  className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${
                    (itemFormData.card_variants && itemFormData.card_variants.length > 0)
                      ? 'opacity-60 cursor-not-allowed'
                      : ''
                  }`}
                  title={
                    (itemFormData.card_variants && itemFormData.card_variants.length > 0)
                      ? 'Quantity is auto-calculated from card variant stocks'
                      : ''
                  }
                />
                {(itemFormData.card_variants && itemFormData.card_variants.length > 0) && (
                  <p className="text-purple-400 text-xs mt-1">Auto-calculated from variants</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-300">Sell Value</label>
                <Input
                  type="number"
                  value={itemFormData.sell_value}
                  onChange={(e) => setItemFormData({ ...itemFormData, sell_value: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-300">Token Reward</label>
                <Input
                  type="number"
                  value={itemFormData.ticket_reward}
                  onChange={(e) => setItemFormData({ ...itemFormData, ticket_reward: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
                <p className="text-zinc-500 text-xs mt-1">Amount of tokens user receives when getting this item</p>
              </div>
            </div>
            
            {/* Win Probability & Special Item Settings */}
            <div className="p-3 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-700/30 rounded-lg space-y-3">
              <label className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                <Percent className="w-4 h-4" />
                Win Probability Settings
              </label>
              
              {/* Win Chance Percent */}
              <div>
                <label className="text-xs text-zinc-400 block mb-1">Win Chance (%)</label>
                <div className="flex items-center gap-3">
                  <Input
                    type="number"
                    value={itemFormData.win_chance_percent ?? 100}
                    onChange={(e) => setItemFormData({ ...itemFormData, win_chance_percent: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                    placeholder="0-100"
                    className="bg-zinc-800 border-zinc-700 text-white w-24"
                    min={0}
                    max={100}
                    step={0.1}
                  />
                  <div className="flex-1">
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          (itemFormData.win_chance_percent ?? 100) > 70 ? 'bg-green-500' :
                          (itemFormData.win_chance_percent ?? 100) > 30 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${itemFormData.win_chance_percent ?? 100}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-1">
                      {(itemFormData.win_chance_percent ?? 100) > 70 ? 'High probability' :
                       (itemFormData.win_chance_percent ?? 100) > 30 ? 'Medium probability' : 'Low probability (rare!)'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Is The One and Only */}
              <div className="flex items-start gap-3 p-2 bg-yellow-900/20 border border-yellow-700/30 rounded-lg">
                <input
                  type="checkbox"
                  checked={itemFormData.is_the_one_and_only || itemFormData.tier === 'THE_ONE_ONLY'}
                  onChange={(e) => setItemFormData({ ...itemFormData, is_the_one_and_only: e.target.checked })}
                  disabled={itemFormData.tier === 'THE_ONE_ONLY'}
                  className="mt-1 rounded bg-zinc-800 border-zinc-700"
                />
                <div>
                  <label className="text-sm font-medium text-yellow-400 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    "The One and Only" Item
                  </label>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    This item will only become available when pool stock falls below threshold
                  </p>
                </div>
              </div>
              
              {/* Is Pack Item */}
              <div className="flex items-start gap-3 p-2 bg-cyan-900/20 border border-cyan-700/30 rounded-lg">
                <input
                  type="checkbox"
                  checked={itemFormData.is_pack || false}
                  onChange={(e) => setItemFormData({ ...itemFormData, is_pack: e.target.checked })}
                  className="mt-1 rounded bg-zinc-800 border-zinc-700"
                  data-testid="is-pack-checkbox"
                />
                <div>
                  <label className="text-sm font-medium text-cyan-400 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Is Pack
                  </label>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Pack item gives 5-9 random cards from Card Variants when won. Each card is added to collection separately.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Card Variants - For LEGEND/EPIC/RARE/THE_ONE_ONLY */}
            {(itemFormData.tier === 'LEGEND' || itemFormData.tier === 'EPIC' || itemFormData.tier === 'RARE' || itemFormData.tier === 'THE_ONE_ONLY') && (
              <div className="space-y-3 p-3 bg-gradient-to-br from-purple-900/20 to-blue-900/20 rounded-lg border border-purple-500/30">
                {/* Header */}
                <div className="flex items-center justify-between pb-2 border-b border-purple-500/20">
                  <label className="text-sm font-medium text-purple-300 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Card Variants
                  </label>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
                      {itemFormData.card_variants?.length || 0} cards
                    </span>
                    <span className="text-xs text-yellow-400 font-medium">
                      Stock: {(itemFormData.card_variants || []).reduce((sum, cv) => sum + (parseInt(cv.stock) || 0), 0)}
                    </span>
                  </div>
                </div>
                
                {/* Card Variants Grid - Compact Table Layout */}
                <div className="space-y-3">
                  {(itemFormData.card_variants || []).map((card, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 rounded-lg border border-zinc-700/50 bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors">
                      {/* Card Number */}
                      <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-1 rounded font-bold w-8 text-center flex-shrink-0">
                        {index + 1}
                      </span>
                      
                      {/* Card Image - With Upload Queue */}
                      <div className="w-12 h-16 rounded overflow-hidden bg-zinc-900 flex-shrink-0 relative group">
                        {uploadingVariants[index] !== undefined ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-purple-900/30 relative">
                            <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                            <span className="text-[10px] text-purple-400 font-bold mt-0.5">{uploadingVariants[index]}%</span>
                            {/* Progress bar */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-700">
                              <div 
                                className="h-full bg-purple-500 transition-all duration-200"
                                style={{ width: `${uploadingVariants[index]}%` }}
                              />
                            </div>
                          </div>
                        ) : card.image_url ? (
                          <>
                            <img src={card.image_url} alt={getLocalizedText(card.name) || `Card ${index + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => {
                                setItemFormData(prevData => {
                                  const newVariants = [...(prevData.card_variants || [])];
                                  newVariants[index] = { ...newVariants[index], image_url: '' };
                                  return { ...prevData, card_variants: newVariants };
                                });
                              }}
                              className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </>
                        ) : (
                          <div 
                            onClick={() => document.getElementById(`card-upload-${index}`)?.click()}
                            className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-800 transition-colors"
                          >
                            <Plus className="w-3 h-3 text-purple-400" />
                            <input
                              id={`card-upload-${index}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  uploadCardVariantImage(file, index);
                                  e.target.value = ''; // Reset input to allow re-upload same file
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Card Name - Wider */}
                      <Input
                        value={typeof card.name === 'object' ? (card.name?.en || card.name?.id || '') : (card.name || '')}
                        onChange={(e) => {
                          const value = e.target.value;
                          setItemFormData(prevData => {
                            const newVariants = [...(prevData.card_variants || [])];
                            const currentCard = newVariants[index] || {};
                            const newName = typeof currentCard.name === 'object' 
                              ? { ...currentCard.name, en: value, id: value }
                              : { en: value, id: value };
                            newVariants[index] = { ...currentCard, name: newName };
                            return { ...prevData, card_variants: newVariants };
                          });
                        }}
                        placeholder="Card name..."
                        className="h-8 text-sm bg-zinc-800 border-zinc-700 text-white flex-1 min-w-[120px] md:min-w-[180px]"
                      />
                      
                      {/* Stock Input */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs text-zinc-400 font-medium">Stock:</span>
                        <Input
                          type="number"
                          min="1"
                          value={card.stock || 1}
                          onChange={(e) => {
                            const newStock = parseInt(e.target.value) || 1;
                            setItemFormData(prevData => {
                              const newVariants = [...(prevData.card_variants || [])];
                              const currentCard = newVariants[index] || {};
                              newVariants[index] = { 
                                ...currentCard, 
                                stock: newStock,
                                nft_count: Math.min(currentCard.nft_count || 0, newStock)
                              };
                              return { ...prevData, card_variants: newVariants };
                            });
                          }}
                          className="h-8 w-20 text-sm bg-zinc-800 border-zinc-700 text-white text-center"
                        />
                      </div>
                      
                      {/* NFT Count - Always show for card variants (already filtered by tier above) */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Gem className="w-4 h-4 text-purple-400" />
                        <span className="text-xs text-purple-400 font-medium">NFT:</span>
                        <Input
                          type="number"
                          min="0"
                          max={card.stock || 1}
                          value={card.nft_count || 0}
                          onChange={(e) => {
                            const value = parseInt(e.target.value) || 0;
                            setItemFormData(prevData => {
                              const newVariants = [...(prevData.card_variants || [])];
                              const currentCard = newVariants[index] || {};
                              const nftCount = Math.min(value, currentCard.stock || 1);
                              newVariants[index] = { ...currentCard, nft_count: nftCount };
                              return { ...prevData, card_variants: newVariants };
                            });
                          }}
                          className="h-8 w-20 text-sm bg-purple-900/30 border-purple-700 text-purple-300 text-center"
                        />
                      </div>
                      
                      {/* Win Chance for variant */}
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Percent className="w-4 h-4 text-cyan-400" />
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={card.win_chance_percent ?? 100}
                          onChange={(e) => {
                            const value = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                            setItemFormData(prevData => {
                              const newVariants = [...(prevData.card_variants || [])];
                              const currentCard = newVariants[index] || {};
                              newVariants[index] = { ...currentCard, win_chance_percent: value };
                              return { ...prevData, card_variants: newVariants };
                            });
                          }}
                          className="h-8 w-20 text-sm bg-cyan-900/30 border-cyan-700 text-cyan-300 text-center"
                          title="Win chance %"
                        />
                        <span className="text-xs text-zinc-400">%</span>
                      </div>
                      
                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => {
                          setItemFormData(prevData => {
                            const newVariants = [...(prevData.card_variants || [])];
                            newVariants.splice(index, 1);
                            return { ...prevData, card_variants: newVariants };
                          });
                        }}
                        className="text-red-400 hover:text-red-300 p-1 hover:bg-red-500/10 rounded transition-colors flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  
                  {/* Add Card Button - Compact */}
                  <button
                    type="button"
                    onClick={() => {
                      setItemFormData(prevData => ({
                        ...prevData, 
                        card_variants: [...(prevData.card_variants || []), { image_url: '', name: '', stock: 1, nft_count: 0, win_chance_percent: 100 }]
                      }));
                    }}
                    className="w-full py-2 rounded-lg border border-dashed border-purple-500/50 text-purple-400 hover:border-purple-400 hover:bg-purple-900/20 transition-colors flex items-center justify-center gap-2 text-xs"
                  >
                    <Plus className="w-3 h-3" />
                    Tambah Varian
                  </button>
                </div>
                
                {/* Footer Note - Compact */}
                <p className="text-[10px] text-zinc-500 pt-1 border-t border-purple-500/10">
                  NFT = jumlah kartu yang bisa di-mint. THE_ONE_ONLY selalu NFT.
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetItemForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSaveItem} disabled={saving} className="flex-1 bg-green-600 hover:bg-green-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Pool Confirmation */}
      <AlertDialog open={!!deletePool} onOpenChange={() => setDeletePool(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Pool?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will also delete all items in this pool.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeletePool} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Item Confirmation */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Item?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== SHOP TAB ====================
const ShopTab = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', image_url: '', ticket_price: 100, stock: 10, category: 'general', is_active: true,
    input_label: 'Shipping Address', input_placeholder: 'Enter your shipping address...', input_required: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/admin/shop/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.image_url) {
      toast.error('Name and image are required');
      return;
    }
    setSaving(true);
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/shop/products/${editingProduct.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/shop/products`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Product created');
      }
      fetchProducts();
      resetForm();
    } catch (err) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/shop/products/${deleteProduct.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Product deleted');
      fetchProducts();
      setDeleteProduct(null);
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({ 
      name: '', description: '', image_url: '', ticket_price: 100, stock: 10, category: 'general', is_active: true,
      input_label: 'Shipping Address', input_placeholder: 'Enter your shipping address...', input_required: false
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm">Products redeemable with tickets</p>
        <Button onClick={() => setShowForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <ShoppingBag className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No products yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map((product) => (
            <div key={product.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden group">
              <div className="aspect-square relative">
                <img src={product.image_url} alt={getLocalizedText(product.name)} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditingProduct(product);
                      setFormData(product);
                      setShowForm(true);
                    }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setDeleteProduct(product)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <h4 className="text-white font-medium truncate">{getLocalizedText(product.name)}</h4>
                {product.category && (
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium capitalize
                    ${product.category === 'general' ? 'bg-zinc-500/20 text-zinc-400' :
                      product.category === 'balance' ? 'bg-green-500/20 text-green-400' :
                      product.category === 'merchandise' ? 'bg-blue-500/20 text-blue-400' :
                      product.category === 'voucher' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-zinc-500/20 text-zinc-400'}`}
                  >
                    {product.category}
                  </span>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1 text-purple-400">
                    <Ticket className="w-4 h-4" />
                    <span className="font-bold">{product.ticket_price}</span>
                  </div>
                  <span className="text-zinc-500 text-sm">Stock: {product.stock}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={resetForm}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white">{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <ImageUploader
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
              token={token}
              label="Product Image"
            />
            <div>
              <label className="text-sm font-medium text-zinc-300">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Product name"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Product description"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-300">Price (tickets)</label>
                <Input
                  type="number"
                  value={formData.ticket_price}
                  onChange={(e) => setFormData({ ...formData, ticket_price: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-300">Stock</label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>

            {/* Category Selection */}
            <div>
              <label className="text-sm font-medium text-zinc-300">Category</label>
              <Select
                value={formData.category || 'general'}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="balance">Balance</SelectItem>
                  <SelectItem value="merchandise">Merchandise</SelectItem>
                  <SelectItem value="voucher">Voucher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Dynamic Input Field Settings */}
            <div className="border-t border-zinc-800 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-zinc-300 mb-3">User Input Field (for redemption)</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-zinc-400">Input Label</label>
                  <Input
                    value={formData.input_label || ''}
                    onChange={(e) => setFormData({ ...formData, input_label: e.target.value })}
                    placeholder="e.g., Shipping Address, Email, Phone Number"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-400">Input Placeholder</label>
                  <Input
                    value={formData.input_placeholder || ''}
                    onChange={(e) => setFormData({ ...formData, input_placeholder: e.target.value })}
                    placeholder="e.g., Enter your shipping address..."
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="input_required"
                    checked={formData.input_required || false}
                    onChange={(e) => setFormData({ ...formData, input_required: e.target.checked })}
                    className="rounded bg-zinc-800 border-zinc-700"
                  />
                  <label htmlFor="input_required" className="text-sm text-zinc-400">Input is required</label>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Product?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-cyan-600 hover:bg-cyan-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== SOLANA TAB ====================
// ==================== DEPOSITS TAB ====================
const DepositsTab = ({ token }) => {
  const [deposits, setDeposits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [depositsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/deposits`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/deposits/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDeposits(depositsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  };

  const filteredDeposits = filterType === 'all' 
    ? deposits 
    : deposits.filter(d => d.type === filterType);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'deposit_sol': return 'bg-purple-500/20 text-purple-400';
      case 'admin_topup': return 'bg-blue-500/20 text-blue-400';
      case 'voucher': return 'bg-green-500/20 text-green-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'deposit_sol': return 'SOL Deposit';
      case 'admin_topup': return 'Admin Top Up';
      case 'voucher': return 'Voucher';
      default: return 'Deposit';
    }
  };

  const truncateAddress = (address) => {
    if (!address) return '-';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Deposits', value: stats.total_deposits?.toLocaleString(), icon: Coins, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
            { label: 'SOL Deposits', value: stats.sol_deposits?.toLocaleString(), icon: Wallet, color: 'text-purple-400', bg: 'bg-purple-500/10' },
            { label: 'Admin Top Ups', value: stats.admin_topups?.toLocaleString(), icon: Plus, color: 'text-blue-400', bg: 'bg-blue-500/10' },
            { label: 'Voucher Claims', value: stats.voucher_deposits?.toLocaleString(), icon: Ticket, color: 'text-green-400', bg: 'bg-green-500/10' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className={`${stat.bg} border border-zinc-800 rounded-xl p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${stat.color}`} />
                  <span className="text-zinc-400 text-xs">{stat.label}</span>
                </div>
                <p className={`text-xl font-bold ${stat.color}`}>{stat.value} coins</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <p className="text-zinc-400 text-sm">{filteredDeposits.length} transactions</p>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="deposit_sol">SOL Deposits</SelectItem>
            <SelectItem value="admin_topup">Admin Top Ups</SelectItem>
            <SelectItem value="voucher">Voucher Claims</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deposits Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : filteredDeposits.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <CreditCard className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No deposits found</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">User</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Type</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Amount</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Details</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Date</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeposits.map((deposit) => (
                <tr 
                  key={deposit.id} 
                  className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedDeposit(deposit)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                        {deposit.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{deposit.user?.name || 'Unknown'}</p>
                        <p className="text-zinc-500 text-xs">{deposit.user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(deposit.type)}`}>
                      {getTypeLabel(deposit.type)}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-green-400 font-semibold">
                      <Coins className="w-4 h-4" />
                      +{deposit.amount?.toLocaleString()}
                    </div>
                  </td>
                  <td className="p-4 text-zinc-400 text-sm">
                    {deposit.voucher_code && <span>Code: {deposit.voucher_code}</span>}
                    {deposit.sol_amount && <span>{deposit.sol_amount} SOL</span>}
                    {deposit.note && <span>{deposit.note}</span>}
                    {deposit.admin_email && <span>By: {deposit.admin_email}</span>}
                  </td>
                  <td className="p-4 text-zinc-500 text-sm">{formatDate(deposit.created_at)}</td>
                  <td className="p-4 text-right">
                    <Eye className="w-4 h-4 text-zinc-500 inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deposit Detail Dialog */}
      <Dialog open={!!selectedDeposit} onOpenChange={() => setSelectedDeposit(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-purple-400" />
              Deposit Details
            </DialogTitle>
          </DialogHeader>
          {selectedDeposit && (
            <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
              {/* Type Badge */}
              <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <span className="text-zinc-400 text-sm">Type</span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${getTypeColor(selectedDeposit.type)}`}>
                  {getTypeLabel(selectedDeposit.type)}
                </span>
              </div>

              {/* Amount */}
              <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg">
                <p className="text-zinc-400 text-xs mb-1">Amount Credited</p>
                <p className="text-green-400 text-2xl font-bold flex items-center gap-2">
                  <Coins className="w-5 h-5" />
                  +{selectedDeposit.amount?.toLocaleString()} coins
                </p>
              </div>

              {/* User Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-xs mb-1">User</p>
                <p className="text-white font-medium">{selectedDeposit.user?.name || 'Unknown'}</p>
                <p className="text-zinc-500 text-sm">{selectedDeposit.user?.email}</p>
              </div>

              {/* SOL Transaction Details (for SOL deposits) */}
              {selectedDeposit.type === 'deposit_sol' && (
                <>
                  {selectedDeposit.sol_amount && (
                    <div className="p-3 bg-purple-900/20 border border-purple-800/30 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">SOL Amount</p>
                      <p className="text-purple-400 text-xl font-bold">{selectedDeposit.sol_amount} SOL</p>
                    </div>
                  )}

                  {selectedDeposit.wallet_address && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Wallet Address</p>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-mono text-sm">{truncateAddress(selectedDeposit.wallet_address)}</p>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedDeposit.wallet_address); }}
                          className="h-7 px-2 text-zinc-400 hover:text-white"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-zinc-600 text-xs mt-1 break-all">{selectedDeposit.wallet_address}</p>
                    </div>
                  )}

                  {selectedDeposit.tx_id && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Transaction ID</p>
                      <div className="flex items-center justify-between">
                        <p className="text-white font-mono text-sm">{truncateAddress(selectedDeposit.tx_id)}</p>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedDeposit.tx_id); }}
                            className="h-7 px-2 text-zinc-400 hover:text-white"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); window.open(`https://solscan.io/tx/${selectedDeposit.tx_id}`, '_blank'); }}
                            className="h-7 px-2 text-purple-400 hover:text-purple-300"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <a 
                        href={`https://solscan.io/tx/${selectedDeposit.tx_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 text-xs hover:underline mt-2 inline-flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View on Solscan <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {selectedDeposit.tx_status && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Transaction Status</p>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedDeposit.tx_status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        selectedDeposit.tx_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-cyan-500/20 text-cyan-400'
                      }`}>
                        {selectedDeposit.tx_status?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Voucher Details */}
              {selectedDeposit.type === 'voucher' && selectedDeposit.voucher_code && (
                <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg">
                  <p className="text-zinc-400 text-xs mb-1">Voucher Code</p>
                  <div className="flex items-center justify-between">
                    <p className="text-green-400 font-mono text-lg font-bold">{selectedDeposit.voucher_code}</p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedDeposit.voucher_code); }}
                      className="h-7 px-2 text-zinc-400 hover:text-white"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Admin Top Up Details */}
              {selectedDeposit.type === 'admin_topup' && (
                <>
                  {selectedDeposit.admin_email && (
                    <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Top Up By</p>
                      <p className="text-blue-400 font-medium">{selectedDeposit.admin_email}</p>
                    </div>
                  )}
                  {selectedDeposit.note && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Note</p>
                      <p className="text-white text-sm">{selectedDeposit.note}</p>
                    </div>
                  )}
                </>
              )}

              {/* Date Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-xs mb-1">Transaction Date</p>
                <p className="text-white text-sm">{formatDate(selectedDeposit.created_at)}</p>
              </div>

              {/* Transaction ID */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-xs mb-1">Internal Transaction ID</p>
                <div className="flex items-center justify-between">
                  <p className="text-zinc-500 font-mono text-xs">{selectedDeposit.id}</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedDeposit.id); }}
                    className="h-7 px-2 text-zinc-400 hover:text-white"
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== TOKEN REDEMPTIONS TAB ====================
const TicketRedemptionsTab = ({ token }) => {
  const [redemptions, setRedemptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRedemption, setSelectedRedemption] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [redemptionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/shop/purchases`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/ticket-redemptions/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRedemptions(redemptionsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReject = async (purchaseId, status) => {
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/shop/purchases/${purchaseId}/approve`, 
        { status, admin_note: actionNote }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(status === 'rejected' ? 'Rejected & tokens refunded' : 'Approved successfully');
      fetchData();
      setSelectedRedemption(null);
      setActionNote('');
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const filteredRedemptions = filterStatus === 'all' 
    ? redemptions 
    : redemptions.filter(r => r.status === filterStatus);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-cyan-500/20 text-cyan-400';
      case 'processing': return 'bg-blue-500/20 text-blue-400';
      case 'completed': return 'bg-emerald-500/20 text-emerald-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Redemptions', value: stats.total_redemptions, icon: ShoppingBag, color: 'text-purple-400', bg: 'bg-purple-500/10' },
            { label: 'Tokens Spent', value: stats.total_tickets_spent, icon: Coins, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
            { label: 'Pending', value: stats.pending_count, icon: Clock, color: 'text-orange-400', bg: 'bg-orange-500/10' },
            { label: 'Completed', value: stats.completed_count, icon: Package, color: 'text-green-400', bg: 'bg-green-500/10' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} className={`${stat.bg} border border-zinc-800 rounded-xl p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${stat.color}`} />
                  <span className="text-zinc-400 text-xs">{stat.label}</span>
                </div>
                <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <p className="text-zinc-400 text-sm">{filteredRedemptions.length} redemptions</p>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Redemptions Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : filteredRedemptions.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Gift className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No token redemptions found</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">User</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Product</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">User Input</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Tokens</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Status</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Date</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRedemptions.map((redemption) => (
                <tr 
                  key={redemption.id} 
                  className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedRedemption(redemption)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                        {redemption.user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{redemption.user?.name || 'Unknown'}</p>
                        <p className="text-zinc-500 text-xs">{redemption.user?.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {redemption.product_image && (
                        <img src={redemption.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                      )}
                      <span className="text-white text-sm">{redemption.product_name || 'Unknown Product'}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {redemption.user_input ? (
                      <div>
                        <p className="text-zinc-500 text-[10px]">{redemption.input_label || 'Input'}:</p>
                        <p className="text-white text-xs max-w-[150px] truncate" title={redemption.user_input}>{redemption.user_input}</p>
                      </div>
                    ) : (
                      <span className="text-zinc-600 text-xs">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-orange-400 font-semibold">
                      <Ticket className="w-4 h-4" />
                      {redemption.ticket_spent}
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(redemption.status)}`}>
                      {redemption.status?.toUpperCase()}
                    </span>
                    {redemption.admin_note && (
                      <p className="text-zinc-500 text-[10px] mt-1 max-w-[100px] truncate" title={redemption.admin_note}>Note: {redemption.admin_note}</p>
                    )}
                  </td>
                  <td className="p-4 text-zinc-500 text-sm">{formatDate(redemption.created_at)}</td>
                  <td className="p-4 text-right">
                    <Eye className="w-4 h-4 text-zinc-500 inline-block" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review/Detail Dialog */}
      <Dialog open={!!selectedRedemption} onOpenChange={() => { setSelectedRedemption(null); setActionNote(''); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Redemption Details</DialogTitle>
          </DialogHeader>
          {selectedRedemption && (
            <div className="space-y-4 py-4">
              {/* Product Info */}
              <div className="flex items-center gap-4 p-3 bg-zinc-800 rounded-lg">
                <img src={selectedRedemption.product_image} alt="" className="w-16 h-16 rounded object-cover" />
                <div>
                  <p className="text-white font-medium">{selectedRedemption.product_name}</p>
                  <p className="text-orange-400 text-sm flex items-center gap-1">
                    <Ticket className="w-3 h-3" /> {selectedRedemption.ticket_spent} tickets
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                <span className="text-zinc-400 text-sm">Status</span>
                <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(selectedRedemption.status)}`}>
                  {selectedRedemption.status?.toUpperCase()}
                </span>
              </div>

              {/* User Info */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-xs mb-1">Requested by</p>
                <p className="text-white font-medium">{selectedRedemption.user?.name}</p>
                <p className="text-zinc-500 text-sm">{selectedRedemption.user?.email}</p>
              </div>

              {/* User Input */}
              {selectedRedemption.user_input && (
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-400 text-xs mb-1">{selectedRedemption.input_label || 'User Input'}</p>
                  <p className="text-white text-sm whitespace-pre-wrap">{selectedRedemption.user_input}</p>
                </div>
              )}

              {/* Date Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-400 text-xs mb-1">Requested At</p>
                  <p className="text-white text-sm">{formatDate(selectedRedemption.created_at)}</p>
                </div>
                {selectedRedemption.processed_at && (
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-zinc-400 text-xs mb-1">Processed At</p>
                    <p className="text-white text-sm">{formatDate(selectedRedemption.processed_at)}</p>
                  </div>
                )}
              </div>

              {/* Processed By & Admin Note (for completed/rejected) */}
              {selectedRedemption.status !== 'pending' && (
                <>
                  {selectedRedemption.processed_by && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Processed By</p>
                      <p className="text-white text-sm">{selectedRedemption.processed_by}</p>
                    </div>
                  )}
                  {selectedRedemption.admin_note && (
                    <div className="p-3 bg-zinc-800/50 rounded-lg">
                      <p className="text-zinc-400 text-xs mb-1">Admin Note</p>
                      <p className="text-white text-sm">{selectedRedemption.admin_note}</p>
                    </div>
                  )}
                </>
              )}

              {/* Admin Actions (only for pending) */}
              {selectedRedemption.status === 'pending' && (
                <>
                  {/* Admin Note */}
                  <div>
                    <label className="text-sm font-medium text-zinc-300">Admin Note (optional)</label>
                    <Textarea
                      value={actionNote}
                      onChange={(e) => setActionNote(e.target.value)}
                      placeholder="Add a note for this action..."
                      className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                      rows={2}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => handleApproveReject(selectedRedemption.id, 'rejected')}
                      disabled={processing}
                      variant="outline"
                      className="flex-1 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Reject & Refund
                </Button>
                <Button
                  onClick={() => handleApproveReject(selectedRedemption.id, 'approved')}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Approve
                </Button>
              </div>

              <p className="text-zinc-500 text-xs text-center">
                Rejecting will refund {selectedRedemption.ticket_spent} tickets to the user
              </p>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== SITE SETTINGS TAB ====================
const SiteSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    app_name: 'TCG Gacha',
    logo: '',
    favicon: '',
    colors: {
      primary: '#18BFE0',
      primary_hover: '#0ea5e9',
      accent: '#18BFE0',
      background: '#009DAE',
      card: '#18181b',
      card_border: '#27272a',
      text_primary: '#fafafa',
      text_secondary: '#a1a1aa',
      text_muted: '#71717a'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLogo, setPreviewLogo] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
        setPreviewLogo(res.data.logo || '');
      }
    } catch (err) {
      console.error('Failed to fetch site settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setSettings(prev => ({ ...prev, logo: reader.result }));
        setPreviewLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        toast.error('Favicon must be less than 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setSettings(prev => ({ ...prev, favicon: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleColorChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Site settings saved! Refresh to see changes.');
      
      // Apply theme immediately
      const root = document.documentElement;
      Object.entries(settings.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key.replace(/_/g, '-')}`, value);
      });
      document.body.style.backgroundColor = settings.colors.background;
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      app_name: 'TCG Gacha',
      logo: '',
      favicon: '',
      colors: {
        primary: '#18BFE0',
        primary_hover: '#0ea5e9',
        accent: '#18BFE0',
        background: '#009DAE',
        card: '#18181b',
        card_border: '#27272a',
        text_primary: '#fafafa',
        text_secondary: '#a1a1aa',
        text_muted: '#71717a'
      }
    });
    setPreviewLogo('');
    toast.success('Reset to defaults. Click Save to apply.');
  };

  const ColorInput = ({ label, colorKey, description }) => (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
      <div className="flex-1">
        <span className="text-white text-sm font-medium">{label}</span>
        {description && <p className="text-zinc-500 text-xs mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-3">
        <div className="relative">
          <div 
            className="w-10 h-10 rounded-lg border-2 border-zinc-700 cursor-pointer"
            style={{ backgroundColor: settings.colors[colorKey] }}
            onClick={() => document.getElementById(`color-${colorKey}`).click()}
          />
          <input
            id={`color-${colorKey}`}
            type="color"
            value={settings.colors[colorKey]}
            onChange={(e) => handleColorChange(colorKey, e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <Input
          value={settings.colors[colorKey]}
          onChange={(e) => handleColorChange(colorKey, e.target.value)}
          className="w-28 bg-zinc-800 border-zinc-700 text-white text-sm font-mono"
          placeholder="#000000"
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Site Settings</h2>
          <p className="text-zinc-500 text-sm mt-1">Customize your app's branding and colors</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={resetToDefaults}
            className="border-zinc-700 text-zinc-400"
          >
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {/* Branding */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-blue-500" />
          Branding
        </h3>
        
        <div className="space-y-6">
          {/* App Name */}
          <div>
            <label className="text-zinc-400 text-sm block mb-2">App Name</label>
            <Input
              value={settings.app_name}
              onChange={(e) => setSettings(prev => ({ ...prev, app_name: e.target.value }))}
              placeholder="TCG Gacha"
              className="bg-zinc-800 border-zinc-700 text-white max-w-md"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Logo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-zinc-800 border border-zinc-700 rounded-xl flex items-center justify-center overflow-hidden">
                {previewLogo ? (
                  <img src={previewLogo} alt="Logo" className="w-full h-full object-contain" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-zinc-600" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload">
                  <Button variant="outline" className="border-zinc-700 text-zinc-400 cursor-pointer" asChild>
                    <span><Upload className="w-4 h-4 mr-2" /> Upload Logo</span>
                  </Button>
                </label>
                <p className="text-zinc-500 text-xs mt-2">Recommended: 2200x330px, PNG or SVG. Max 2MB</p>
              </div>
              {previewLogo && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSettings(prev => ({ ...prev, logo: '' }));
                    setPreviewLogo('');
                  }}
                  className="border-cyan-900 text-cyan-500"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Favicon Upload */}
          <div>
            <label className="text-zinc-400 text-sm block mb-2">Favicon</label>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-zinc-800 border border-zinc-700 rounded-lg flex items-center justify-center overflow-hidden">
                {settings.favicon ? (
                  <img src={settings.favicon} alt="Favicon" className="w-full h-full object-contain" />
                ) : (
                  <Globe className="w-5 h-5 text-zinc-600" />
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*,.ico"
                  onChange={handleFaviconUpload}
                  className="hidden"
                  id="favicon-upload"
                />
                <label htmlFor="favicon-upload">
                  <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400 cursor-pointer" asChild>
                    <span><Upload className="w-4 h-4 mr-2" /> Upload Favicon</span>
                  </Button>
                </label>
                <p className="text-zinc-500 text-xs mt-1">32x32px or 64x64px. Max 500KB</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Colors */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-cyan-500" />
          Primary Colors
        </h3>
        
        <div className="space-y-1">
          <ColorInput 
            label="Primary Color" 
            colorKey="primary" 
            description="Main brand color (buttons, links, accents)"
          />
          <ColorInput 
            label="Primary Hover" 
            colorKey="primary_hover" 
            description="Hover state for primary elements"
          />
          <ColorInput 
            label="Accent Color" 
            colorKey="accent" 
            description="Secondary highlight color"
          />
        </div>
      </div>

      {/* Background Colors */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-500" />
          Background Colors
        </h3>
        
        <div className="space-y-1">
          <ColorInput 
            label="Background" 
            colorKey="background" 
            description="Main page background"
          />
          <ColorInput 
            label="Card Background" 
            colorKey="card" 
            description="Cards, modals, dropdowns"
          />
          <ColorInput 
            label="Card Border" 
            colorKey="card_border" 
            description="Borders and dividers"
          />
        </div>
      </div>

      {/* Text Colors */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-green-500" />
          Text Colors
        </h3>
        
        <div className="space-y-1">
          <ColorInput 
            label="Primary Text" 
            colorKey="text_primary" 
            description="Main text, headings"
          />
          <ColorInput 
            label="Secondary Text" 
            colorKey="text_secondary" 
            description="Descriptions, labels"
          />
          <ColorInput 
            label="Muted Text" 
            colorKey="text_muted" 
            description="Hints, placeholders"
          />
        </div>
      </div>

      {/* Preview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4">Live Preview</h3>
        <div 
          className="rounded-xl p-6 border"
          style={{ 
            backgroundColor: settings.colors.background,
            borderColor: settings.colors.card_border
          }}
        >
          <div 
            className="rounded-lg p-4 mb-4"
            style={{ 
              backgroundColor: settings.colors.card,
              borderColor: settings.colors.card_border,
              border: '1px solid'
            }}
          >
            <h4 style={{ color: settings.colors.text_primary }} className="font-semibold mb-2">
              {settings.app_name}
            </h4>
            <p style={{ color: settings.colors.text_secondary }} className="text-sm mb-3">
              This is how your app will look with these colors.
            </p>
            <p style={{ color: settings.colors.text_muted }} className="text-xs">
              Muted text example
            </p>
          </div>
          <div className="flex gap-3">
            <button
              className="px-4 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: settings.colors.primary }}
            >
              Primary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg font-medium"
              style={{ 
                backgroundColor: settings.colors.accent,
                color: settings.colors.text_primary
              }}
            >
              Accent Button
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== LEVEL FORM COMPONENT ====================
// Extracted outside LevelsTab to prevent re-creation on every render
const LevelFormComponent = React.memo(({ level, setLevel, isNew, uploadingImage, onImageUpload }) => {
  // Use functional updates to prevent scroll issues
  const handleFieldChange = (field, value) => {
    setLevel(prev => ({ ...prev, [field]: value }));
  };

  const handleRequirementChange = (field, value) => {
    setLevel(prev => ({
      ...prev,
      requirements: { ...prev.requirements, [field]: value }
    }));
  };

  const handleBenefitChange = (field, value) => {
    setLevel(prev => ({
      ...prev,
      benefits: { ...prev.benefits, [field]: value }
    }));
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-zinc-400 mb-1">Level ID *</label>
          <Input
            value={level.id}
            onChange={(e) => handleFieldChange('id', e.target.value.toUpperCase().replace(/\s/g, '_'))}
            placeholder="e.g. BRONZE, SILVER"
            className="bg-zinc-800 border-zinc-700"
            disabled={!isNew}
          />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-1">Display Name *</label>
          <Input
            value={level.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder="e.g. Bronze, Silver"
            className="bg-zinc-800 border-zinc-700"
          />
        </div>
      </div>
      
      {/* Appearance */}
      <div className="space-y-4">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Palette className="w-4 h-4" /> Appearance
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Card Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={level.color}
                onChange={(e) => handleFieldChange('color', e.target.value)}
                className="w-10 h-10 rounded border-0 cursor-pointer"
              />
              <Input
                value={level.color}
                onChange={(e) => handleFieldChange('color', e.target.value)}
                placeholder="#CD7F32"
                className="bg-zinc-800 border-zinc-700 flex-1"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Background Gradient</label>
            <Input
              value={level.bg_gradient || ''}
              onChange={(e) => handleFieldChange('bg_gradient', e.target.value)}
              placeholder="linear-gradient(135deg, #000, #333)"
              className="bg-zinc-800 border-zinc-700"
            />
          </div>
        </div>
        
        {/* Background Image */}
        <div>
          <label className="block text-sm text-zinc-400 mb-1">Background Image (Optional)</label>
          <div className="flex items-center gap-4">
            {level.bg_image ? (
              <div className="relative w-24 h-16 rounded overflow-hidden">
                <img src={level.bg_image} alt="BG" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => handleFieldChange('bg_image', null)}
                  className="absolute top-1 right-1 p-1 bg-red-500 rounded-full"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-16 rounded border-2 border-dashed border-zinc-700 flex items-center justify-center text-zinc-500 text-xs">
                No Image
              </div>
            )}
            <label className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-white transition">
                {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload Image
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={onImageUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>
      
      {/* Requirements */}
      <div className="space-y-4">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Target className="w-4 h-4" /> Requirements
        </h4>
        <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Requirement Type</label>
            <div className="flex gap-2">
              {['any', 'all'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleRequirementChange('requirement_type', type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    level.requirements?.requirement_type === type
                      ? 'bg-cyan-600 text-white'
                      : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                  }`}
                >
                  {type === 'any' ? 'Meet ANY requirement' : 'Meet ALL requirements'}
                </button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Min Staked Tokens</label>
              <Input
                type="number"
                value={level.requirements?.min_staked || 0}
                onChange={(e) => handleRequirementChange('min_staked', parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Min Spent (Gacha)</label>
              <Input
                type="number"
                value={level.requirements?.min_spent || 0}
                onChange={(e) => handleRequirementChange('min_spent', parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Min Tokens Owned</label>
              <Input
                type="number"
                value={level.requirements?.min_tokens_owned || 0}
                onChange={(e) => handleRequirementChange('min_tokens_owned', parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits */}
      <div className="space-y-4">
        <h4 className="text-white font-medium flex items-center gap-2">
          <Gift className="w-4 h-4" /> Benefits
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Deposit Token Bonus (%)</label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                value={level.benefits?.deposit_bonus_percent || 0}
                onChange={(e) => handleBenefitChange('deposit_bonus_percent', parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700"
              />
              <span className="text-zinc-400">%</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">Token bonus sent to wallet on coin deposits</p>
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-1">Benefit Description</label>
            <Input
              value={level.benefits?.description || ''}
              onChange={(e) => handleBenefitChange('description', e.target.value)}
              placeholder="e.g. 0.5% token bonus on deposits"
              className="bg-zinc-800 border-zinc-700"
            />
          </div>
        </div>
        <div className="p-3 bg-violet-900/20 border border-violet-500/30 rounded-lg">
          <p className="text-violet-400 text-sm">
            <strong>Formula:</strong> For every 1 coin ($0.1) deposit, user receives: <br/>
            <code className="bg-black/30 px-2 py-0.5 rounded text-xs">
              $0.1 ÷ 100 × {level.benefits?.deposit_bonus_percent || 0}% = {((0.1 / 100) * (level.benefits?.deposit_bonus_percent || 0)).toFixed(6)} tokens
            </code>
          </p>
        </div>
      </div>
      
      {/* Preview Card */}
      <div className="space-y-2">
        <h4 className="text-white font-medium">Preview</h4>
        <div 
          className="w-full max-w-sm h-48 rounded-xl p-4 relative overflow-hidden"
          style={{
            background: level.bg_gradient || level.color,
            boxShadow: `0 4px 20px ${level.color}40`
          }}
        >
          {level.bg_image && (
            <img 
              src={level.bg_image} 
              alt="bg" 
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
          )}
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-lg">{level.name || 'Level Name'}</span>
            </div>
            <p className="text-white/70 text-sm">{level.benefits?.description || 'Benefit description'}</p>
            <div className="mt-4 text-white/80 text-xs">
              <p>Bonus: {level.benefits?.deposit_bonus_percent || 0}%</p>
              <p>Min Staked: {(level.requirements?.min_staked || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ==================== LEVEL MANAGEMENT TAB ====================
const LevelsTab = ({ token }) => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const defaultLevel = {
    id: '',
    name: '',
    order: levels.length + 1,
    color: '#CD7F32',
    bg_gradient: '',
    bg_image: null,
    requirements: {
      min_staked: 0,
      min_spent: 0,
      min_tokens_owned: 0,
      requirement_type: 'any'
    },
    benefits: {
      deposit_bonus_percent: 0.5,
      description: '0.5% token bonus on deposits'
    }
  };
  
  const [newLevel, setNewLevel] = useState(defaultLevel);
  
  useEffect(() => {
    fetchLevels();
  }, []);
  
  const fetchLevels = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/levels/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevels(res.data.levels || []);
    } catch (error) {
      console.error('Failed to fetch levels:', error);
      toast.error('Failed to load level settings');
    } finally {
      setLoading(false);
    }
  };
  
  const saveLevels = async (updatedLevels) => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/levels/settings`, 
        { levels: updatedLevels },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLevels(updatedLevels);
      toast.success('Level settings saved!');
    } catch (error) {
      console.error('Failed to save levels:', error);
      toast.error('Failed to save level settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddLevel = () => {
    if (!newLevel.id || !newLevel.name) {
      toast.error('Level ID and Name are required');
      return;
    }
    
    // Check for duplicate ID
    if (levels.some(l => l.id === newLevel.id)) {
      toast.error('Level ID already exists');
      return;
    }
    
    const updatedLevels = [...levels, { ...newLevel, order: levels.length + 1 }];
    saveLevels(updatedLevels);
    setNewLevel(defaultLevel);
    setShowAddDialog(false);
  };
  
  const handleUpdateLevel = () => {
    if (!editingLevel) return;
    
    const updatedLevels = levels.map(l => 
      l.id === editingLevel.id ? editingLevel : l
    );
    saveLevels(updatedLevels);
    setEditingLevel(null);
  };
  
  const handleDeleteLevel = (levelId) => {
    if (levels.length <= 1) {
      toast.error('Cannot delete the last level');
      return;
    }
    
    const updatedLevels = levels
      .filter(l => l.id !== levelId)
      .map((l, idx) => ({ ...l, order: idx + 1 }));
    saveLevels(updatedLevels);
  };
  
  const handleMoveLevel = (levelId, direction) => {
    const idx = levels.findIndex(l => l.id === levelId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === levels.length - 1)) {
      return;
    }
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newLevels = [...levels];
    [newLevels[idx], newLevels[newIdx]] = [newLevels[newIdx], newLevels[idx]];
    
    // Update order numbers
    const updatedLevels = newLevels.map((l, i) => ({ ...l, order: i + 1 }));
    saveLevels(updatedLevels);
  };
  
  const handleImageUpload = async (e, isNewLevel = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid format. Use: jpg, png, webp, gif');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max file size is 5MB');
      return;
    }
    
    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/levels/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.image_url) {
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.image_url}`;
        if (isNewLevel) {
          setNewLevel(prev => ({ ...prev, bg_image: fullUrl }));
        } else if (editingLevel) {
          setEditingLevel(prev => ({ ...prev, bg_image: fullUrl }));
        }
        toast.success('Image uploaded!');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-yellow-500" />
            Level Management
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Configure user levels, requirements, and reward multipliers
          </p>
        </div>
        <Button
          onClick={() => {
            setNewLevel({
              ...defaultLevel,
              order: levels.length + 1
            });
            setShowAddDialog(true);
          }}
          className="bg-cyan-600 hover:bg-cyan-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Level
        </Button>
      </div>
      
      {/* Levels List */}
      <div className="space-y-4">
        {levels.sort((a, b) => a.order - b.order).map((level, index) => (
          <motion.div
            key={level.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden"
          >
            <div className="flex items-center">
              {/* Level Color Bar */}
              <div 
                className="w-2 h-full min-h-[100px]"
                style={{ background: level.color }}
              />
              
              {/* Level Info */}
              <div className="flex-1 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {/* Order Controls */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveLevel(level.id, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-zinc-800 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4 text-zinc-400 rotate-180" />
                      </button>
                      <span className="text-center text-xs text-zinc-500">{level.order}</span>
                      <button
                        onClick={() => handleMoveLevel(level.id, 'down')}
                        disabled={index === levels.length - 1}
                        className="p-1 hover:bg-zinc-800 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4 text-zinc-400" />
                      </button>
                    </div>
                    
                    {/* Level Details */}
                    <div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ background: level.color }}
                        >
                          <Crown className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{level.name}</h3>
                          <p className="text-xs text-zinc-500">ID: {level.id}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingLevel(level)}
                      className="text-zinc-400 hover:text-white"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteLevel(level.id)}
                      className="text-zinc-400 hover:text-red-400"
                      disabled={levels.length <= 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Level Stats */}
                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Deposit Bonus</p>
                    <p className="text-lg font-bold text-violet-400">{level.benefits?.deposit_bonus_percent || 0}%</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Min Staked</p>
                    <p className="text-lg font-bold text-white">{(level.requirements?.min_staked || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Min Spent</p>
                    <p className="text-lg font-bold text-white">{(level.requirements?.min_spent || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-3">
                    <p className="text-xs text-zinc-500">Req Type</p>
                    <p className="text-sm font-medium text-white capitalize">{level.requirements?.requirement_type || 'any'}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Add Level Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-cyan-500" />
              Add New Level
            </DialogTitle>
          </DialogHeader>
          <LevelFormComponent 
            level={newLevel} 
            setLevel={setNewLevel} 
            isNew={true}
            uploadingImage={uploadingImage}
            onImageUpload={(e) => handleImageUpload(e, true)}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddLevel} className="bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Level
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Level Dialog */}
      <Dialog open={!!editingLevel} onOpenChange={(open) => !open && setEditingLevel(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-500" />
              Edit Level: {editingLevel?.name}
            </DialogTitle>
          </DialogHeader>
          {editingLevel && (
            <LevelFormComponent 
              level={editingLevel} 
              setLevel={setEditingLevel}
              isNew={false}
              uploadingImage={uploadingImage}
              onImageUpload={(e) => handleImageUpload(e, false)}
            />
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => setEditingLevel(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateLevel} className="bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== GACHA SETTINGS TAB ====================
const GachaSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    algorithm: 'STATIC',
    profit_target: 30,
    the_one_only_threshold: 1000
  });
  const [tierWinChances, setTierWinChances] = useState({
    THE_ONE_ONLY: 0.1,
    LEGEND: 3,
    EPIC: 15,
    RARE: 100
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingWinChances, setSavingWinChances] = useState(false);

  useEffect(() => { fetchSettings(); fetchTierWinChances(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings({
        algorithm: res.data.algorithm || 'STATIC',
        profit_target: res.data.profit_target || 30,
        the_one_only_threshold: res.data.the_one_only_threshold || 1000
      });
    } catch (err) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchTierWinChances = async () => {
    try {
      const res = await axios.get(`${API}/admin/items`, { headers: { Authorization: `Bearer ${token}` } });
      // Calculate average win_chance per tier
      const tierStats = {};
      res.data.forEach(item => {
        const tier = item.rarity;
        if (!tierStats[tier]) tierStats[tier] = [];
        tierStats[tier].push(item.win_chance_percent ?? 100);
      });
      const averages = {};
      Object.entries(tierStats).forEach(([tier, chances]) => {
        averages[tier] = chances.length > 0 ? chances[0] : 100; // Take first item's value as reference
      });
      setTierWinChances(prev => ({ ...prev, ...averages }));
    } catch (err) {
      console.error('Failed to fetch tier win chances');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        gacha_algorithm: settings.algorithm,
        profit_target: settings.profit_target,
        the_one_only_threshold: settings.the_one_only_threshold
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Gacha settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWinChances = async () => {
    setSavingWinChances(true);
    try {
      const updates = Object.entries(tierWinChances).map(([rarity, win_chance_percent]) => ({
        rarity,
        win_chance_percent: parseFloat(win_chance_percent) || 0
      }));
      await axios.post(`${API}/admin/items/bulk-win-chance`, updates, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Win chances updated for all items');
    } catch (err) {
      toast.error('Failed to update win chances');
    } finally {
      setSavingWinChances(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* THE ONE ONLY Threshold */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 via-purple-500 to-cyan-400 flex items-center justify-center">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">THE ONE AND ONLY Unlock Threshold</h3>
            <p className="text-zinc-500 text-sm mt-1">
              THE ONE AND ONLY tier items can only be won when the pool's remaining stock is at or below this number.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-300">Stock Threshold</label>
            <Input
              type="number"
              value={settings.the_one_only_threshold}
              onChange={(e) => setSettings({ ...settings, the_one_only_threshold: parseInt(e.target.value) || 0 })}
              className="mt-1 bg-zinc-800 border-zinc-700 text-white w-40"
            />
            <p className="text-zinc-600 text-xs mt-1">Default: 1000</p>
          </div>
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-zinc-400 text-xs">
              <strong>Example:</strong> If threshold is 1000, and a pool has 1500 items remaining, 
              THE ONE AND ONLY items will be locked. Once stock drops to 1000 or below, they become winnable.
            </p>
          </div>
        </div>
      </div>

      {/* Tier Win Chances - Bulk Update */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-cyan-500 flex items-center justify-center">
            <Percent className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Tier Win Chances</h3>
            <p className="text-zinc-500 text-sm mt-1">
              Set win probability per tier. Lower = rarer. This affects ALL items of each tier.
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* THE ONE AND ONLY */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-gradient-to-r from-pink-500/10 to-cyan-500/10 border border-pink-500/30">
            <Crown className="w-5 h-5 text-pink-400" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">THE ONE AND ONLY</p>
              <p className="text-zinc-500 text-xs">Ultra rare, jackpot tier</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={tierWinChances.THE_ONE_ONLY}
                onChange={(e) => setTierWinChances({ ...tierWinChances, THE_ONE_ONLY: parseFloat(e.target.value) || 0 })}
                className="w-20 h-8 text-sm bg-zinc-800 border-zinc-700 text-white text-center"
              />
              <span className="text-zinc-400 text-sm">%</span>
            </div>
          </div>
          
          {/* LEGEND */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
            <Star className="w-5 h-5 text-yellow-400" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">LEGEND</p>
              <p className="text-zinc-500 text-xs">Very rare items</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={tierWinChances.LEGEND}
                onChange={(e) => setTierWinChances({ ...tierWinChances, LEGEND: parseFloat(e.target.value) || 0 })}
                className="w-20 h-8 text-sm bg-zinc-800 border-zinc-700 text-white text-center"
              />
              <span className="text-zinc-400 text-sm">%</span>
            </div>
          </div>
          
          {/* EPIC */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
            <Gem className="w-5 h-5 text-purple-400" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">EPIC</p>
              <p className="text-zinc-500 text-xs">Rare items</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={tierWinChances.EPIC}
                onChange={(e) => setTierWinChances({ ...tierWinChances, EPIC: parseFloat(e.target.value) || 0 })}
                className="w-20 h-8 text-sm bg-zinc-800 border-zinc-700 text-white text-center"
              />
              <span className="text-zinc-400 text-sm">%</span>
            </div>
          </div>
          
          {/* RARE */}
          <div className="flex items-center gap-4 p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
            <Package className="w-5 h-5 text-cyan-400" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">RARE</p>
              <p className="text-zinc-500 text-xs">Common items (recommended: 100%)</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="1"
                min="0"
                max="100"
                value={tierWinChances.RARE}
                onChange={(e) => setTierWinChances({ ...tierWinChances, RARE: parseFloat(e.target.value) || 0 })}
                className="w-20 h-8 text-sm bg-zinc-800 border-zinc-700 text-white text-center"
              />
              <span className="text-zinc-400 text-sm">%</span>
            </div>
          </div>
          
          <Button 
            onClick={handleSaveWinChances} 
            disabled={savingWinChances}
            className="w-full bg-green-600 hover:bg-green-700 h-10"
          >
            {savingWinChances ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Apply Win Chances to All Items
          </Button>
          
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-zinc-400 text-xs">
              <strong>How it works:</strong> Each pull, items are filtered by their win chance. 
              If LEGEND is 3%, there's only 3% chance LEGEND items enter the eligible pool. 
              After filtering, drop_rate determines which specific item is selected.
            </p>
          </div>
        </div>
      </div>

      {/* Gacha Algorithm */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4">Gacha Algorithm</h3>
        <div className="space-y-3">
          {[
            { value: 'STATIC', label: 'Static Drop Rates', desc: 'Fixed drop rates based on item configuration' },
            { value: 'DYNAMIC', label: 'Dynamic (Pity System)', desc: 'Increasing chances after consecutive losses' },
            { value: 'HARD_LOCK', label: 'Hard Lock', desc: 'Guaranteed win after certain number of pulls' }
          ].map((opt) => (
            <div
              key={opt.value}
              onClick={() => setSettings({ ...settings, algorithm: opt.value })}
              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                settings.algorithm === opt.value
                  ? 'bg-cyan-600/10 border-cyan-500/50'
                  : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  settings.algorithm === opt.value ? 'border-cyan-500 bg-cyan-500' : 'border-zinc-600'
                }`}>
                  {settings.algorithm === opt.value && (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-white font-medium">{opt.label}</p>
                  <p className="text-zinc-500 text-xs">{opt.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Profit Target */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-2">Profit Target (%)</h3>
        <p className="text-zinc-500 text-sm mb-4">Target profit margin for dynamic algorithm calculations</p>
        <Input
          type="number"
          value={settings.profit_target}
          onChange={(e) => setSettings({ ...settings, profit_target: parseFloat(e.target.value) || 0 })}
          className="bg-zinc-800 border-zinc-700 text-white w-32"
        />
      </div>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700 h-12">
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
        Save Gacha Settings
      </Button>
    </div>
  );
};

// ==================== SEO SETTINGS TAB ====================
const SEOTab = ({ token }) => {
  const [settings, setSettings] = useState({
    site_title: 'TCG Gacha - Premium Trading Card Gacha',
    site_description: 'Experience the thrill of TCG gacha. Collect rare Pokemon cards, anime collectibles, and exclusive items.',
    favicon_url: '',
    og_image_url: ''
  });
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPool, setEditingPool] = useState(null);
  const [poolSEO, setPoolSEO] = useState({ meta_title: '', meta_description: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [seoRes, poolsRes] = await Promise.all([
        axios.get(`${API}/admin/seo`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pools`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSettings(seoRes.data);
      setPools(poolsRes.data);
    } catch (err) {
      console.error('Failed to fetch SEO settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/seo`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SEO settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePoolSEO = async () => {
    if (!editingPool) return;
    try {
      await axios.put(`${API}/admin/pools/${editingPool.id}/seo`, poolSEO, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pool SEO saved');
      fetchData();
      setEditingPool(null);
    } catch (err) {
      toast.error('Failed to save pool SEO');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Site SEO */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Global SEO Settings</h3>
            <p className="text-zinc-500 text-sm">Meta tags for your entire site</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-300">Site Title</label>
            <Input
              value={settings.site_title || ''}
              onChange={(e) => setSettings({ ...settings, site_title: e.target.value })}
              placeholder="TCG Gacha - Premium Trading Card Gacha"
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-600 text-xs mt-1">Appears in browser tab and search results</p>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-300">Site Description</label>
            <Textarea
              value={settings.site_description || ''}
              onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
              placeholder="Experience the thrill of TCG gacha..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              rows={3}
            />
            <p className="text-zinc-600 text-xs mt-1">Meta description for search engines (150-160 characters recommended)</p>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-300">Favicon URL</label>
            <Input
              value={settings.favicon_url || ''}
              onChange={(e) => setSettings({ ...settings, favicon_url: e.target.value })}
              placeholder="https://example.com/favicon.ico"
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-600 text-xs mt-1">URL to your site favicon (ICO, PNG, or SVG)</p>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-300">OG Image URL</label>
            <Input
              value={settings.og_image_url || ''}
              onChange={(e) => setSettings({ ...settings, og_image_url: e.target.value })}
              placeholder="https://example.com/og-image.jpg"
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-600 text-xs mt-1">Image shown when shared on social media (1200x630px recommended)</p>
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full mt-6 bg-cyan-600 hover:bg-cyan-700 h-12">
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
          Save Global SEO
        </Button>
      </div>

      {/* Preview */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4">Search Result Preview</h3>
        <div className="bg-white rounded-lg p-4">
          <p className="text-blue-600 text-lg hover:underline cursor-pointer">{settings.site_title || 'TCG Gacha'}</p>
          <p className="text-green-700 text-sm">tcg-gacha.com</p>
          <p className="text-gray-600 text-sm mt-1">{settings.site_description?.substring(0, 160) || 'Your site description here...'}</p>
        </div>
      </div>

      {/* Pool SEO */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Box className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Pool SEO</h3>
            <p className="text-zinc-500 text-sm">Custom meta tags for each pool page</p>
          </div>
        </div>

        <div className="space-y-2 max-h-80 overflow-y-auto">
          {pools.map((pool) => (
            <div 
              key={pool.id}
              className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <img src={pool.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                <div>
                  <p className="text-white text-sm font-medium">{getLocalizedText(pool.name)}</p>
                  <p className="text-zinc-500 text-xs">
                    {pool.meta_title ? '✓ SEO set' : 'No custom SEO'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingPool(pool);
                  setPoolSEO({
                    meta_title: pool.meta_title || '',
                    meta_description: pool.meta_description || ''
                  });
                }}
                className="text-zinc-400 hover:text-white"
              >
                <Edit className="w-4 h-4 mr-1" /> Edit SEO
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Pool SEO Dialog */}
      <Dialog open={!!editingPool} onOpenChange={() => setEditingPool(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Pool SEO - {getLocalizedText(editingPool?.name)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">Meta Title</label>
              <Input
                value={poolSEO.meta_title}
                onChange={(e) => setPoolSEO({ ...poolSEO, meta_title: e.target.value })}
                placeholder={`${getLocalizedText(editingPool?.name)} | TCG Gacha`}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Meta Description</label>
              <Textarea
                value={poolSEO.meta_description}
                onChange={(e) => setPoolSEO({ ...poolSEO, meta_description: e.target.value })}
                placeholder="Try your luck with this premium gacha pool..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setEditingPool(null)} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSavePoolSEO} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              Save Pool SEO
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIL SETTINGS TAB ====================
const MailSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'TCG Gacha',
    use_tls: true,
    email_verification_enabled: false,
    password_reset_enabled: false,
    deposit_notification_enabled: false,
    purchase_notification_enabled: false,
    redemption_notification_enabled: false,
    admin_redemption_notification_enabled: false,
    password_change_notification_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/smtp`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && Object.keys(res.data).length > 0) {
        setSettings({ ...settings, ...res.data });
      }
    } catch (err) {
      console.error('Failed to fetch SMTP settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/smtp`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mail settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!settings.smtp_host || !settings.smtp_username) {
      toast.error('Please configure SMTP settings first');
      return;
    }
    setTesting(true);
    try {
      await axios.post(`${API}/admin/smtp/test`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Test email sent! Check your inbox');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const ToggleSwitch = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3 border-b border-zinc-800 last:border-0">
      <div>
        <span className="text-white font-medium text-sm">{label}</span>
        {description && <p className="text-zinc-500 text-xs mt-0.5">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`w-12 h-6 rounded-full transition-colors relative ${
          enabled ? 'bg-green-600' : 'bg-zinc-700'
        }`}
      >
        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
          enabled ? 'left-7' : 'left-1'
        }`} />
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* SMTP Server Configuration */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-cyan-500" />
          SMTP Server Configuration
        </h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-sm block mb-2">SMTP Host</label>
              <Input
                value={settings.smtp_host}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                placeholder="smtp.gmail.com"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm block mb-2">Port</label>
              <Input
                type="number"
                value={settings.smtp_port}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                placeholder="587"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          <div>
            <label className="text-zinc-400 text-sm block mb-2">Username / Email</label>
            <Input
              value={settings.smtp_username}
              onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
              placeholder="your-email@gmail.com"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          <div>
            <label className="text-zinc-400 text-sm block mb-2">Password / App Password</label>
            <Input
              type="password"
              value={settings.smtp_password}
              onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
              placeholder="••••••••••••••••"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
            <p className="text-zinc-500 text-xs mt-1">
              For Gmail, use App Password (16 characters)
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-zinc-400 text-sm block mb-2">From Email</label>
              <Input
                value={settings.smtp_from_email}
                onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                placeholder="noreply@tcggacha.com"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-sm block mb-2">From Name</label>
              <Input
                value={settings.smtp_from_name}
                onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                placeholder="TCG Gacha"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div>
              <span className="text-white text-sm">Use TLS</span>
              <p className="text-zinc-500 text-xs">Enable for port 587 (recommended)</p>
            </div>
            <button
              onClick={() => setSettings({ ...settings, use_tls: !settings.use_tls })}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                settings.use_tls ? 'bg-green-600' : 'bg-zinc-700'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                settings.use_tls ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Authentication Features */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Authentication Features
        </h3>
        
        <div className="space-y-1">
          <ToggleSwitch
            enabled={settings.email_verification_enabled}
            onChange={(v) => setSettings({ ...settings, email_verification_enabled: v })}
            label="Email Verification"
            description="Require users to verify email before registration"
          />
          <ToggleSwitch
            enabled={settings.password_reset_enabled}
            onChange={(v) => setSettings({ ...settings, password_reset_enabled: v })}
            label="Password Reset"
            description="Allow users to reset password via email"
          />
          <ToggleSwitch
            enabled={settings.password_change_notification_enabled}
            onChange={(v) => setSettings({ ...settings, password_change_notification_enabled: v })}
            label="Password Change Notification"
            description="Notify users when their password is changed"
          />
        </div>
      </div>

      {/* Transaction Notifications */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-yellow-500" />
          Transaction Notifications
        </h3>
        
        <div className="space-y-1">
          <ToggleSwitch
            enabled={settings.deposit_notification_enabled}
            onChange={(v) => setSettings({ ...settings, deposit_notification_enabled: v })}
            label="Deposit Notifications"
            description="Notify users on successful deposits"
          />
          <ToggleSwitch
            enabled={settings.purchase_notification_enabled}
            onChange={(v) => setSettings({ ...settings, purchase_notification_enabled: v })}
            label="Gacha Purchase Notifications"
            description="Notify users when they win an item from gacha"
          />
          <ToggleSwitch
            enabled={settings.redemption_notification_enabled}
            onChange={(v) => setSettings({ ...settings, redemption_notification_enabled: v })}
            label="Redemption Status Notifications"
            description="Notify users when token redemption status changes"
          />
          <ToggleSwitch
            enabled={settings.admin_redemption_notification_enabled}
            onChange={(v) => setSettings({ ...settings, admin_redemption_notification_enabled: v })}
            label="Admin Redemption Alerts"
            description="Notify admin when new token redemption is submitted"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="flex-1 bg-cyan-600 hover:bg-cyan-700"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
        <Button 
          onClick={handleTestEmail}
          disabled={testing || !settings.smtp_host}
          variant="outline"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
          Send Test Email
        </Button>
      </div>

      {/* Help Section */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h4 className="text-zinc-400 text-sm font-medium mb-2">Quick Setup Guide</h4>
        <ul className="text-zinc-500 text-xs space-y-1.5">
          <li>• <strong>Gmail:</strong> Host: smtp.gmail.com, Port: 587, TLS: Yes. Create App Password at myaccount.google.com</li>
          <li>• <strong>SendGrid:</strong> Host: smtp.sendgrid.net, Port: 587, Username: apikey, Password: your-api-key</li>
          <li>• <strong>Mailgun:</strong> Host: smtp.mailgun.org, Port: 587</li>
        </ul>
      </div>
    </div>
  );
};

// ==================== SOLANA TAB ====================
const SolanaTab = ({ token }) => {
  const [settings, setSettings] = useState({
    rpc_url: 'https://api.mainnet-beta.solana.com',
    rpc_url_devnet: 'https://api.devnet.solana.com',
    rpc_url_mainnet: 'https://api.mainnet-beta.solana.com',
    network: 'mainnet-beta',
    admin_wallet: '',
    admin_wallet_devnet: '',
    admin_wallet_mainnet: '',
    treasury_private_key: '',
    deposit_treasury_private_key: '',
    swap_treasury_devnet: '',
    swap_treasury_mainnet: '',
    swap_treasury_private_key: '',
    has_deposit_treasury_private_key: false,
    has_swap_treasury_private_key: false,
    sol_to_coin_rate: 1000,
    is_active: false,
    deposit_method: 'contract_deposit',
    program_id_devnet: '',
    program_id_mainnet: '',
    // PumpSwap settings for cashback (pool derived from token mint)
    pumpswap_program_devnet: '',
    pumpswap_program_mainnet: '',
    cashback_token_mint_devnet: '',
    cashback_token_mint_mainnet: '',
    cashback_token_symbol_devnet: '',
    cashback_token_symbol_mainnet: '',
    cashback_bps_devnet: 100,
    cashback_bps_mainnet: 100
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Smart contract config state
  const [contractConfig, setContractConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/solana/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data;
      // Initialize separate RPC URLs if not present
      if (!data.rpc_url_devnet) {
        data.rpc_url_devnet = data.network === 'devnet' ? data.rpc_url : 'https://api.devnet.solana.com';
      }
      if (!data.rpc_url_mainnet) {
        data.rpc_url_mainnet = data.network === 'mainnet-beta' ? data.rpc_url : 'https://api.mainnet-beta.solana.com';
      }
      // Initialize deposit method fields - default to contract_deposit (no legacy)
      if (!data.deposit_method || data.deposit_method === 'legacy') data.deposit_method = 'contract_deposit';
      if (!data.program_id_devnet) data.program_id_devnet = '';
      if (!data.program_id_mainnet) data.program_id_mainnet = '';
      // Initialize separate treasury wallets
      if (!data.admin_wallet_devnet) data.admin_wallet_devnet = data.network === 'devnet' ? data.admin_wallet : '';
      if (!data.admin_wallet_mainnet) data.admin_wallet_mainnet = data.network === 'mainnet-beta' ? data.admin_wallet : '';
      // Initialize PumpSwap settings for cashback (pool derived from token mint)
      if (!data.pumpswap_program_devnet) data.pumpswap_program_devnet = '';
      if (!data.pumpswap_program_mainnet) data.pumpswap_program_mainnet = '';
      if (!data.cashback_token_mint_devnet) data.cashback_token_mint_devnet = '';
      if (!data.cashback_token_mint_mainnet) data.cashback_token_mint_mainnet = '';
      if (!data.cashback_token_symbol_devnet) data.cashback_token_symbol_devnet = '';
      if (!data.cashback_token_symbol_mainnet) data.cashback_token_symbol_mainnet = '';
      if (!data.cashback_bps_devnet && data.cashback_bps_devnet !== 0) data.cashback_bps_devnet = 100;
      if (!data.cashback_bps_mainnet && data.cashback_bps_mainnet !== 0) data.cashback_bps_mainnet = 100;
      if (!data.swap_treasury_devnet) data.swap_treasury_devnet = '';
      if (!data.swap_treasury_mainnet) data.swap_treasury_mainnet = '';
      setSettings(data);
    } catch (err) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch smart contract config from on-chain
  const fetchContractConfig = async (network) => {
    const programId = network === 'devnet' ? settings.program_id_devnet : settings.program_id_mainnet;
    if (!programId) {
      toast.error(`Program ID for ${network === 'devnet' ? 'Devnet' : 'Mainnet'} not configured`);
      return;
    }
    
    setLoadingConfig(true);
    setConfigError(null);
    setContractConfig(null);
    
    try {
      const res = await axios.post(`${API}/solana/read-contract-config`, {
        program_id: programId,
        network: network
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setContractConfig({ ...res.data, network });
      
      // Check if treasury matches
      const currentTreasury = network === 'devnet' ? settings.admin_wallet_devnet : settings.admin_wallet_mainnet;
      if (res.data.treasury && res.data.treasury !== currentTreasury) {
        toast.warning(`Treasury address mismatch for ${network === 'devnet' ? 'Devnet' : 'Mainnet'}! Click "Use" to update.`);
      } else {
        toast.success('Contract config loaded successfully');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to read contract config';
      setConfigError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoadingConfig(false);
    }
  };
  
  // Apply treasury wallet from contract config to settings
  const applyContractTreasury = (network) => {
    if (contractConfig?.treasury) {
      if (network === 'devnet') {
        setSettings({ 
          ...settings, 
          admin_wallet_devnet: contractConfig.treasury,
          admin_wallet: settings.network === 'devnet' ? contractConfig.treasury : settings.admin_wallet
        });
      } else {
        setSettings({ 
          ...settings, 
          admin_wallet_mainnet: contractConfig.treasury,
          admin_wallet: settings.network === 'mainnet-beta' ? contractConfig.treasury : settings.admin_wallet
        });
      }
      toast.success(`Treasury wallet updated for ${network === 'devnet' ? 'Devnet' : 'Mainnet'}`);
    }
  };
  
  // PumpSwap validation state
  const [pumpswapConfig, setPumpswapConfig] = useState(null);
  const [loadingPumpswap, setLoadingPumpswap] = useState(false);
  const [pumpswapError, setPumpswapError] = useState(null);
  
  // Validate PumpSwap settings by reading pool data from on-chain
  const validatePumpswapSettings = async (network) => {
    const isDevnet = network === 'devnet';
    const pumpswapProgram = isDevnet ? settings.pumpswap_program_devnet : settings.pumpswap_program_mainnet;
    const cashbackMint = isDevnet ? settings.cashback_token_mint_devnet : settings.cashback_token_mint_mainnet;
    const rpcUrl = isDevnet ? settings.rpc_url_devnet : settings.rpc_url_mainnet;
    
    if (!pumpswapProgram || !cashbackMint) {
      toast.error('Please fill in PumpSwap Program and Token Mint first');
      return;
    }
    
    setLoadingPumpswap(true);
    setPumpswapError(null);
    setPumpswapConfig(null);
    
    try {
      // Validate addresses format
      const { Connection, PublicKey } = await import('@solana/web3.js');
      const { canonicalPumpPoolPda } = await import('@pump-fun/pump-swap-sdk');
      
      let pumpswapProgramPubkey, cashbackMintPubkey;
      try {
        pumpswapProgramPubkey = new PublicKey(pumpswapProgram);
      } catch (e) {
        throw new Error('Invalid PumpSwap Program ID format');
      }
      try {
        cashbackMintPubkey = new PublicKey(cashbackMint);
      } catch (e) {
        throw new Error('Invalid Cashback Token Mint format');
      }
      
      // Derive pool address from token mint using SDK
      let poolPubkey = canonicalPumpPoolPda(cashbackTokenMintPubkey);
      if (!poolPubkey) {
        throw new Error('Failed to derive pool address from token mint');
      }
      console.log('Derived canonical pool address:', poolPubkey.toString());
      
      // Connect and fetch pool data
      const connection = new Connection(rpcUrl, 'confirmed');
      
      // Check if canonical pool account exists
      let poolAccountInfo = await connection.getAccountInfo(poolPubkey);
      
      // Fallback: if canonical pool not found, search PumpSwap program accounts
      // for any pool with this token as base_mint (offset 43 in Pool struct).
      if (!poolAccountInfo) {
        console.log('Canonical pool not found, searching all PumpSwap pools by base_mint…');
        const found = await connection.getProgramAccounts(pumpswapProgramPubkey, {
          encoding: 'base64',
          filters: [
            { memcmp: { offset: 43, bytes: cashbackMintPubkey.toString() } },
          ],
        });
        if (!found || found.length === 0) {
          throw new Error(`No PumpSwap pool found for token ${cashbackMint}. Token may not have a PumpSwap pool yet.`);
        }
        // Pick the first matching pool (or one with quote_mint = WSOL)
        const WSOL_STR = 'So11111111111111111111111111111111111111112';
        const wsolPool = found.find((p) => {
          try {
            const qm = new PublicKey(p.account.data.slice(75, 107)).toString();
            return qm === WSOL_STR;
          } catch { return false; }
        }) || found[0];
        poolPubkey = wsolPool.pubkey;
        poolAccountInfo = wsolPool.account;
        console.log(`Found non-canonical pool (${found.length} total):`, poolPubkey.toString());
      }
      
      // Check pool owner matches PumpSwap program
      if (poolAccountInfo.owner.toString() !== pumpswapProgramPubkey.toString()) {
        throw new Error(`Pool is not owned by PumpSwap program. Owner: ${poolAccountInfo.owner.toString()}`);
      }
      
      // Parse PumpSwap pool data
      // Pool layout (from SDK IDL): discriminator(8) + pool_bump(1) + index(2) + 
      //   creator(32) + base_mint(32) + quote_mint(32) + lp_mint(32) + 
      //   pool_base_token_account(32) + pool_quote_token_account(32) + ...
      const poolData = poolAccountInfo.data;
      let offset = 8 + 1 + 2 + 32; // discriminator + pool_bump + index + creator = 43
      
      const baseMintBytes = poolData.slice(offset, offset + 32);
      const baseMint = new PublicKey(baseMintBytes).toString();
      offset += 32;
      
      const quoteMintBytes = poolData.slice(offset, offset + 32);
      const quoteMint = new PublicKey(quoteMintBytes).toString();
      offset += 32;
      
      // skip lp_mint (32 bytes)
      offset += 32;
      
      const baseVaultBytes = poolData.slice(offset, offset + 32);
      const baseVault = new PublicKey(baseVaultBytes).toString();
      offset += 32;
      
      const quoteVaultBytes = poolData.slice(offset, offset + 32);
      const quoteVault = new PublicKey(quoteVaultBytes).toString();
      
      // Verify cashback token mint matches base mint
      const cashbackMintMatches = baseMint === cashbackMintPubkey.toString();
      
      // WSOL address (quote should be WSOL for SOL->Token swap)
      const WSOL = 'So11111111111111111111111111111111111111112';
      const isQuoteWSOL = quoteMint === WSOL;
      
      // Fetch global config PDA
      const [globalConfigPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('global_config')],
        pumpswapProgramPubkey
      );
      
      const globalConfigInfo = await connection.getAccountInfo(globalConfigPda);
      let protocolFeeRecipient = null;
      if (globalConfigInfo) {
        const gcData = globalConfigInfo.data;
        let gcOffset = 8 + 32; // discriminator + authority
        const feeRecipientBytes = gcData.slice(gcOffset, gcOffset + 32);
        protocolFeeRecipient = new PublicKey(feeRecipientBytes).toString();
      }
      
      const config = {
        network,
        poolAddress: poolPubkey.toString(),
        poolOwner: poolAccountInfo.owner.toString(),
        baseMint,
        quoteMint,
        baseVault,
        quoteVault,
        cashbackMintMatches,
        isQuoteWSOL,
        globalConfig: globalConfigPda.toString(),
        protocolFeeRecipient,
        valid: cashbackMintMatches && isQuoteWSOL
      };
      
      setPumpswapConfig(config);
      
      if (!cashbackMintMatches) {
        toast.warning('Cashback Token Mint does not match pool base mint!');
      } else if (!isQuoteWSOL) {
        toast.warning('Pool quote mint is not WSOL. This pool may not work for SOL deposits.');
      } else {
        toast.success('PumpSwap settings validated successfully!');
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to validate PumpSwap settings';
      setPumpswapError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoadingPumpswap(false);
    }
  };
  
  // Apply base mint from pool as cashback token mint
  const applyPoolBaseMint = (network) => {
    if (pumpswapConfig?.baseMint) {
      const isDevnet = network === 'devnet';
      setSettings({
        ...settings,
        [isDevnet ? 'cashback_token_mint_devnet' : 'cashback_token_mint_mainnet']: pumpswapConfig.baseMint
      });
      toast.success('Cashback Token Mint updated from pool');
    }
  };

  const switchNetwork = (network) => {
    // If switching to mainnet, validate required settings
    if (network === 'mainnet-beta') {
      const missingMainnet = [];
      if (!settings.program_id_mainnet) missingMainnet.push('Program ID');
      if (!settings.admin_wallet_mainnet) missingMainnet.push('Treasury Wallet');
      if (!settings.rpc_url_mainnet) missingMainnet.push('RPC URL');
      
      // Check cashback settings if deposit method is contract_cashback
      if (settings.deposit_method === 'contract_cashback') {
        if (!settings.pumpswap_program_mainnet) missingMainnet.push('PumpSwap Program');
        if (!settings.cashback_token_mint_mainnet) missingMainnet.push('Cashback Token Mint');
      }
      
      if (missingMainnet.length > 0) {
        toast.error(`Mainnet settings incomplete: ${missingMainnet.join(', ')}`);
        return; // Don't switch
      }
    }
    
    const rpc_url = network === 'devnet' ? settings.rpc_url_devnet : settings.rpc_url_mainnet;
    const admin_wallet = network === 'devnet' ? settings.admin_wallet_devnet : settings.admin_wallet_mainnet;
    setSettings({ ...settings, network, rpc_url, admin_wallet });
    // Clear contract config when switching network
    setContractConfig(null);
    setConfigError(null);
    
    if (network === 'mainnet-beta') {
      toast.success('Switched to MAINNET - Production mode active!', { duration: 4000 });
    } else {
      toast.info('Switched to DEVNET - Testing mode');
    }
  };

  const handleSave = async () => {
    // Validate based on active network
    const currentProgramId = settings.network === 'devnet' ? settings.program_id_devnet : settings.program_id_mainnet;
    const currentTreasury = settings.network === 'devnet' ? settings.admin_wallet_devnet : settings.admin_wallet_mainnet;
    
    if (settings.is_active) {
      if (!currentProgramId) {
        toast.error(`Program ID for ${settings.network === 'devnet' ? 'Devnet' : 'Mainnet'} is required`);
        return;
      }
      if (!currentTreasury) {
        toast.error(`Treasury wallet for ${settings.network === 'devnet' ? 'Devnet' : 'Mainnet'} is required`);
        return;
      }
    }
    
    setSaving(true);
    try {
      // Sync admin_wallet with the active network's treasury
      const saveData = {
        ...settings,
        admin_wallet: currentTreasury
      };
      await axios.put(`${API}/admin/solana/settings`, saveData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const depositMethods = [
    { 
      id: 'contract_deposit', 
      name: 'Smart Contract: Deposit', 
      description: 'User deposits via smart contract to treasury. Secure on-chain verification.',
      icon: Shield,
      color: 'green'
    },
    { 
      id: 'contract_cashback', 
      name: 'Smart Contract: Deposit + Cashback', 
      description: 'User deposits via contract and receives token cashback via PumpSwap.',
      icon: Gift,
      color: 'purple'
    }
  ];

  // Network config component
  const NetworkConfigSection = ({ network, isActive }) => {
    const isDevnet = network === 'devnet';
    const borderColor = isActive 
      ? (isDevnet ? 'border-yellow-500' : 'border-green-500')
      : 'border-zinc-700';
    const bgColor = isActive 
      ? (isDevnet ? 'bg-yellow-900/10' : 'bg-green-900/10')
      : 'bg-zinc-800/20';
    const accentColor = isDevnet ? 'yellow' : 'green';
    const Icon = isDevnet ? Zap : Shield;
    
    const programId = isDevnet ? settings.program_id_devnet : settings.program_id_mainnet;
    const rpcUrl = isDevnet ? settings.rpc_url_devnet : settings.rpc_url_mainnet;
    const treasury = isDevnet ? settings.admin_wallet_devnet : settings.admin_wallet_mainnet;
    const swapTreasury = isDevnet ? settings.swap_treasury_devnet : settings.swap_treasury_mainnet;
    const netKey = isDevnet ? 'devnet' : 'mainnet';
    
    return (
      <div className={`rounded-xl border-2 ${borderColor} ${bgColor} overflow-hidden`}>
        {/* Header */}
        <div className={`px-5 py-3 flex items-center justify-between ${isActive ? (isDevnet ? 'bg-yellow-600' : 'bg-green-600') : 'bg-zinc-800'}`}>
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5 text-white" />
            <span className="font-bold text-white">{isDevnet ? 'DEVNET' : 'MAINNET'}</span>
            <span className="text-white/70 text-sm">({isDevnet ? 'Testing' : 'Production'})</span>
          </div>
          {isActive && (
            <span className="px-3 py-1 bg-white/20 text-white text-xs rounded-full font-bold">ACTIVE</span>
          )}
        </div>
        
        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Program ID */}
          <div>
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              Program ID
            </label>
            <Input
              value={programId}
              onChange={(e) => setSettings({ 
                ...settings, 
                [isDevnet ? 'program_id_devnet' : 'program_id_mainnet']: e.target.value 
              })}
              placeholder={isDevnet ? "e.g., J2xLY7BPnx332HacQfTJi5vW2K8Zde2SaBXMvsY7qFpD" : "Enter Mainnet Program ID"}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
            />
          </div>
          
          {/* RPC URL */}
          <div>
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              RPC URL
            </label>
            <Input
              value={rpcUrl}
              onChange={(e) => {
                const newUrl = e.target.value;
                const updates = isDevnet 
                  ? { rpc_url_devnet: newUrl, rpc_url: settings.network === 'devnet' ? newUrl : settings.rpc_url }
                  : { rpc_url_mainnet: newUrl, rpc_url: settings.network === 'mainnet-beta' ? newUrl : settings.rpc_url };
                setSettings({ ...settings, ...updates });
              }}
              placeholder={isDevnet ? "https://api.devnet.solana.com" : "https://api.mainnet-beta.solana.com"}
              className="bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          
          {/* Deposit Treasury Wallet */}
          <div>
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              Deposit Treasury Wallet
            </label>
            <Input
              value={treasury}
              onChange={(e) => {
                const newWallet = e.target.value;
                const updates = isDevnet 
                  ? { admin_wallet_devnet: newWallet, admin_wallet: settings.network === 'devnet' ? newWallet : settings.admin_wallet }
                  : { admin_wallet_mainnet: newWallet, admin_wallet: settings.network === 'mainnet-beta' ? newWallet : settings.admin_wallet };
                setSettings({ ...settings, ...updates });
              }}
              placeholder="Deposit treasury wallet (receives SOL deposits)"
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              data-testid={`deposit-treasury-input-${netKey}`}
            />
            <p className="text-zinc-500 text-xs mt-1">Receives user SOL deposits. Must match treasury in smart contract config.</p>
          </div>

          {/* Deposit Treasury Private Key */}
          <div>
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              Deposit Treasury Private Key
            </label>
            <Input
              type="password"
              value={settings.deposit_treasury_private_key || settings.treasury_private_key || ''}
              onChange={(e) => setSettings({ ...settings, deposit_treasury_private_key: e.target.value, treasury_private_key: e.target.value })}
              placeholder={settings.has_deposit_treasury_private_key ? '•••••••• (saved)' : 'Base58 encoded private key of deposit treasury'}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              data-testid={`deposit-treasury-privatekey-input-${netKey}`}
            />
            <p className="text-zinc-500 text-xs mt-1">Optional. Used to manage deposit treasury funds. Saved encrypted.</p>
          </div>

          {/* Swap Treasury Wallet (separate from deposit) */}
          <div className="pt-3 mt-3 border-t border-zinc-700/60">
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              Swap Treasury Wallet
            </label>
            <Input
              value={swapTreasury || ''}
              onChange={(e) => {
                const newWallet = e.target.value;
                const updates = isDevnet 
                  ? { swap_treasury_devnet: newWallet }
                  : { swap_treasury_mainnet: newWallet };
                setSettings({ ...settings, ...updates });
              }}
              placeholder="Swap treasury wallet (used for swap / cashback payouts)"
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              data-testid={`swap-treasury-input-${netKey}`}
            />
            <p className="text-zinc-500 text-xs mt-1">Separate wallet dedicated to swap/cashback ecosystem. Keeps deposit and swap funds isolated.</p>
          </div>

          {/* Swap Treasury Private Key */}
          <div>
            <label className={`text-sm font-medium ${isActive ? (isDevnet ? 'text-yellow-400' : 'text-green-400') : 'text-zinc-400'} mb-2 block`}>
              Swap Treasury Private Key (for Swap)
            </label>
            <Input
              type="password"
              value={settings.swap_treasury_private_key || ''}
              onChange={(e) => setSettings({ ...settings, swap_treasury_private_key: e.target.value })}
              placeholder={settings.has_swap_treasury_private_key ? '•••••••• (saved)' : 'Base58 encoded private key for auto-transfer'}
              className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              data-testid={`swap-treasury-privatekey-input-${netKey}`}
            />
            <p className="text-zinc-500 text-xs mt-1">Required for auto-sending when accepting swap requests. Saved encrypted.</p>
          </div>
          
          {/* Read Config Button */}
          <Button
            onClick={() => fetchContractConfig(network)}
            disabled={loadingConfig || !programId}
            variant="outline"
            size="sm"
            className={`w-full border-${accentColor}-600/50 text-${accentColor}-400 hover:bg-${accentColor}-900/20`}
          >
            {loadingConfig && contractConfig?.network === network ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reading...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Read Config from Contract
              </>
            )}
          </Button>
          
          {/* PumpSwap Settings - Only show when cashback method is selected */}
          {settings.deposit_method === 'contract_cashback' && (
            <div className="p-4 rounded-lg border border-purple-600/50 bg-purple-900/10 space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-semibold">PumpSwap Cashback Settings</span>
              </div>
              
              {/* Network-Specific Notice - Show based on whether settings are configured */}
              {(() => {
                const hasSettings = isDevnet 
                  ? (settings.pumpswap_program_devnet && settings.cashback_token_mint_devnet)
                  : (settings.pumpswap_program_mainnet && settings.cashback_token_mint_mainnet);
                
                return (
                  <div className={`p-3 rounded-lg ${hasSettings ? 'bg-green-900/20 border border-green-600/30' : 'bg-yellow-900/30 border border-yellow-600/50'}`}>
                    <div className="flex items-start gap-2">
                      {hasSettings ? (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <p className={`text-xs font-medium ${hasSettings ? 'text-green-400' : 'text-yellow-400'}`}>
                          {hasSettings 
                            ? `Fitur Cashback Aktif (${isDevnet ? 'Devnet' : 'Mainnet'})`
                            : `Fitur Cashback Belum Dikonfigurasi (${isDevnet ? 'Devnet' : 'Mainnet'})`
                          }
                        </p>
                        <p className={`text-[10px] mt-1 ${hasSettings ? 'text-green-500/80' : 'text-yellow-500/80'}`}>
                          {hasSettings 
                            ? 'Fitur cashback aktif. User akan menerima token cashback via PumpSwap saat deposit.'
                            : 'Lengkapi semua field di bawah untuk mengaktifkan fitur cashback.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              {/* PumpSwap Program ID */}
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">
                  PumpSwap Program ID
                </label>
                <Input
                  value={isDevnet ? settings.pumpswap_program_devnet : settings.pumpswap_program_mainnet}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    [isDevnet ? 'pumpswap_program_devnet' : 'pumpswap_program_mainnet']: e.target.value 
                  })}
                  placeholder="e.g., pAMMBay6oceH9fJKBRHGP5D4bD4sWpmSwMn52FMfXEA"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs"
                />
                {!isDevnet && (
                  <p className="text-zinc-500 text-[10px] mt-1">
                    PumpSwap Program: pAMMBay6oceH9fJKBRHGP5D4bD4sWpmSwMn52FMfXEA
                  </p>
                )}
              </div>
              
              {/* Cashback Token Mint */}
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">
                  Cashback Token Mint
                </label>
                <Input
                  value={isDevnet ? settings.cashback_token_mint_devnet : settings.cashback_token_mint_mainnet}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    [isDevnet ? 'cashback_token_mint_devnet' : 'cashback_token_mint_mainnet']: e.target.value 
                  })}
                  placeholder="Token mint address for cashback rewards"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs"
                />
                <p className="text-zinc-500 text-[10px] mt-1">
                  Pool address akan otomatis di-derive dari token mint ini
                </p>
              </div>
              
              {/* Cashback Token Symbol */}
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">
                  Token Symbol
                </label>
                <Input
                  value={isDevnet ? settings.cashback_token_symbol_devnet : settings.cashback_token_symbol_mainnet}
                  onChange={(e) => setSettings({ 
                    ...settings, 
                    [isDevnet ? 'cashback_token_symbol_devnet' : 'cashback_token_symbol_mainnet']: e.target.value.toUpperCase()
                  })}
                  placeholder="e.g., GACHA, TCG, PUMP"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs uppercase"
                  maxLength={10}
                />
                <p className="text-zinc-500 text-[10px] mt-1">
                  Symbol akan ditampilkan di history sebagai $SYMBOL
                </p>
              </div>

              {/* Cashback BPS (Basis Points) */}
              <div>
                <label className="text-xs font-medium text-purple-300 mb-1 block">
                  Cashback Rate (BPS)
                </label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={isDevnet ? settings.cashback_bps_devnet : settings.cashback_bps_mainnet}
                    onChange={(e) => setSettings({
                      ...settings,
                      [isDevnet ? 'cashback_bps_devnet' : 'cashback_bps_mainnet']: parseInt(e.target.value) || 0
                    })}
                    placeholder="100"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs"
                    min={0}
                    max={10000}
                  />
                  <span className="text-purple-400 font-bold text-sm whitespace-nowrap">
                    = {((isDevnet ? settings.cashback_bps_devnet : settings.cashback_bps_mainnet) / 100).toFixed(2)}%
                  </span>
                </div>
                <p className="text-zinc-500 text-[10px] mt-1">
                  100 bps = 1% | Smart contract harus di-update dengan nilai yang sama
                </p>
              </div>
              
              {/* Validate PumpSwap Button */}
              <Button
                onClick={() => validatePumpswapSettings(network)}
                disabled={loadingPumpswap || 
                  !(isDevnet ? settings.pumpswap_program_devnet : settings.pumpswap_program_mainnet) ||
                  !(isDevnet ? settings.cashback_token_mint_devnet : settings.cashback_token_mint_mainnet)
                }
                variant="outline"
                size="sm"
                className="w-full border-purple-600/50 text-purple-400 hover:bg-purple-900/20"
              >
                {loadingPumpswap && pumpswapConfig?.network !== network ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Validate PumpSwap Settings
                  </>
                )}
              </Button>
              
              {/* PumpSwap Validation Error */}
              {pumpswapError && (
                <div className="p-3 rounded-lg bg-red-900/20 border border-red-700/50">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-red-400" />
                    <span className="text-red-400 text-xs">{pumpswapError}</span>
                  </div>
                </div>
              )}
              
              {/* PumpSwap Validation Result */}
              {pumpswapConfig && pumpswapConfig.network === network && (
                <div className={`p-3 rounded-lg border space-y-2 ${pumpswapConfig.valid ? 'bg-green-900/20 border-green-700/50' : 'bg-yellow-900/20 border-yellow-700/50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {pumpswapConfig.valid ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    )}
                    <span className={`text-sm font-medium ${pumpswapConfig.valid ? 'text-green-400' : 'text-yellow-400'}`}>
                      {pumpswapConfig.valid ? 'Pool Validated' : 'Pool Found (with warnings)'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <span className="text-zinc-500">Derived Pool:</span>
                    <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.poolAddress?.slice(0, 8)}...{pumpswapConfig.poolAddress?.slice(-4)}</code>
                    
                    <span className="text-zinc-500">Pool Owner:</span>
                    <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.poolOwner?.slice(0, 8)}...{pumpswapConfig.poolOwner?.slice(-4)}</code>
                    
                    <span className="text-zinc-500">Base Mint (Token):</span>
                    <div className="flex items-center gap-1">
                      <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.baseMint?.slice(0, 8)}...{pumpswapConfig.baseMint?.slice(-4)}</code>
                      {pumpswapConfig.cashbackMintMatches ? (
                        <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                      ) : (
                        <button 
                          onClick={() => applyPoolBaseMint(network)}
                          className="text-[10px] px-1.5 py-0.5 bg-purple-600 hover:bg-purple-700 rounded text-white flex-shrink-0"
                        >
                          Use
                        </button>
                      )}
                    </div>
                    
                    <span className="text-zinc-500">Quote Mint:</span>
                    <div className="flex items-center gap-1">
                      <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.quoteMint?.slice(0, 8)}...{pumpswapConfig.quoteMint?.slice(-4)}</code>
                      {pumpswapConfig.isQuoteWSOL ? (
                        <span className="text-[10px] px-1.5 py-0.5 bg-green-600/30 rounded text-green-400 flex-shrink-0">WSOL ✓</span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 bg-yellow-600/30 rounded text-yellow-400 flex-shrink-0">Not WSOL</span>
                      )}
                    </div>
                    
                    <span className="text-zinc-500">Base Vault:</span>
                    <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.baseVault?.slice(0, 8)}...{pumpswapConfig.baseVault?.slice(-4)}</code>
                    
                    <span className="text-zinc-500">Quote Vault:</span>
                    <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.quoteVault?.slice(0, 8)}...{pumpswapConfig.quoteVault?.slice(-4)}</code>
                    
                    {pumpswapConfig.protocolFeeRecipient && (
                      <>
                        <span className="text-zinc-500">Fee Recipient:</span>
                        <code className="text-zinc-300 font-mono truncate">{pumpswapConfig.protocolFeeRecipient?.slice(0, 8)}...{pumpswapConfig.protocolFeeRecipient?.slice(-4)}</code>
                      </>
                    )}
                  </div>
                  
                  {!pumpswapConfig.cashbackMintMatches && (
                    <p className="text-yellow-400 text-[10px] mt-2">
                      ⚠️ Cashback Token Mint doesn't match pool's base mint. Click "Use" to fix.
                    </p>
                  )}
                  {!pumpswapConfig.isQuoteWSOL && (
                    <p className="text-yellow-400 text-[10px]">
                      ⚠️ Pool quote mint is not WSOL. Users deposit SOL which is wrapped to WSOL.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Contract Config Display */}
          {contractConfig && contractConfig.network === network && (
            <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700 space-y-2">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-green-400 text-sm font-medium">Config Loaded</span>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span className="text-zinc-500">Authority:</span>
                <code className="text-zinc-300 font-mono truncate">{contractConfig.authority}</code>
                
                <span className="text-zinc-500">Treasury:</span>
                <div className="flex items-center gap-1">
                  <code className={`font-mono truncate ${
                    contractConfig.treasury === treasury ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {contractConfig.treasury?.slice(0, 20)}...
                  </code>
                  {contractConfig.treasury !== treasury && (
                    <button
                      onClick={() => applyContractTreasury(network)}
                      className="px-1.5 py-0.5 bg-cyan-600 hover:bg-cyan-500 text-white text-[10px] rounded font-medium flex-shrink-0"
                    >
                      Use
                    </button>
                  )}
                </div>
                
                <span className="text-zinc-500">Cashback:</span>
                <span className="text-zinc-300">{contractConfig.cashback_bps / 100}%</span>
                
                <span className="text-zinc-500">Paused:</span>
                <span className={contractConfig.paused ? 'text-red-400' : 'text-green-400'}>
                  {contractConfig.paused ? 'Yes' : 'No'}
                </span>
              </div>
              
              {contractConfig.treasury !== treasury && (
                <div className="p-2 rounded bg-red-900/30 border border-red-600/50 mt-2">
                  <p className="text-red-400 text-[10px] flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Treasury mismatch! Click "Use" to update.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Config Error for this network */}
          {configError && contractConfig?.network === network && (
            <div className="p-2 rounded-lg bg-red-900/20 border border-red-600/50">
              <p className="text-red-400 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {configError}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Network Mode Banner */}
      {settings.network === 'devnet' && (
        <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-yellow-500 font-semibold">Devnet Mode Active</h4>
            <p className="text-yellow-300/70 text-sm mt-1">
              You are currently in testing mode. All transactions use test SOL, not real money. 
              Switch to Mainnet when ready for production.
            </p>
          </div>
        </div>
      )}

      {/* Enable Toggle + Deposit Method */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-6">
        {/* Enable Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Enable Solana Payments</h3>
            <p className="text-zinc-500 text-sm mt-1">Allow users to top up coins with SOL</p>
          </div>
          <button
            onClick={() => setSettings({ ...settings, is_active: !settings.is_active })}
            className={`w-14 h-7 rounded-full transition-colors relative ${
              settings.is_active ? 'bg-green-600' : 'bg-zinc-700'
            }`}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all ${
              settings.is_active ? 'left-8' : 'left-1'
            }`} />
          </button>
        </div>
        
        {/* Deposit Method Selection */}
        <div className="pt-4 border-t border-zinc-800">
          <label className="text-white font-semibold mb-3 block">Deposit Method</label>
          <div className="grid grid-cols-2 gap-3">
            {depositMethods.map((method) => {
              const Icon = method.icon;
              const isSelected = settings.deposit_method === method.id;
              
              return (
                <button
                  key={method.id}
                  onClick={() => setSettings({ ...settings, deposit_method: method.id })}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    isSelected 
                      ? method.color === 'green' 
                        ? 'border-green-500 bg-green-900/20' 
                        : 'border-purple-500 bg-purple-900/20'
                      : 'border-zinc-700 bg-zinc-800/30 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-5 h-5 ${
                      isSelected 
                        ? method.color === 'green' ? 'text-green-400' : 'text-purple-400'
                        : 'text-zinc-500'
                    }`} />
                    {isSelected && (
                      <CheckCircle className={`w-4 h-4 ${
                        method.color === 'green' ? 'text-green-400' : 'text-purple-400'
                      }`} />
                    )}
                  </div>
                  <p className={`font-medium text-sm ${isSelected ? 'text-white' : 'text-zinc-400'}`}>
                    {method.name.replace('Smart Contract: ', '')}
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">{method.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Network Mode Toggle */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <label className="text-white font-semibold mb-4 block">Active Network</label>
        <div className="flex rounded-xl bg-zinc-800 p-1 gap-1">
          <button
            onClick={() => switchNetwork('devnet')}
            className={`flex-1 py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
              settings.network === 'devnet' 
                ? 'bg-yellow-600 text-white shadow-lg' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            <Zap className="w-4 h-4" />
            <span className="font-semibold">Devnet</span>
          </button>
          <button
            onClick={() => switchNetwork('mainnet-beta')}
            className={`flex-1 py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 ${
              settings.network === 'mainnet-beta' 
                ? 'bg-green-600 text-white shadow-lg' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span className="font-semibold">Mainnet</span>
          </button>
        </div>
      </div>

      {/* Network Configurations - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NetworkConfigSection network="devnet" isActive={settings.network === 'devnet'} />
        <NetworkConfigSection network="mainnet-beta" isActive={settings.network === 'mainnet-beta'} />
      </div>

      {/* Conversion Rate */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <label className="text-white font-semibold">Conversion Rate</label>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-zinc-400">1 SOL =</span>
          <Input
            type="number"
            value={settings.sol_to_coin_rate}
            onChange={(e) => setSettings({ ...settings, sol_to_coin_rate: parseFloat(e.target.value) || 0 })}
            className="w-32 bg-zinc-800 border-zinc-700 text-white text-center font-bold"
          />
          <span className="text-zinc-400">Coins</span>
        </div>
      </div>

      {/* Preview */}
      {settings.is_active && (
        <div className="bg-purple-900/20 border border-purple-800/30 rounded-xl p-6">
          <h4 className="text-purple-400 font-semibold mb-3">Preview</h4>
          <div className="space-y-2 text-sm">
            <p className="text-white">User pays <span className="text-purple-400 font-bold">0.1 SOL</span></p>
            <p className="text-white">User receives <span className="text-yellow-400 font-bold">{(0.1 * settings.sol_to_coin_rate).toFixed(0)} Coins</span></p>
            <p className="text-zinc-400 text-xs mt-2">
              Network: <span className={settings.network === 'devnet' ? 'text-yellow-400' : 'text-green-400'}>{settings.network === 'devnet' ? 'Devnet' : 'Mainnet'}</span>
              {' • '}
              Method: <span className="text-purple-300">{depositMethods.find(m => m.id === settings.deposit_method)?.name}</span>
            </p>
          </div>
        </div>
      )}

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700 h-12">
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
        Save Settings
      </Button>
    </div>
  );
};

// ==================== TOKEN SETTINGS TAB ====================
const TokenSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState({
    token_name: 'Reward Token',
    symbol: 'RWT',
    contract_address: '',
    icon_url: '',
    decimals: 9,
    is_active: true,
    min_redeem_amount: 100.0,
    withdrawal_enabled: false,
    treasury_wallet: '',
    treasury_private_key: '',
    withdrawal_fee_percent: 0,
    max_withdrawal_per_day: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/token/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(s => ({ ...s, ...res.data }));
    } catch (err) {
      console.error('Failed to fetch token settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/token/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Token settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/token/upload-icon`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setSettings(s => ({ ...s, icon_url: res.data.icon_url }));
      toast.success('Icon uploaded!');
    } catch (err) {
      toast.error('Failed to upload icon');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Token Settings</h2>
        <p className="text-zinc-400 text-sm">Configure the reward token that replaces tickets. Users earn tokens from pool purchases and can redeem them in the shop or withdraw to their wallet.</p>
      </div>

      <div className="bg-zinc-800/50 rounded-xl p-6 space-y-6">
        {/* Enable Token */}
        <div className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg">
          <div>
            <p className="text-white font-medium">Enable Token System</p>
            <p className="text-zinc-500 text-sm">Use custom token instead of tickets</p>
          </div>
          <button
            onClick={() => setSettings(s => ({ ...s, is_active: !s.is_active }))}
            className={`w-12 h-6 rounded-full transition-colors ${settings.is_active ? 'bg-green-500' : 'bg-zinc-600'}`}
          >
            <div className={`w-5 h-5 bg-white rounded-full transition-transform ${settings.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
          </button>
        </div>

        {/* Token Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 block">Token Name</label>
            <Input
              value={settings.token_name}
              onChange={(e) => setSettings(s => ({ ...s, token_name: e.target.value }))}
              className="bg-zinc-800 border-zinc-700 text-white"
              placeholder="Reward Token"
            />
          </div>
          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 block">Symbol</label>
            <Input
              value={settings.symbol}
              onChange={(e) => setSettings(s => ({ ...s, symbol: e.target.value.toUpperCase().slice(0, 9) }))}
              className="bg-zinc-800 border-zinc-700 text-white uppercase"
              placeholder="RWT"
              maxLength={9}
            />
            <p className="text-zinc-500 text-xs mt-1">Max 9 characters (e.g., RWT, COLLECTOR)</p>
          </div>
        </div>

        {/* Contract Address */}
        <div>
          <label className="text-zinc-300 text-sm font-medium mb-2 block">Contract Address (SPL Token)</label>
          <Input
            value={settings.contract_address}
            onChange={(e) => setSettings(s => ({ ...s, contract_address: e.target.value }))}
            className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
            placeholder="Token mint address on Solana..."
          />
          <p className="text-zinc-500 text-xs mt-1">Leave empty if using internal token only (no on-chain withdrawal)</p>
        </div>

        {/* Token Icon */}
        <div>
          <label className="text-zinc-300 text-sm font-medium mb-2 block">Token Icon</label>
          <div className="flex items-start gap-4">
            {/* Preview */}
            <div className="w-20 h-20 bg-zinc-900 rounded-xl border-2 border-dashed border-zinc-700 flex items-center justify-center overflow-hidden">
              {settings.icon_url ? (
                <img src={settings.icon_url} alt="Token" className="w-full h-full object-cover" />
              ) : (
                <Coins className="w-8 h-8 text-zinc-600" />
              )}
            </div>
            
            {/* Upload Options */}
            <div className="flex-1 space-y-3">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleIconUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-zinc-300 border-zinc-700"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  Upload
                </Button>
                {settings.icon_url && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSettings(s => ({ ...s, icon_url: '' }))}
                    className="text-cyan-400 border-cyan-500/50 hover:bg-cyan-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <div>
                <Input
                  value={settings.icon_url}
                  onChange={(e) => setSettings(s => ({ ...s, icon_url: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                  placeholder="Or enter image URL..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Decimals & Min Redeem */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 block">Decimals</label>
            <Input
              type="number"
              value={settings.decimals}
              onChange={(e) => setSettings(s => ({ ...s, decimals: parseInt(e.target.value) || 0 }))}
              className="bg-zinc-800 border-zinc-700 text-white"
              min={0}
              max={18}
            />
            <p className="text-zinc-500 text-xs mt-1">For on-chain token (SPL default: 9)</p>
          </div>
          <div>
            <label className="text-zinc-300 text-sm font-medium mb-2 block">Min Redeem to Wallet</label>
            <Input
              type="number"
              value={settings.min_redeem_amount}
              onChange={(e) => setSettings(s => ({ ...s, min_redeem_amount: parseFloat(e.target.value) || 0 }))}
              className="bg-zinc-800 border-zinc-700 text-white"
              min={0}
            />
            <p className="text-zinc-500 text-xs mt-1">Minimum {settings.symbol} to withdraw to wallet</p>
          </div>
        </div>

        {/* Preview Card */}
        <div className="p-4 bg-zinc-900/50 rounded-lg">
          <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">Preview</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center overflow-hidden">
              {settings.icon_url ? (
                <img src={settings.icon_url} alt="Token" className="w-full h-full object-cover" />
              ) : (
                <Coins className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <p className="text-white font-bold">{settings.token_name || 'Reward Token'}</p>
              <p className="text-zinc-400 text-sm">${settings.symbol || 'RWT'}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-yellow-400 font-bold text-lg">1,250</p>
              <p className="text-zinc-500 text-xs">${settings.symbol}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Token Info Section - Withdrawal Disabled */}
      <div className="bg-zinc-800/50 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-zinc-600 to-zinc-700 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-zinc-400" />
          </div>
          <div>
            <h3 className="text-white font-bold">Token On-Chain Info</h3>
            <p className="text-zinc-500 text-sm">Token used for deposit bonus display only</p>
          </div>
        </div>
        
        <div className="p-4 bg-zinc-900/50 rounded-lg">
          <p className="text-zinc-400 text-sm">
            Token withdrawal feature is disabled. The token settings above are used for:
          </p>
          <ul className="text-zinc-500 text-sm mt-2 space-y-1 list-disc list-inside">
            <li>Displaying on-chain token balance in user profile</li>
            <li>Deposit bonus calculations (if enabled)</li>
            <li>Token symbol display throughout the app</li>
          </ul>
          <p className="text-cyan-400 text-xs mt-3">
            Users earn and use Tickets for shop redemptions. Tickets cannot be withdrawn to wallet.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold"
      >
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
        Save Token Settings
      </Button>
    </div>
  );
};

// ==================== S3 STORAGE SETTINGS TAB ====================
const StorageTab = ({ token }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    access_key: '',
    secret_key: '',
    region: 'us-east-1',
    endpoint_url: '',
    bucket_name: '',
    total_files: 0,
    has_credentials: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);
  
  const API = process.env.REACT_APP_BACKEND_URL;
  
  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/storage/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch storage settings:', err);
      toast.error('Failed to load storage settings');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSettings();
  }, [token]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/storage/settings`, {
        access_key: settings.access_key,
        secret_key: settings.secret_key,
        region: settings.region,
        endpoint_url: settings.endpoint_url,
        bucket_name: settings.bucket_name,
        enabled: settings.enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('S3 settings saved successfully');
      await fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/admin/storage/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };
  
  const handleToggleStorage = async () => {
    try {
      const endpoint = settings.enabled ? 'disable' : 'enable';
      await axios.post(`${API}/api/admin/storage/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(settings.enabled ? 'S3 storage disabled' : 'S3 storage enabled');
      await fetchSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle storage');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
          <HardDrive className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">S3 Storage Settings</h2>
          <p className="text-zinc-400 text-sm">Configure AWS S3 or S3-compatible storage (DigitalOcean Spaces, Cloudflare R2, MinIO)</p>
        </div>
      </div>
      
      {/* Status Bar */}
      <div className="flex items-center justify-between bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center gap-4">
          <div className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 ${
            settings.enabled && settings.has_credentials
              ? 'bg-green-500/20 text-green-400'
              : settings.has_credentials
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-zinc-700/50 text-zinc-400'
          }`}>
            {settings.enabled && settings.has_credentials ? (
              <><Cloud className="w-4 h-4" /> Active</>
            ) : settings.has_credentials ? (
              <><CloudOff className="w-4 h-4" /> Configured but Disabled</>
            ) : (
              <><CloudOff className="w-4 h-4" /> Not Configured</>
            )}
          </div>
          <span className="text-zinc-500 text-sm">{settings.total_files} files uploaded</span>
        </div>
        
        <Button
          onClick={handleToggleStorage}
          disabled={!settings.has_credentials}
          size="sm"
          className={settings.enabled 
            ? "bg-red-600 hover:bg-red-700" 
            : "bg-green-600 hover:bg-green-700"
          }
        >
          {settings.enabled ? 'Disable' : 'Enable'} S3
        </Button>
      </div>
      
      {/* Credentials Form */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-medium text-lg mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-amber-400" />
          S3 Credentials
        </h3>
        
        <div className="grid gap-4">
          {/* Access Key */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Access Key ID</label>
            <input
              type="text"
              value={settings.access_key}
              onChange={(e) => setSettings({ ...settings, access_key: e.target.value })}
              placeholder="AKIAIOSFODNN7EXAMPLE"
              className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              data-testid="s3-access-key"
            />
          </div>
          
          {/* Secret Key */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">Secret Access Key</label>
            <div className="relative">
              <input
                type={showSecretKey ? "text" : "password"}
                value={settings.secret_key}
                onChange={(e) => setSettings({ ...settings, secret_key: e.target.value })}
                placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 pr-12"
                data-testid="s3-secret-key"
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
              >
                {showSecretKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {/* Region & Endpoint Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Region - Text input with datalist for suggestions */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Region ID</label>
              <input
                type="text"
                list="region-suggestions"
                value={settings.region}
                onChange={(e) => setSettings({ ...settings, region: e.target.value })}
                placeholder="us-east-1, sgp1, or your custom region"
                className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                data-testid="s3-region"
              />
              <datalist id="region-suggestions">
                <option value="us-east-1">US East (N. Virginia)</option>
                <option value="us-east-2">US East (Ohio)</option>
                <option value="us-west-1">US West (N. California)</option>
                <option value="us-west-2">US West (Oregon)</option>
                <option value="eu-west-1">EU (Ireland)</option>
                <option value="eu-central-1">EU (Frankfurt)</option>
                <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                <option value="ap-southeast-3">Asia Pacific (Jakarta)</option>
                <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                <option value="sgp1">DigitalOcean Singapore</option>
                <option value="nyc3">DigitalOcean New York</option>
                <option value="auto">Cloudflare R2</option>
              </datalist>
              <p className="text-xs text-zinc-500 mt-1">
                Enter your S3 region ID (e.g., us-east-1, ap-southeast-3, or custom region for Biznet Gio, etc.)
              </p>
            </div>
            
            {/* Bucket Name */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-1.5">Bucket Name</label>
              <input
                type="text"
                value={settings.bucket_name}
                onChange={(e) => setSettings({ ...settings, bucket_name: e.target.value })}
                placeholder="my-bucket-name"
                className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                data-testid="s3-bucket-name"
              />
            </div>
          </div>
          
          {/* Custom Endpoint (for S3-compatible) */}
          <div>
            <label className="block text-sm font-medium text-zinc-300 mb-1.5">
              Custom Endpoint URL <span className="text-zinc-500">(Optional - for S3-compatible services)</span>
            </label>
            <input
              type="text"
              value={settings.endpoint_url}
              onChange={(e) => setSettings({ ...settings, endpoint_url: e.target.value })}
              placeholder="https://sgp1.digitaloceanspaces.com or https://<account-id>.r2.cloudflarestorage.com"
              className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              data-testid="s3-endpoint-url"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Leave empty for AWS S3. For DigitalOcean Spaces: https://[region].digitaloceanspaces.com
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-zinc-800">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Settings
          </Button>
          
          <Button
            onClick={handleTestConnection}
            disabled={testing || !settings.access_key || !settings.bucket_name}
            variant="outline"
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            {testing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Test Connection
          </Button>
        </div>
        
        {/* Test Result */}
        {testResult && (
          <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-2">
              {testResult.success ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                {testResult.message}
              </span>
            </div>
            {testResult.region && (
              <div className="mt-2 text-zinc-400 text-sm">
                Region: <span className="font-mono text-cyan-400">{testResult.region}</span>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-amber-400 font-medium mb-1">AWS S3</h4>
              <p className="text-zinc-400 text-sm">
                Use your AWS Access Key and Secret Key. Leave Endpoint URL empty. 
                Make sure your bucket has public-read ACL enabled for images.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-cyan-400 font-medium mb-1">S3-Compatible (Biznet Gio, DO Spaces, R2, MinIO)</h4>
              <p className="text-zinc-400 text-sm">
                Enter your custom region ID and endpoint URL. For Biznet Gio NEO Object Storage, 
                use your region and endpoint from the Biznet Gio portal.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== STAKING SETTINGS TAB ====================
const StakingTab = ({ token }) => {
  const [settings, setSettings] = useState({
    enabled: true,
    on_chain_enabled: false,
    contract_address: '',
    reward_token_symbol: 'TOKEN',
  });
  const [plans, setPlans] = useState({
    '7days': { duration: 7, apy: 5, min_amount: 100, name: '7 Days', enabled: true },
    '30days': { duration: 30, apy: 15, min_amount: 500, name: '30 Days', enabled: true },
    '90days': { duration: 90, apy: 30, min_amount: 1000, name: '90 Days', enabled: true },
  });
  const [stats, setStats] = useState({ total_staked: 0, total_rewards_paid: 0, active_stakes: 0 });
  const [stakes, setStakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeStakingTab, setActiveStakingTab] = useState('settings');
  
  // Cron job state
  const [cronStatus, setCronStatus] = useState({
    is_running: false,
    enabled: false,
    last_run: null,
    next_run: null,
    run_count: 0,
    interval: '1 hour',
    log_count: 0,
  });
  const [cronLoading, setCronLoading] = useState(false);
  const [showCronLogs, setShowCronLogs] = useState(false);
  const [cronLogs, setCronLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    fetchStakingData();
    fetchCronStatus();
    
    // Refresh cron status every 30 seconds
    const cronInterval = setInterval(fetchCronStatus, 30000);
    return () => clearInterval(cronInterval);
  }, []);

  const fetchCronStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/staking/cron-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronStatus(res.data);
    } catch (error) {
      console.error('Error fetching cron status:', error);
    }
  };

  const fetchCronLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await axios.get(`${API}/admin/staking/cron-logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCronLogs(res.data.logs || []);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleClearLogs = async () => {
    try {
      await axios.delete(`${API}/admin/staking/cron-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Logs cleared');
      fetchCronLogs();
    } catch (error) {
      toast.error('Failed to clear logs');
    }
  };

  const openCronLogs = () => {
    setShowCronLogs(true);
    fetchCronLogs();
  };

  const handleToggleCron = async () => {
    try {
      setCronLoading(true);
      const res = await axios.post(`${API}/admin/staking/cron-toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchCronStatus();
    } catch (error) {
      toast.error('Failed to toggle cron');
    } finally {
      setCronLoading(false);
    }
  };

  const handleRestartCron = async () => {
    try {
      setCronLoading(true);
      const res = await axios.post(`${API}/admin/staking/cron-restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchCronStatus();
    } catch (error) {
      toast.error('Failed to restart cron');
    } finally {
      setCronLoading(false);
    }
  };

  const fetchStakingData = async () => {
    try {
      setLoading(true);
      const [settingsRes, stakesRes] = await Promise.all([
        axios.get(`${API}/admin/staking/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/staking/stakes?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      if (settingsRes.data.settings) {
        setSettings(settingsRes.data.settings);
      }
      if (settingsRes.data.plans) {
        setPlans(settingsRes.data.plans);
      }
      if (settingsRes.data.stats) {
        setStats(settingsRes.data.stats);
      }
      setStakes(stakesRes.data || []);
    } catch (error) {
      console.error('Error fetching staking data:', error);
      toast.error('Failed to load staking settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/staking/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staking settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlans = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/staking/plans`, { plans }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staking plans saved');
    } catch (error) {
      toast.error('Failed to save plans');
    } finally {
      setSaving(false);
    }
  };

  const handleProcessRewards = async () => {
    try {
      const res = await axios.post(`${API}/admin/staking/process-rewards`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Processed ${res.data.processed_stakes} stakes`);
      fetchStakingData();
    } catch (error) {
      toast.error('Failed to process rewards');
    }
  };

  const updatePlan = (planId, field, value) => {
    setPlans(prev => ({
      ...prev,
      [planId]: { ...prev[planId], [field]: value }
    }));
  };

  const addNewPlan = () => {
    const newId = `plan_${Date.now()}`;
    setPlans(prev => ({
      ...prev,
      [newId]: {
        duration: 14,
        apy: 10,
        min_amount: 100,
        name: 'New Plan',
        enabled: true,
        // On-chain fields for smart contract integration
        pool_id: '',
        reward_rate: 0,
        lock_period: 14 * 24 * 60 * 60, // seconds
      }
    }));
  };

  const deletePlan = (planId) => {
    if (Object.keys(plans).length <= 1) {
      toast.error('Must have at least one staking plan');
      return;
    }
    setPlans(prev => {
      const newPlans = { ...prev };
      delete newPlans[planId];
      return newPlans;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white mb-2">Staking Settings</h2>
          <p className="text-zinc-400 text-sm">Configure staking plans and rewards</p>
        </div>
        <Button
          onClick={handleProcessRewards}
          variant="outline"
          className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Process Rewards
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4 text-cyan-400" />
            <span className="text-zinc-400 text-sm">Total Staked</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats.total_staked?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-green-400" />
            <span className="text-zinc-400 text-sm">Rewards Paid</span>
          </div>
          <p className="text-2xl font-bold text-green-400">{stats.total_rewards_paid?.toLocaleString() || 0}</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-zinc-400 text-sm">Active Stakes</span>
          </div>
          <p className="text-2xl font-bold text-purple-400">{stats.active_stakes || 0}</p>
        </div>
      </div>

      {/* Cron Job Status Panel */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Status Indicator */}
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                cronStatus.is_running && cronStatus.enabled 
                  ? 'bg-green-500 animate-pulse' 
                  : cronStatus.is_running && !cronStatus.enabled
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
              }`} />
              <span className="text-white font-medium">
                Reward Cron Job
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                cronStatus.is_running && cronStatus.enabled
                  ? 'bg-green-900/50 text-green-400'
                  : cronStatus.is_running && !cronStatus.enabled
                    ? 'bg-yellow-900/50 text-yellow-400'
                    : 'bg-red-900/50 text-red-400'
              }`}>
                {cronStatus.is_running ? (cronStatus.enabled ? 'RUNNING' : 'PAUSED') : 'STOPPED'}
              </span>
            </div>
            
            {/* Cron Info */}
            <div className="hidden md:flex items-center gap-4 text-sm text-zinc-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Interval: {cronStatus.interval}
              </span>
              <span>
                Runs: {cronStatus.run_count}
              </span>
              {cronStatus.last_run && (
                <span>
                  Last: {new Date(cronStatus.last_run).toLocaleTimeString('id-ID')}
                </span>
              )}
            </div>
          </div>
          
          {/* Control Buttons */}
          <div className="flex items-center gap-2">
            <Button
              onClick={handleToggleCron}
              disabled={cronLoading}
              size="sm"
              className={cronStatus.enabled 
                ? 'bg-yellow-600 hover:bg-yellow-700' 
                : 'bg-green-600 hover:bg-green-700'
              }
            >
              {cronLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : cronStatus.enabled ? (
                <>
                  <Square className="w-4 h-4 mr-1" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Resume
                </>
              )}
            </Button>
            <Button
              onClick={handleRestartCron}
              disabled={cronLoading}
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Restart
            </Button>
            <Button
              onClick={openCronLogs}
              size="sm"
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white"
            >
              <FileText className="w-4 h-4 mr-1" />
              Logs
              {cronStatus.log_count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-cyan-600 text-white text-xs rounded-full">
                  {cronStatus.log_count}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Cron Logs Dialog - CLI Style */}
      <Dialog open={showCronLogs} onOpenChange={setShowCronLogs}>
        <DialogContent className="bg-black border-zinc-800 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col p-0">
          {/* Terminal Header */}
          <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-zinc-400 text-sm font-mono ml-2">staking-cron.log</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchCronLogs}
                size="sm"
                variant="ghost"
                className="text-zinc-400 hover:text-white h-7 px-2"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={handleClearLogs}
                size="sm"
                variant="ghost"
                className="text-zinc-400 hover:text-red-400 h-7 px-2"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          
          {/* Terminal Body */}
          <div className="flex-1 overflow-y-auto p-4 font-mono text-sm bg-black">
            {logsLoading ? (
              <div className="text-green-400">Loading logs...</div>
            ) : cronLogs.length === 0 ? (
              <div className="text-zinc-500">$ No logs available</div>
            ) : (
              <div className="space-y-1">
                {cronLogs.map((log) => {
                  const timestamp = new Date(log.timestamp).toISOString().replace('T', ' ').substring(0, 19);
                  const levelColor = {
                    error: 'text-red-400',
                    warning: 'text-yellow-400',
                    success: 'text-green-400',
                    info: 'text-cyan-400',
                  }[log.level] || 'text-zinc-400';
                  
                  const levelPrefix = {
                    error: '[ERROR]',
                    warning: '[WARN] ',
                    success: '[OK]   ',
                    info: '[INFO] ',
                  }[log.level] || '[LOG]  ';
                  
                  // Format details as key=value pairs
                  const detailsStr = log.details && Object.keys(log.details).length > 0
                    ? ' ' + Object.entries(log.details).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' ')
                    : '';
                  
                  return (
                    <div key={log.id} className="flex">
                      <span className="text-zinc-600 select-none">{timestamp}</span>
                      <span className={`${levelColor} mx-2`}>{levelPrefix}</span>
                      <span className="text-zinc-300">{log.message}</span>
                      {detailsStr && <span className="text-zinc-500">{detailsStr}</span>}
                    </div>
                  );
                })}
                <div className="text-green-400 animate-pulse">█</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2">
        {['settings', 'plans', 'stakes'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveStakingTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeStakingTab === tab
                ? 'bg-cyan-600 text-white'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Settings Tab */}
      {activeStakingTab === 'settings' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-medium">Enable Staking</h3>
              <p className="text-zinc-500 text-sm">Allow users to stake tokens</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-medium">On-Chain Staking</h3>
              <p className="text-zinc-500 text-sm">Use Solana smart contract (requires contract address)</p>
            </div>
            <Switch
              checked={settings.on_chain_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, on_chain_enabled: checked })}
            />
          </div>

          {settings.on_chain_enabled && (
            <div>
              <label className="text-zinc-400 text-sm mb-2 block">Staking Contract Address</label>
              <Input
                value={settings.contract_address || ''}
                onChange={(e) => setSettings({ ...settings, contract_address: e.target.value })}
                placeholder="Enter Solana program address"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          )}

          <div>
            <label className="text-zinc-400 text-sm mb-2 block">Reward Token Symbol</label>
            <Input
              value={settings.reward_token_symbol || ''}
              onChange={(e) => setSettings({ ...settings, reward_token_symbol: e.target.value })}
              placeholder="TOKEN"
              className="bg-zinc-800 border-zinc-700 text-white max-w-xs"
            />
          </div>

          <Button onClick={handleSaveSettings} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      )}

      {/* Plans Tab */}
      {activeStakingTab === 'plans' && (
        <div className="space-y-4">
          {/* Add New Plan Button */}
          <div className="flex justify-between items-center">
            <p className="text-zinc-400 text-sm">
              Configure staking plans. For on-chain integration, set Pool ID and Reward Rate.
            </p>
            <Button onClick={addNewPlan} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Plan
            </Button>
          </div>

          {Object.entries(plans).map(([planId, plan]) => (
            <div key={planId} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              {/* Plan Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    plan.enabled !== false ? 'bg-cyan-600' : 'bg-zinc-700'
                  }`}>
                    <Gavel className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{plan.name || planId}</h3>
                    <p className="text-zinc-500 text-xs">ID: {planId}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={plan.enabled !== false}
                    onCheckedChange={(checked) => updatePlan(planId, 'enabled', checked)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deletePlan(planId)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Basic Settings */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Display Name</label>
                  <Input
                    value={plan.name || ''}
                    onChange={(e) => updatePlan(planId, 'name', e.target.value)}
                    placeholder="e.g., 7 Days"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Duration (days)</label>
                  <Input
                    type="number"
                    value={plan.duration}
                    onChange={(e) => updatePlan(planId, 'duration', parseInt(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">APY (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={plan.apy}
                    onChange={(e) => updatePlan(planId, 'apy', parseFloat(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Min Stake Amount</label>
                  <Input
                    type="number"
                    value={plan.min_amount}
                    onChange={(e) => updatePlan(planId, 'min_amount', parseFloat(e.target.value) || 0)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              {/* On-Chain Settings (collapsible) */}
              {settings.on_chain_enabled && (
                <div className="border-t border-zinc-700 pt-4 mt-4">
                  <p className="text-cyan-400 text-xs font-medium mb-3 flex items-center gap-1">
                    <Link className="w-3 h-3" />
                    On-Chain Configuration
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Pool ID (on-chain)</label>
                      <Input
                        value={plan.pool_id || ''}
                        onChange={(e) => updatePlan(planId, 'pool_id', e.target.value)}
                        placeholder="Solana pool account address"
                        className="bg-zinc-800 border-zinc-700 text-white font-mono text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Reward Rate (per second)</label>
                      <Input
                        type="number"
                        step="0.000001"
                        value={plan.reward_rate || 0}
                        onChange={(e) => updatePlan(planId, 'reward_rate', parseFloat(e.target.value) || 0)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs mb-1 block">Lock Period (seconds)</label>
                      <Input
                        type="number"
                        value={plan.lock_period || (plan.duration * 24 * 60 * 60)}
                        onChange={(e) => updatePlan(planId, 'lock_period', parseInt(e.target.value) || 0)}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Preview */}
              <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-400 text-xs">Preview:</p>
                <p className="text-white text-sm">
                  Stake minimum <span className="text-cyan-400 font-bold">{plan.min_amount}</span> tokens for{' '}
                  <span className="text-cyan-400 font-bold">{plan.duration} days</span> and earn{' '}
                  <span className="text-green-400 font-bold">{plan.apy}% APY</span>
                </p>
                <p className="text-zinc-500 text-xs mt-1">
                  Example: Stake 1000 tokens → Earn ~{((1000 * (plan.apy / 100) * (plan.duration / 365))).toFixed(2)} tokens after {plan.duration} days
                </p>
              </div>
            </div>
          ))}
          
          <div className="flex gap-3">
            <Button onClick={handleSavePlans} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Plans
            </Button>
            <Button onClick={addNewPlan} variant="outline" className="border-zinc-700 text-zinc-400 hover:text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Another Plan
            </Button>
          </div>
        </div>
      )}

      {/* Stakes Tab */}
      {activeStakingTab === 'stakes' && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">User</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Plan</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase p-4">Amount</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase p-4">APY</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase p-4">Accrued</th>
                <th className="text-center text-zinc-400 text-xs font-medium uppercase p-4">Status</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Unlock Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {stakes.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-zinc-500">No stakes found</td>
                </tr>
              ) : (
                stakes.map((stake) => (
                  <tr key={stake.id} className="hover:bg-zinc-800/30">
                    <td className="p-4">
                      <p className="text-white text-sm">{stake.user?.username || 'Unknown'}</p>
                      <p className="text-zinc-500 text-xs truncate max-w-[120px]">{stake.user?.wallet_address}</p>
                    </td>
                    <td className="p-4 text-white text-sm">{stake.plan_id}</td>
                    <td className="p-4 text-right text-white font-medium">{stake.amount?.toLocaleString()}</td>
                    <td className="p-4 text-right text-cyan-400">{stake.apy}%</td>
                    <td className="p-4 text-right text-green-400">+{stake.accrued_reward?.toFixed(4) || 0}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        stake.status === 'active' ? 'bg-green-900/50 text-green-400' :
                        stake.status === 'completed' ? 'bg-blue-900/50 text-blue-400' :
                        stake.status === 'ready' ? 'bg-yellow-900/50 text-yellow-400' :
                        'bg-zinc-700 text-zinc-400'
                      }`}>
                        {stake.status}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-400 text-sm">{formatDate(stake.unlock_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== NFT MINTING SETTINGS TAB ====================
const NFTSettingsTab = ({ token }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    is_active: false,
    collection_symbol: 'TC',
    pinata_jwt: '',
    pinata_gateway: 'gateway.pinata.cloud'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nfts, setNfts] = useState([]);
  const [loadingNfts, setLoadingNfts] = useState(false);
  const [selectedNft, setSelectedNft] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchNFTs();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/nft/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch NFT settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNFTs = async () => {
    setLoadingNfts(true);
    try {
      const res = await axios.get(`${API}/admin/nft/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNfts(res.data);
    } catch (err) {
      console.error('Failed to fetch NFTs:', err);
    } finally {
      setLoadingNfts(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/nft/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('NFT settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gem className="w-6 h-6 text-purple-500" />
            NFT Minting Settings
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Configure NFT minting for inventory items
          </p>
        </div>
      </div>

      {/* Settings Card */}
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-6 space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-medium">Enable NFT Minting</h3>
            <p className="text-zinc-500 text-sm">Allow users to mint their items as NFTs</p>
          </div>
          <button
            onClick={() => setSettings(s => ({ ...s, is_active: !s.is_active }))}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              settings.is_active ? 'bg-purple-600' : 'bg-zinc-700'
            }`}
          >
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
              settings.is_active ? 'left-8' : 'left-1'
            }`} />
          </button>
        </div>

        {/* Collection Symbol Prefix */}
        <div>
          <label className="text-zinc-300 text-sm font-medium mb-2 block">Collection Symbol Prefix</label>
          <Input
            value={settings.collection_symbol}
            onChange={(e) => setSettings(s => ({ ...s, collection_symbol: e.target.value.toUpperCase().slice(0, 2) }))}
            className="bg-zinc-800 border-zinc-700 text-white uppercase"
            maxLength={2}
            placeholder="TC"
          />
          <p className="text-zinc-500 text-xs mt-1">
            2 character prefix. User adds 4 more when minting (e.g., TC + ABCD = TCABCD)
          </p>
        </div>

        {/* Pinata Config */}
        <div className="pt-4 border-t border-zinc-800">
          <h4 className="text-white font-medium mb-4 flex items-center gap-2">
            <Database className="w-4 h-4 text-purple-400" />
            IPFS Storage (Pinata)
          </h4>
          
          <div className="space-y-4">
            <div>
              <label className="text-zinc-300 text-sm font-medium mb-2 block">Pinata JWT Token</label>
              <Input
                type="password"
                value={settings.pinata_jwt}
                onChange={(e) => setSettings(s => ({ ...s, pinata_jwt: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="eyJ..."
              />
              <p className="text-zinc-500 text-xs mt-1">
                Get your JWT from <a href="https://pinata.cloud" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">pinata.cloud</a>
              </p>
            </div>
            
            <div>
              <label className="text-zinc-300 text-sm font-medium mb-2 block">Pinata Gateway</label>
              <Input
                value={settings.pinata_gateway}
                onChange={(e) => setSettings(s => ({ ...s, pinata_gateway: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="gateway.pinata.cloud"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-purple-600 hover:bg-purple-700"
        >
          {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
          Save NFT Settings
        </Button>
      </div>

      {/* Minted NFTs List */}
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-medium flex items-center gap-2">
            <Gem className="w-5 h-5 text-purple-400" />
            Minted NFTs ({nfts.length})
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchNFTs}
            disabled={loadingNfts}
          >
            {loadingNfts ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {loadingNfts ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : nfts.length === 0 ? (
          <div className="text-center py-10 text-zinc-500">
            No NFTs minted yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-zinc-400 text-sm border-b border-zinc-800">
                  <th className="pb-3 font-medium">Item</th>
                  <th className="pb-3 font-medium">User</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {nfts.map((nft) => (
                  <tr key={nft.id} className="text-sm">
                    <td className="py-3">
                      <span className="text-white">{nft.item_name || 'Unknown'}</span>
                      {nft.rarity && (
                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                          nft.rarity === 'LEGEND' ? 'bg-yellow-500/20 text-yellow-400' :
                          nft.rarity === 'EPIC' ? 'bg-purple-500/20 text-purple-400' :
                          nft.rarity === 'RARE' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {t(`rarity.${nft.rarity}`)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 text-zinc-400">{nft.user_name || 'Unknown'}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        nft.status === 'minted' ? 'bg-green-500/20 text-green-400' :
                        nft.status === 'metadata_uploaded' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {nft.status}
                      </span>
                    </td>
                    <td className="py-3 text-zinc-400">
                      {new Date(nft.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedNft(nft)}
                        className="text-purple-400 border-purple-500/50 hover:bg-purple-500/20"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Detail
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* NFT Detail Dialog - Compact */}
      <Dialog open={!!selectedNft} onOpenChange={() => setSelectedNft(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden">
          {selectedNft && (
            <div className="space-y-3 overflow-hidden">
              {/* Header - Compact */}
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center">
                  <Gem className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="text-white text-sm font-bold truncate">{selectedNft.item_name || 'Unknown Item'}</h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-500 text-xs truncate max-w-[100px]">{selectedNft.user_name || 'Unknown'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 ${
                      selectedNft.rarity === 'LEGEND' ? 'bg-yellow-500/20 text-yellow-400' :
                      selectedNft.rarity === 'EPIC' ? 'bg-purple-500/20 text-purple-400' :
                      selectedNft.rarity === 'RARE' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>{selectedNft.rarity ? t(`rarity.${selectedNft.rarity}`) : 'N/A'}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 ${
                      selectedNft.status === 'minted' ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'
                    }`}>{selectedNft.status}</span>
                  </div>
                </div>
              </div>

              {/* Contract Address */}
              {selectedNft.mint_address && (
                <div className="p-2 bg-zinc-800/50 rounded-lg overflow-hidden">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Contract Address</p>
                  <p className="text-purple-300 text-xs font-mono break-all">{selectedNft.mint_address}</p>
                </div>
              )}

              {/* Tx Signature - Truncated */}
              {selectedNft.tx_signature && (
                <div className="p-2 bg-zinc-800/50 rounded-lg overflow-hidden">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Transaction</p>
                  <p className="text-purple-300 text-xs font-mono break-all">{selectedNft.tx_signature.slice(0, 40)}...</p>
                </div>
              )}

              {/* Info Row - Date & IPFS */}
              <div className="flex gap-2 text-xs">
                <div className="flex-1 p-2 bg-zinc-800/50 rounded-lg min-w-0">
                  <p className="text-zinc-500 text-[10px] uppercase">Created</p>
                  <p className="text-white truncate">{new Date(selectedNft.created_at).toLocaleDateString()}</p>
                </div>
                {selectedNft.metadata_cid && (
                  <div className="flex-1 p-2 bg-zinc-800/50 rounded-lg min-w-0 overflow-hidden">
                    <p className="text-zinc-500 text-[10px] uppercase">IPFS</p>
                    <p className="text-purple-300 font-mono truncate">{selectedNft.metadata_cid.slice(0, 10)}...</p>
                  </div>
                )}
              </div>

              {/* Action Links - Grid */}
              <div className="grid grid-cols-2 gap-2 pt-1">
                {selectedNft.mint_address && (
                  <>
                    <a
                      href={`https://solscan.io/token/${selectedNft.mint_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">Solscan</span>
                    </a>
                    <a
                      href={`https://solana.nftscan.com/${selectedNft.mint_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-pink-600 hover:bg-pink-700 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">NFTScan</span>
                    </a>
                    <a
                      href={`https://tensor.trade/item/${selectedNft.mint_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">Tensor</span>
                    </a>
                  </>
                )}
                {selectedNft.tx_signature && (
                  <a
                    href={`https://solscan.io/tx/${selectedNft.tx_signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-xs font-medium transition-colors"
                  >
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">View Tx</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== FOOTER TAB ====================
const FooterTab = ({ token }) => {
  const [settings, setSettings] = useState({
    brand_name: 'TCG Gacha',
    description: '',
    twitter_url: '',
    discord_url: '',
    telegram_url: '',
    instagram_url: '',
    facebook_url: '',
    email_url: '',
    copyright_text: '',
    info_links: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/footer`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data) setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch footer settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/footer`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Footer saved');
    } catch (err) {
      toast.error('Failed to save footer');
    } finally {
      setSaving(false);
    }
  };

  const addInfoLink = () => {
    setSettings({
      ...settings,
      info_links: [...(settings.info_links || []), { title: '', url: '' }]
    });
  };

  const updateInfoLink = (index, field, value) => {
    const newLinks = [...(settings.info_links || [])];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setSettings({ ...settings, info_links: newLinks });
  };

  const removeInfoLink = (index) => {
    const newLinks = [...(settings.info_links || [])];
    newLinks.splice(index, 1);
    setSettings({ ...settings, info_links: newLinks });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      {/* Description */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <label className="text-sm font-medium text-zinc-300">Site Description</label>
        <Textarea
          value={settings.description}
          onChange={(e) => setSettings({ ...settings, description: e.target.value })}
          placeholder="Brief description of your site"
          className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          rows={3}
        />
      </div>

      {/* Information Links - Dynamic */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Link className="w-4 h-4" />
            Information Links
          </h3>
          <Button
            onClick={addInfoLink}
            size="sm"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Link
          </Button>
        </div>
        
        {(settings.info_links || []).length === 0 ? (
          <p className="text-zinc-500 text-sm">No information links added yet. Click "Add Link" to create one.</p>
        ) : (
          <div className="space-y-3">
            {(settings.info_links || []).map((link, index) => (
              <div key={index} className="flex gap-2 items-start p-3 bg-zinc-800/50 rounded-lg">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    value={link.title || ''}
                    onChange={(e) => updateInfoLink(index, 'title', e.target.value)}
                    placeholder="Link Title (e.g., Blog, Terms)"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Input
                    value={link.url || ''}
                    onChange={(e) => updateInfoLink(index, 'url', e.target.value)}
                    placeholder="URL (e.g., https://...)"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <Button
                  onClick={() => removeInfoLink(index)}
                  size="sm"
                  variant="ghost"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Social Media Links */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Social Media Links
        </h3>
        <p className="text-zinc-500 text-xs">Leave empty to hide the icon in footer</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Twitter/X */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Twitter / X
            </label>
            <Input
              value={settings.twitter_url || ''}
              onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
              placeholder="https://x.com/..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Discord */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
              Discord
            </label>
            <Input
              value={settings.discord_url || ''}
              onChange={(e) => setSettings({ ...settings, discord_url: e.target.value })}
              placeholder="https://discord.gg/..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Telegram */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Send className="w-4 h-4" />
              Telegram
            </label>
            <Input
              value={settings.telegram_url || ''}
              onChange={(e) => setSettings({ ...settings, telegram_url: e.target.value })}
              placeholder="https://t.me/..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Instagram */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Instagram className="w-4 h-4" />
              Instagram
            </label>
            <Input
              value={settings.instagram_url || ''}
              onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
              placeholder="https://instagram.com/..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Facebook */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Facebook className="w-4 h-4" />
              Facebook
            </label>
            <Input
              value={settings.facebook_url || ''}
              onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
              placeholder="https://facebook.com/..."
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          
          {/* Email */}
          <div>
            <label className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Email
            </label>
            <Input
              value={settings.email_url || ''}
              onChange={(e) => setSettings({ ...settings, email_url: e.target.value })}
              placeholder="support@example.com"
              className="mt-1 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <label className="text-sm font-medium text-zinc-300">Copyright Text</label>
        <Input
          value={settings.copyright_text || ''}
          onChange={(e) => setSettings({ ...settings, copyright_text: e.target.value })}
          placeholder="© 2025 Your Company"
          className="mt-2 bg-zinc-800 border-zinc-700 text-white"
        />
      </div>

      {/* Save Button */}
      <Button onClick={handleSave} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700 h-12">
        {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
        Save Footer Settings
      </Button>
    </div>
  );
};

// ==================== ADMINS TAB ====================
const AdminsTab = ({ token }) => {
  const [admins, setAdmins] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState('new'); // 'new' or 'promote'
  const [selectedUserId, setSelectedUserId] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [removeAdmin, setRemoveAdmin] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => { fetchAdmins(); fetchAllUsers(); }, []);

  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/admins`, { headers: { Authorization: `Bearer ${token}` } });
      setAdmins(res.data);
    } catch (err) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data.filter(u => !u.is_admin));
    } catch (err) {
      console.error('Failed to load users');
    }
  };

  const handleAddAdmin = async () => {
    if (addMode === 'new') {
      if (!formData.name || !formData.email || !formData.password) {
        toast.error('All fields are required');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    } else {
      if (!selectedUserId) {
        toast.error('Please select a user to promote');
        return;
      }
    }

    setSaving(true);
    try {
      if (addMode === 'new') {
        await axios.post(`${API}/admin/admins`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('New admin created successfully');
      } else {
        const selectedUser = allUsers.find(u => u.id === selectedUserId);
        await axios.post(`${API}/admin/admins`, { 
          name: selectedUser.name, 
          email: selectedUser.email, 
          password: 'dummy' // Not used for promotion
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('User promoted to admin');
      }
      fetchAdmins();
      fetchAllUsers();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add admin');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = async () => {
    try {
      await axios.delete(`${API}/admin/admins/${removeAdmin.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Admin removed');
      fetchAdmins();
      fetchAllUsers();
      setRemoveAdmin(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const resetForm = () => {
    setShowAddForm(false);
    setAddMode('new');
    setSelectedUserId('');
    setFormData({ name: '', email: '', password: '' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm">{admins.length} admin users</p>
        <Button onClick={() => setShowAddForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Add Admin
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : admins.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No admins found</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">User</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Email</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Joined</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id} className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-orange-500 flex items-center justify-center text-white font-bold">
                        {admin.name?.charAt(0)?.toUpperCase() || 'A'}
                      </div>
                      <div>
                        <span className="text-white font-medium">{admin.name}</span>
                        {admin.id === currentUser?.id && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-500/20 text-blue-400 rounded">YOU</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-zinc-400">{admin.email}</td>
                  <td className="p-4 text-zinc-500 text-sm">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-right">
                    {admin.id !== currentUser?.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemoveAdmin(admin)}
                        className="text-zinc-400 hover:text-cyan-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={showAddForm} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Mode Selector */}
            <div className="flex gap-2 p-1 bg-zinc-800 rounded-lg">
              <button
                onClick={() => setAddMode('new')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  addMode === 'new' ? 'bg-cyan-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Create New Admin
              </button>
              <button
                onClick={() => setAddMode('promote')}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  addMode === 'promote' ? 'bg-cyan-600 text-white' : 'text-zinc-400 hover:text-white'
                }`}
              >
                Promote User
              </button>
            </div>

            {addMode === 'new' ? (
              <>
                <div>
                  <label className="text-sm font-medium text-zinc-300">Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Admin name"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-300">Email</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="admin@example.com"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-300">Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 6 characters"
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="text-sm font-medium text-zinc-300">Select User to Promote</label>
                {allUsers.length === 0 ? (
                  <div className="mt-2 p-4 bg-zinc-800 rounded-lg text-center">
                    <p className="text-zinc-500 text-sm">No non-admin users available</p>
                  </div>
                ) : (
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select a user..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {allUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex items-center gap-2">
                            <span>{user.name}</span>
                            <span className="text-zinc-500 text-xs">({user.email})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-500 text-xs">
                {addMode === 'new' 
                  ? 'Create a new user account with admin privileges.' 
                  : 'Promote an existing user to admin. They will have full admin access.'}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button 
              onClick={handleAddAdmin} 
              disabled={saving || (addMode === 'promote' && allUsers.length === 0)} 
              className="flex-1 bg-cyan-600 hover:bg-cyan-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {addMode === 'new' ? 'Create Admin' : 'Promote to Admin'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Admin Confirmation */}
      <Dialog open={!!removeAdmin} onOpenChange={() => setRemoveAdmin(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Remove Admin</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 py-4">
            Are you sure you want to remove admin privileges from <span className="text-white font-medium">{removeAdmin?.name}</span>? They will become a regular user.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setRemoveAdmin(null)} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleRemoveAdmin} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              Remove Admin
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== USERS TAB ====================
const UsersTab = ({ token }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNote, setTopUpNote] = useState('');
  const [topUpLoading, setTopUpLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  
  // Send Ticket states
  const [showSendTicket, setShowSendTicket] = useState(false);
  const [sendTicketAmount, setSendTicketAmount] = useState('');
  const [sendTicketNote, setSendTicketNote] = useState('');
  const [sendTicketLoading, setSendTicketLoading] = useState(false);
  
  // Inventory detail states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetail, setShowItemDetail] = useState(false);
  
  // Toggle Status states
  const [showToggleStatus, setShowToggleStatus] = useState(false);
  const [toggleStatusNote, setToggleStatusNote] = useState('');
  const [toggleStatusLoading, setToggleStatusLoading] = useState(false);

  // Level frame styles for avatar
  const getLevelFrameStyle = (levelInfo) => {
    if (!levelInfo) return { frame: '', glow: '' };
    
    const styles = {
      BRONZE: {
        frame: 'ring-2 ring-amber-600/70',
        glow: '',
        innerBg: 'bg-gradient-to-br from-amber-700 to-amber-900'
      },
      SILVER: {
        frame: 'ring-2 ring-slate-400/70',
        glow: 'shadow-[0_0_10px_rgba(192,192,192,0.3)]',
        innerBg: 'bg-gradient-to-br from-slate-400 to-slate-600'
      },
      GOLD: {
        frame: 'ring-[3px] ring-yellow-500/80',
        glow: 'shadow-[0_0_15px_rgba(255,215,0,0.4)]',
        innerBg: 'bg-gradient-to-br from-yellow-500 to-amber-600'
      },
      PLATINUM: {
        frame: 'ring-[3px] ring-slate-200/80',
        glow: 'shadow-[0_0_15px_rgba(229,228,226,0.5)]',
        innerBg: 'bg-gradient-to-br from-slate-200 to-slate-400'
      },
      VIP: {
        frame: 'ring-[4px] ring-purple-500/90 animate-pulse',
        glow: 'shadow-[0_0_20px_rgba(139,92,246,0.6)]',
        innerBg: 'bg-gradient-to-br from-purple-500 via-pink-500 to-violet-600'
      }
    };
    
    return styles[levelInfo.level] || styles.BRONZE;
  };

  useEffect(() => { 
    fetchUsers(); 
    fetchTokenSettings();
  }, []);

  const fetchTokenSettings = async () => {
    try {
      const res = await axios.get(`${API}/token/settings`);
      setTokenSettings(res.data);
    } catch (err) {
      console.error('Failed to load token settings');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      setUserDetail(res.data);
    } catch (err) {
      toast.error('Failed to load user details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setActiveDetailTab('overview');
    fetchUserDetail(user.id);
  };

  const handleTopUp = async () => {
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setTopUpLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${selectedUser.id}/topup`,
        { amount: parseFloat(topUpAmount), note: topUpNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Added ${topUpAmount} coins to ${selectedUser.name}`);
      setShowTopUp(false);
      setTopUpAmount('');
      setTopUpNote('');
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to top up');
    } finally {
      setTopUpLoading(false);
    }
  };

  const handleStakeAction = async (stakeId, action) => {
    const confirmMsg = action === 'cancel' 
      ? 'Cancel this stake? User will get back their staked tokens without rewards.'
      : 'Refund this stake? User will get back their staked tokens + accrued rewards.';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.post(
        `${API}/admin/staking/user-stake/${stakeId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Stake ${action}led successfully`);
      fetchUserDetail(selectedUser.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} stake`);
    }
  };

  const handleSendTicket = async () => {
    if (!sendTicketAmount || parseFloat(sendTicketAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSendTicketLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/send-token`,
        { amount: parseFloat(sendTicketAmount), note: sendTicketNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Sent ${sendTicketAmount} Tickets to ${selectedUser.name}`);
      setShowSendTicket(false);
      setSendTicketAmount('');
      setSendTicketNote('');
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send tickets');
    } finally {
      setSendTicketLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    const isCurrentlyActive = userDetail?.user?.is_active !== false;
    const newStatus = !isCurrentlyActive;
    
    if (!newStatus && !toggleStatusNote.trim()) {
      toast.error('Please provide a reason for deactivating the account');
      return;
    }
    
    setToggleStatusLoading(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.id}/toggle-status`,
        { is_active: newStatus, note: toggleStatusNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Account ${newStatus ? 'activated' : 'deactivated'} successfully`);
      setShowToggleStatus(false);
      setToggleStatusNote('');
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account status');
    } finally {
      setToggleStatusLoading(false);
    }
  };

  const openItemDetail = (item) => {
    setSelectedItem(item);
    setShowItemDetail(true);
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.wallet_address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // User List View
  if (!selectedUser) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-zinc-400 text-sm">{users.length} registered users</p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search users..."
              className="pl-9 w-64 bg-zinc-900 border-zinc-800 text-white"
            />
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
            <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-500">No users found</p>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">User</th>
                  <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Balance</th>
                  <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Inventory</th>
                  <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Total Spent</th>
                  <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Joined</th>
                  <th className="text-right text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr 
                    key={user.id} 
                    className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50 cursor-pointer"
                    onClick={() => handleSelectUser(user)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {/* Avatar with Level Frame */}
                        <div className={`relative ${getLevelFrameStyle(user.level_info).glow}`}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${getLevelFrameStyle(user.level_info).frame} ${
                            user.is_admin 
                              ? 'bg-gradient-to-br from-cyan-500 to-orange-500' 
                              : user.is_active === false 
                                ? 'bg-gradient-to-br from-red-500 to-red-700' 
                                : getLevelFrameStyle(user.level_info).innerBg || 'bg-gradient-to-br from-blue-500 to-purple-500'
                          }`}>
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          {/* VIP Crown indicator */}
                          {user.level_info?.level === 'VIP' && (
                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                              <Crown className="w-2.5 h-2.5 text-yellow-900" />
                            </div>
                          )}
                          {/* Platinum/Gold star indicator */}
                          {(user.level_info?.level === 'PLATINUM' || user.level_info?.level === 'GOLD') && (
                            <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center shadow-lg ${
                              user.level_info.level === 'PLATINUM' ? 'bg-slate-200' : 'bg-yellow-500'
                            }`}>
                              <Star className={`w-2.5 h-2.5 ${user.level_info.level === 'PLATINUM' ? 'text-slate-700' : 'text-yellow-900'}`} />
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{user.name}</span>
                            {user.is_admin && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-cyan-500/20 text-cyan-400 rounded">ADMIN</span>
                            )}
                            {user.is_active === false && (
                              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-red-500/20 text-red-400 rounded">DEACTIVATED</span>
                            )}
                          </div>
                          <span className="text-zinc-500 text-sm font-mono truncate max-w-[180px] block">
                            {user.wallet_address 
                              ? `${user.wallet_address.slice(0, 4)}...${user.wallet_address.slice(-4)}`
                              : user.email || '-'
                            }
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-yellow-400">
                          <Coins className="w-4 h-4" />
                          <span className="font-semibold">{user.coins?.toLocaleString() || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-violet-400 text-sm">
                          <Ticket className="w-3 h-3" />
                          <span>{user.tickets || 0} Tickets</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-zinc-400">
                      {user.inventory_count || 0} items
                    </td>
                    <td className="p-4">
                      <span className="text-green-400 font-medium">{user.total_spent?.toLocaleString() || 0}</span>
                      <span className="text-zinc-500 text-sm"> coins</span>
                    </td>
                    <td className="p-4 text-zinc-500 text-sm">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-zinc-400 hover:text-white"
                        onClick={(e) => { e.stopPropagation(); handleSelectUser(user); }}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // User Detail View
  return (
    <div className="space-y-6">
      {/* Back Button & Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => { setSelectedUser(null); setUserDetail(null); }}
            className="text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
          </Button>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${selectedUser.is_admin ? 'bg-gradient-to-br from-cyan-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'}`}>
              {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-white font-bold text-lg">{selectedUser.name}</h2>
                {selectedUser.is_admin && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-cyan-500/20 text-cyan-400 rounded">ADMIN</span>
                )}
                {userDetail?.user?.is_active === false && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-red-500/20 text-red-400 rounded">DEACTIVATED</span>
                )}
                {userDetail?.level_info && (
                  <LevelBadge levelInfo={userDetail.level_info} />
                )}
              </div>
              <p className="text-zinc-500 text-sm">{selectedUser.email}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!selectedUser.is_admin && (
            <Button 
              onClick={() => setShowToggleStatus(true)} 
              className={userDetail?.user?.is_active === false ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {userDetail?.user?.is_active === false ? (
                <><UserCheck className="w-4 h-4 mr-2" /> Activate</>
              ) : (
                <><UserX className="w-4 h-4 mr-2" /> Deactivate</>
              )}
            </Button>
          )}
          <Button onClick={() => setShowSendTicket(true)} className="bg-violet-600 hover:bg-violet-700">
            <Ticket className="w-4 h-4 mr-2" /> Send Tickets
          </Button>
          <Button onClick={() => setShowTopUp(true)} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> Top Up Coins
          </Button>
        </div>
      </div>

      {loadingDetail ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : userDetail ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { label: 'Coins', value: userDetail.user?.coins?.toLocaleString() || 0, icon: Coins, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
              { label: 'Tickets', value: userDetail.user?.tickets || 0, icon: Ticket, color: 'text-violet-400', bg: 'bg-violet-500/10' },
              { label: 'Total Pulls', value: userDetail.stats?.total_pulls || 0, icon: Box, color: 'text-blue-400', bg: 'bg-blue-500/10' },
              { label: 'Win Rate', value: `${userDetail.stats?.win_rate || 0}%`, icon: Target, color: 'text-green-400', bg: 'bg-green-500/10' },
              { label: 'Total Spent', value: userDetail.stats?.total_spent?.toLocaleString() || 0, icon: DollarSign, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
              { label: 'Inventory', value: userDetail.stats?.inventory_count || 0, icon: Package, color: 'text-purple-400', bg: 'bg-purple-500/10' },
            ].map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className={`${stat.bg} border border-zinc-800 rounded-xl p-4`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${stat.color}`} />
                    <span className="text-zinc-400 text-xs">{stat.label}</span>
                  </div>
                  <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
              );
            })}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
            {['overview', 'transactions', 'redeem', 'inventory', 'staking', 'logs'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveDetailTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  activeDetailTab === tab 
                    ? 'bg-cyan-600 text-white' 
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            {activeDetailTab === 'overview' && (
              <div className="p-6 space-y-6">
                {/* User Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Wallet Info */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Wallet className="w-5 h-5 text-cyan-400" />
                      <h4 className="text-zinc-300 font-medium">Wallet</h4>
                    </div>
                    {userDetail.user?.wallet_address ? (
                      <div className="space-y-2">
                        <p className="text-white font-mono text-sm break-all">{userDetail.user.wallet_address}</p>
                        <a 
                          href={`https://solscan.io/account/${userDetail.user.wallet_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:underline text-xs flex items-center gap-1"
                        >
                          View on Solscan <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-sm">No wallet connected</p>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="bg-zinc-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-5 h-5 text-purple-400" />
                      <h4 className="text-zinc-300 font-medium">Contact</h4>
                    </div>
                    <div className="space-y-2">
                      {userDetail.user?.email ? (
                        <p className="text-white text-sm flex items-center gap-2">
                          <Mail className="w-4 h-4 text-zinc-500" />
                          {userDetail.user.email}
                        </p>
                      ) : (
                        <p className="text-zinc-500 text-sm">No email set</p>
                      )}
                      {userDetail.user?.country && (
                        <p className="text-white text-sm flex items-center gap-2">
                          <Globe className="w-4 h-4 text-zinc-500" />
                          {userDetail.user.country}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Activity Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-xs mb-1">Total Deposits</p>
                    <p className="text-green-400 text-xl font-bold">{userDetail.stats?.total_deposits?.toLocaleString() || 0}</p>
                    <p className="text-zinc-500 text-xs">coins</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-xs mb-1">Total Spent</p>
                    <p className="text-yellow-400 text-xl font-bold">{userDetail.stats?.total_spent?.toLocaleString() || 0}</p>
                    <p className="text-zinc-500 text-xs">coins</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-xs mb-1">Items Sold</p>
                    <p className="text-blue-400 text-xl font-bold">{userDetail.stats?.total_sold?.toLocaleString() || 0}</p>
                    <p className="text-zinc-500 text-xs">coins</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                    <p className="text-zinc-400 text-xs mb-1">Win Rate</p>
                    <p className="text-purple-400 text-xl font-bold">{userDetail.stats?.win_rate || 0}%</p>
                    <p className="text-zinc-500 text-xs">LEGEND+</p>
                  </div>
                </div>

                {/* Account Info */}
                <div className="bg-zinc-800/50 rounded-xl p-4">
                  <h4 className="text-zinc-300 font-medium mb-3">Account Info</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-zinc-500">Joined</p>
                      <p className="text-white">{formatDate(userDetail.user?.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Total Pulls</p>
                      <p className="text-white">{userDetail.stats?.total_pulls || 0}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Inventory Items</p>
                      <p className="text-white">{userDetail.stats?.inventory_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-zinc-500">Stakes</p>
                      <p className="text-white">{userDetail.stats?.stakes_count || 0}</p>
                    </div>
                  </div>
                </div>

                {/* User Level Card */}
                {userDetail?.level_info && (
                  <AdminLevelCard 
                    levelInfo={userDetail.level_info} 
                    tokenSymbol="Tickets" 
                  />
                )}
              </div>
            )}

            {activeDetailTab === 'transactions' && (
              <div className="max-h-[500px] overflow-y-auto">
                {userDetail.transactions?.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">No transactions yet</div>
                ) : (
                  <table className="w-full">
                    <thead className="sticky top-0 bg-zinc-900">
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Type</th>
                        <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Amount</th>
                        <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Details</th>
                        <th className="text-left text-zinc-400 text-xs font-medium uppercase p-4">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userDetail.transactions?.map((tx) => (
                        <tr key={tx.id} className="border-b border-zinc-800 last:border-b-0">
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              tx.type === 'deposit' || tx.type === 'deposit_sol' || tx.type === 'admin_topup' ? 'bg-green-500/20 text-green-400' :
                              tx.type === 'gacha' ? 'bg-purple-500/20 text-purple-400' :
                              tx.type === 'sell' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {tx.type?.toUpperCase()}
                            </span>
                          </td>
                          <td className="p-4">
                            <span className={tx.amount >= 0 ? 'text-green-400' : 'text-cyan-400'}>
                              {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString()}
                            </span>
                          </td>
                          <td className="p-4 text-zinc-400 text-sm">
                            {tx.pool_name || tx.item_name || tx.note || '-'}
                          </td>
                          <td className="p-4 text-zinc-500 text-sm">{formatDate(tx.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeDetailTab === 'inventory' && (
              <div className="max-h-[500px] overflow-y-auto">
                {userDetail.inventory?.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">No items in inventory</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
                    {userDetail.inventory?.map((item, index) => (
                      <div 
                        key={item.id} 
                        data-testid={`inventory-item-${index}`}
                        onClick={() => openItemDetail(item)}
                        className={`rounded-lg p-3 text-center cursor-pointer transition-all hover:scale-105 hover:shadow-lg ${
                          item.nft_mint 
                            ? 'bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30 hover:border-purple-400' 
                            : 'bg-zinc-800 hover:bg-zinc-700'
                        }`}
                      >
                        {item.nft_mint && (
                          <div className="flex items-center justify-center gap-1 mb-2">
                            <Gem className="w-3 h-3 text-purple-400" />
                            <span className="text-purple-400 text-[10px] font-bold">NFT</span>
                          </div>
                        )}
                        <img src={item.item_image} alt={item.item_name} className="w-full h-20 object-cover rounded mb-2" />
                        <p className="text-white text-xs font-medium truncate">{item.item_name}</p>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                          item.item_rarity === 'THE_ONE_ONLY' ? 'bg-pink-500/20 text-pink-400' :
                          item.item_rarity === 'LEGEND' ? 'bg-yellow-500/20 text-yellow-400' :
                          item.item_rarity === 'EPIC' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>{t(`rarity.${item.item_rarity}`)}</span>
                        <p className={`text-[10px] mt-1 ${
                          item.status === 'kept' ? 'text-green-400' :
                          item.status === 'sold' ? 'text-zinc-500' :
                          'text-orange-400'
                        }`}>{item.status}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeDetailTab === 'redeem' && (
              <div className="max-h-[500px] overflow-y-auto p-4 space-y-4">
                {/* Redemptions Section */}
                <div>
                  <h4 className="text-zinc-400 text-sm font-medium mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Product Redemptions
                  </h4>
                  {userDetail.shop_purchases?.length === 0 ? (
                    <div className="text-center py-6 text-zinc-500 bg-zinc-800/50 rounded-lg">No redemptions yet</div>
                  ) : (
                    <div className="space-y-2">
                      {userDetail.shop_purchases?.map((purchase) => (
                        <div key={purchase.id} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <img src={purchase.product_image} alt="" className="w-10 h-10 rounded object-cover" />
                            <div>
                              <p className="text-white text-sm">{purchase.product_name}</p>
                              <p className="text-zinc-500 text-xs">{formatDate(purchase.created_at)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-purple-400 font-medium">{purchase.ticket_spent} tokens</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              purchase.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              purchase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              purchase.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>{purchase.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeDetailTab === 'staking' && (
              <div className="max-h-[500px] overflow-y-auto p-4">
                {userDetail.stakes?.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">No staking activity</div>
                ) : (
                  <div className="space-y-3">
                    {userDetail.stakes?.map((stake) => {
                      const isActive = stake.status === 'active';
                      const canCancel = isActive && new Date() < new Date(stake.unlock_date);
                      return (
                        <div key={stake.id} className={`rounded-lg p-4 border ${
                          isActive ? 'bg-cyan-900/20 border-cyan-800/50' : 'bg-zinc-800/50 border-zinc-700'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isActive ? 'bg-cyan-600' : 'bg-zinc-700'
                              }`}>
                                <Gavel className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{stake.amount} tokens</p>
                                <p className="text-zinc-400 text-xs">{stake.plan_name || stake.plan_id} @ {stake.apy}% APY</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                stake.status === 'active' ? 'bg-cyan-500/20 text-cyan-400' :
                                stake.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                stake.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                'bg-yellow-500/20 text-yellow-400'
                              }`}>{stake.status}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-zinc-500 text-xs">Started</p>
                              <p className="text-zinc-300">{formatDate(stake.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-zinc-500 text-xs">Unlock Date</p>
                              <p className="text-zinc-300">{formatDate(stake.unlock_date)}</p>
                            </div>
                            <div>
                              <p className="text-zinc-500 text-xs">Est. Reward</p>
                              <p className="text-green-400">{stake.estimated_reward || ((stake.amount * stake.apy / 100 * stake.duration_days / 365).toFixed(2))}</p>
                            </div>
                          </div>

                          {isActive && (
                            <div className="flex gap-2 pt-3 border-t border-zinc-700">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-yellow-400 border-yellow-600 hover:bg-yellow-900/30"
                                onClick={() => handleStakeAction(stake.id, 'cancel')}
                              >
                                Cancel Stake
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400 border-red-600 hover:bg-red-900/30"
                                onClick={() => handleStakeAction(stake.id, 'refund')}
                              >
                                Refund
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeDetailTab === 'logs' && (
              <div className="bg-black font-mono text-sm">
                <div className="sticky top-0 bg-zinc-900 px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
                  <span className="text-zinc-400">user-activity.log</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white h-6"
                    onClick={() => fetchUserDetail(selectedUser.id)}
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
                <div className="max-h-[450px] overflow-y-auto p-4">
                  {userDetail.activity_logs?.length === 0 ? (
                    <div className="text-zinc-500">$ No activity logs available</div>
                  ) : (
                    <div className="space-y-1">
                      {userDetail.activity_logs?.map((log, idx) => {
                        const timestamp = new Date(log.timestamp || log.created_at).toISOString().replace('T', ' ').substring(0, 19);
                        const levelColor = {
                          error: 'text-red-400',
                          warning: 'text-yellow-400',
                          success: 'text-green-400',
                          info: 'text-cyan-400',
                        }[log.level] || 'text-zinc-400';
                        
                        return (
                          <div key={log.id || idx} className="flex">
                            <span className="text-zinc-600 select-none mr-2">{timestamp}</span>
                            <span className={`${levelColor} mr-2`}>[{(log.level || log.type || 'LOG').toUpperCase().padEnd(5)}]</span>
                            <span className="text-zinc-300">{log.message || log.action}</span>
                            {log.details && <span className="text-zinc-500 ml-2">{JSON.stringify(log.details)}</span>}
                          </div>
                        );
                      })}
                      <div className="text-green-400 animate-pulse">█</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : null}

      {/* Top Up Dialog */}
      <Dialog open={showTopUp} onOpenChange={setShowTopUp}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Top Up Coins for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">Current Balance</label>
              <p className="text-2xl font-bold text-yellow-400 mt-1">{userDetail?.user?.coins?.toLocaleString() || 0} coins</p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Amount to Add</label>
              <Input
                type="number"
                value={topUpAmount}
                onChange={(e) => setTopUpAmount(e.target.value)}
                placeholder="Enter amount..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Note (optional)</label>
              <Input
                value={topUpNote}
                onChange={(e) => setTopUpNote(e.target.value)}
                placeholder="Reason for top up..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            {topUpAmount && parseFloat(topUpAmount) > 0 && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 text-sm">
                  New balance will be: <span className="font-bold">{((userDetail?.user?.coins || 0) + parseFloat(topUpAmount)).toLocaleString()}</span> coins
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowTopUp(false)} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleTopUp} disabled={topUpLoading} className="flex-1 bg-green-600 hover:bg-green-700">
              {topUpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Coins
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Ticket Dialog */}
      <Dialog open={showSendTicket} onOpenChange={setShowSendTicket}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ticket className="w-5 h-5 text-violet-400" />
              Send Tickets to {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">Current Ticket Balance</label>
              <p className="text-2xl font-bold text-violet-400 mt-1">{userDetail?.user?.tickets?.toLocaleString() || 0} Tickets</p>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Amount to Send</label>
              <Input
                type="number"
                value={sendTicketAmount}
                onChange={(e) => setSendTicketAmount(e.target.value)}
                placeholder="Enter ticket amount..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Note (optional)</label>
              <Input
                value={sendTicketNote}
                onChange={(e) => setSendTicketNote(e.target.value)}
                placeholder="Reason for sending tickets..."
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            {sendTicketAmount && parseFloat(sendTicketAmount) > 0 && (
              <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
                <p className="text-violet-400 text-sm">
                  New balance will be: <span className="font-bold">{((userDetail?.user?.tickets || 0) + parseFloat(sendTicketAmount)).toLocaleString()}</span> Tickets
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowSendTicket(false)} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSendTicket} disabled={sendTicketLoading} className="flex-1 bg-violet-600 hover:bg-violet-700">
              {sendTicketLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ticket className="w-4 h-4 mr-2" />}
              Send Tickets
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inventory Item Detail Dialog */}
      <Dialog open={showItemDetail} onOpenChange={setShowItemDetail}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedItem?.nft_mint && <Gem className="w-5 h-5 text-purple-400" />}
              Item Detail
            </DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4 py-4">
              {/* Item Image */}
              <div className="flex justify-center">
                <div className={`relative rounded-xl overflow-hidden ${selectedItem.nft_mint ? 'ring-2 ring-purple-500' : ''}`}>
                  <img 
                    src={selectedItem.item_image} 
                    alt={selectedItem.item_name} 
                    className="w-48 h-48 object-cover"
                  />
                  {selectedItem.nft_mint && (
                    <div className="absolute top-2 right-2 px-2 py-1 bg-purple-600/90 rounded text-xs font-bold text-white flex items-center gap-1">
                      <Gem className="w-3 h-3" /> NFT
                    </div>
                  )}
                </div>
              </div>

              {/* Item Info */}
              <div className="text-center">
                <h3 className="text-xl font-bold text-white">{selectedItem.item_name}</h3>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${
                  selectedItem.item_rarity === 'THE_ONE_ONLY' ? 'bg-pink-500/20 text-pink-400' :
                  selectedItem.item_rarity === 'LEGEND' ? 'bg-yellow-500/20 text-yellow-400' :
                  selectedItem.item_rarity === 'EPIC' ? 'bg-purple-500/20 text-purple-400' :
                  selectedItem.item_rarity === 'RARE' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-zinc-500/20 text-zinc-400'
                }`}>
                  {t(`rarity.${selectedItem.item_rarity}`)}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-zinc-500 text-xs">Status</p>
                  <p className={`font-medium ${
                    selectedItem.status === 'kept' ? 'text-green-400' :
                    selectedItem.status === 'sold' ? 'text-zinc-400' :
                    'text-orange-400'
                  }`}>{selectedItem.status?.toUpperCase()}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-zinc-500 text-xs">Pool</p>
                  <p className="text-white font-medium truncate">{selectedItem.pool_name || '-'}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-zinc-500 text-xs">Obtained</p>
                  <p className="text-white font-medium">{formatDate(selectedItem.created_at)}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-3">
                  <p className="text-zinc-500 text-xs">Sell Value</p>
                  <p className="text-yellow-400 font-medium">{selectedItem.sell_value?.toLocaleString() || 0} coins</p>
                </div>
              </div>

              {/* NFT Info (if minted) */}
              {selectedItem.nft_mint && (
                <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2 text-purple-400 font-medium">
                    <Gem className="w-4 h-4" />
                    NFT Information
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-zinc-500 text-xs">Mint Address</p>
                      <p className="text-white font-mono text-sm break-all">{selectedItem.nft_mint}</p>
                    </div>
                    {selectedItem.nft_symbol && (
                      <div>
                        <p className="text-zinc-500 text-xs">Symbol</p>
                        <p className="text-white font-medium">{selectedItem.nft_symbol}</p>
                      </div>
                    )}
                    {selectedItem.nft_metadata_uri && (
                      <div>
                        <p className="text-zinc-500 text-xs">Metadata URI</p>
                        <a 
                          href={selectedItem.nft_metadata_uri} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-cyan-400 hover:underline text-sm break-all"
                        >
                          {selectedItem.nft_metadata_uri}
                        </a>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <a
                        href={`https://solscan.io/token/${selectedItem.nft_mint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-cyan-400 text-sm flex items-center justify-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> Solscan
                      </a>
                      <a
                        href={`https://nftscan.io/solana/token/${selectedItem.nft_mint}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-purple-400 text-sm flex items-center justify-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" /> NFTScan
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <Button variant="outline" onClick={() => setShowItemDetail(false)} className="w-full border-zinc-700 text-zinc-400">
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Toggle Account Status Dialog */}
      <Dialog open={showToggleStatus} onOpenChange={setShowToggleStatus}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {userDetail?.user?.is_active === false ? (
                <><UserCheck className="w-5 h-5 text-green-400" /> Activate Account</>
              ) : (
                <><AlertTriangle className="w-5 h-5 text-red-400" /> Deactivate Account</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-zinc-800 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                selectedUser?.is_admin ? 'bg-gradient-to-br from-cyan-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-purple-500'
              }`}>
                {selectedUser?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-white font-medium">{selectedUser?.name}</p>
                <p className="text-zinc-500 text-sm font-mono">
                  {selectedUser?.wallet_address 
                    ? `${selectedUser.wallet_address.slice(0, 6)}...${selectedUser.wallet_address.slice(-4)}`
                    : selectedUser?.email || '-'
                  }
                </p>
              </div>
            </div>
            
            {userDetail?.user?.is_active === false ? (
              <>
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <p className="text-green-400 text-sm">
                    This account is currently <span className="font-bold">DEACTIVATED</span>. 
                    Activating will allow the user to login again.
                  </p>
                </div>
                {userDetail?.user?.deactivation_note && (
                  <div className="p-3 bg-zinc-800 rounded-lg">
                    <p className="text-zinc-400 text-xs mb-1">Deactivation Reason:</p>
                    <p className="text-white text-sm">{userDetail.user.deactivation_note}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Ban className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="text-red-400 font-medium">Warning: This action will:</p>
                      <ul className="text-red-300 mt-1 space-y-1 list-disc list-inside">
                        <li>Immediately invalidate the user's session</li>
                        <li>Prevent the user from logging in</li>
                        <li>Show your message when they try to connect</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-zinc-300">Reason for Deactivation *</label>
                  <Textarea
                    value={toggleStatusNote}
                    onChange={(e) => setToggleStatusNote(e.target.value)}
                    placeholder="Explain why this account is being deactivated. This message will be shown to the user."
                    className="mt-1 bg-zinc-800 border-zinc-700 text-white h-24"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => { setShowToggleStatus(false); setToggleStatusNote(''); }} 
              className="flex-1 border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleToggleStatus} 
              disabled={toggleStatusLoading || (userDetail?.user?.is_active !== false && !toggleStatusNote.trim())}
              className={`flex-1 ${userDetail?.user?.is_active === false ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {toggleStatusLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : userDetail?.user?.is_active === false ? (
                <UserCheck className="w-4 h-4 mr-2" />
              ) : (
                <UserX className="w-4 h-4 mr-2" />
              )}
              {userDetail?.user?.is_active === false ? 'Activate Account' : 'Deactivate Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== VOUCHERS TAB ====================
const VouchersTab = ({ token }) => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [deleteVoucher, setDeleteVoucher] = useState(null);
  const [showRedemptions, setShowRedemptions] = useState(null);
  const [formData, setFormData] = useState({
    code: '', coin_amount: 100, max_uses: 1, description: '', expires_at: '', is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchVouchers(); }, []);

  const fetchVouchers = async () => {
    try {
      const res = await axios.get(`${API}/admin/vouchers`, { headers: { Authorization: `Bearer ${token}` } });
      setVouchers(res.data);
    } catch (err) {
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.coin_amount) {
      toast.error('Code and coin amount are required');
      return;
    }
    setSaving(true);
    try {
      if (editingVoucher) {
        await axios.put(`${API}/admin/vouchers/${editingVoucher.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Voucher updated');
      } else {
        await axios.post(`${API}/admin/vouchers`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Voucher created');
      }
      fetchVouchers();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save voucher');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/vouchers/${deleteVoucher.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Voucher deleted');
      fetchVouchers();
      setDeleteVoucher(null);
    } catch (err) {
      toast.error('Failed to delete voucher');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingVoucher(null);
    setFormData({ code: '', coin_amount: 100, max_uses: 1, description: '', expires_at: '', is_active: true });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-zinc-400 text-sm">{vouchers.length} vouchers</p>
        <Button onClick={() => setShowForm(true)} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> Create Voucher
        </Button>
      </div>

      {/* Vouchers Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
        </div>
      ) : vouchers.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <Ticket className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No vouchers yet</p>
          <p className="text-zinc-600 text-sm mt-1">Create your first voucher</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Code</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Coins</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Usage</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Expires</th>
                <th className="text-left text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Status</th>
                <th className="text-right text-zinc-400 text-xs font-medium uppercase tracking-wider p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map((voucher) => (
                <tr key={voucher.id} className="border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/50">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1 bg-zinc-800 rounded text-yellow-400 font-mono text-sm">{voucher.code}</code>
                      <button onClick={() => copyCode(voucher.code)} className="text-zinc-500 hover:text-white">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    {voucher.description && (
                      <p className="text-zinc-500 text-xs mt-1">{getLocalizedText(voucher.description)}</p>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-yellow-400">
                      <Coins className="w-4 h-4" />
                      <span className="font-bold">{voucher.coin_amount?.toLocaleString()}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <span className={`${voucher.used_count >= voucher.max_uses ? 'text-cyan-400' : 'text-green-400'}`}>
                        {voucher.used_count || 0} / {voucher.max_uses}
                      </span>
                      {voucher.used_count > 0 && (
                        <button 
                          onClick={() => setShowRedemptions(voucher)}
                          className="text-zinc-500 hover:text-white text-xs underline"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-zinc-400 text-sm">
                    {voucher.expires_at ? (
                      <span className={isExpired(voucher.expires_at) ? 'text-cyan-400' : ''}>
                        {formatDate(voucher.expires_at)}
                        {isExpired(voucher.expires_at) && ' (expired)'}
                      </span>
                    ) : (
                      <span className="text-zinc-600">Never</span>
                    )}
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      !voucher.is_active ? 'bg-zinc-500/20 text-zinc-400' :
                      voucher.used_count >= voucher.max_uses ? 'bg-cyan-500/20 text-cyan-400' :
                      isExpired(voucher.expires_at) ? 'bg-orange-500/20 text-orange-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {!voucher.is_active ? 'Inactive' :
                       voucher.used_count >= voucher.max_uses ? 'Used Up' :
                       isExpired(voucher.expires_at) ? 'Expired' :
                       'Active'}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingVoucher(voucher);
                          setFormData({
                            code: voucher.code,
                            coin_amount: voucher.coin_amount,
                            max_uses: voucher.max_uses,
                            description: voucher.description || '',
                            expires_at: voucher.expires_at || '',
                            is_active: voucher.is_active
                          });
                          setShowForm(true);
                        }}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteVoucher(voucher)}
                        className="text-zinc-400 hover:text-cyan-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingVoucher ? 'Edit Voucher' : 'Create Voucher'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-zinc-300">Voucher Code</label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., WELCOME100"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white font-mono uppercase"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-300">Coin Amount</label>
                <Input
                  type="number"
                  value={formData.coin_amount}
                  onChange={(e) => setFormData({ ...formData, coin_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-300">Max Uses</label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: parseInt(e.target.value) || 1 })}
                  className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Description (optional)</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Welcome bonus voucher"
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Expires At (optional)</label>
              <Input
                type="datetime-local"
                value={formData.expires_at ? formData.expires_at.slice(0, 16) : ''}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded bg-zinc-800 border-zinc-700"
              />
              <label htmlFor="is_active" className="text-sm text-zinc-300">Active</label>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={resetForm} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingVoucher ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteVoucher} onOpenChange={() => setDeleteVoucher(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Voucher</DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 py-4">
            Are you sure you want to delete voucher <code className="text-yellow-400">{deleteVoucher?.code}</code>? This cannot be undone.
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setDeleteVoucher(null)} className="flex-1 border-zinc-700 text-zinc-400">
              Cancel
            </Button>
            <Button onClick={handleDelete} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Redemptions Dialog */}
      <Dialog open={!!showRedemptions} onOpenChange={() => setShowRedemptions(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Redemption History - {showRedemptions?.code}</DialogTitle>
          </DialogHeader>
          <div className="py-4 max-h-80 overflow-y-auto">
            {showRedemptions?.redemptions?.length === 0 ? (
              <p className="text-zinc-500 text-center">No redemptions yet</p>
            ) : (
              <div className="space-y-2">
                {showRedemptions?.redemptions?.map((r, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                    <div>
                      <p className="text-white text-sm">{r.user_name || 'Unknown'}</p>
                      <p className="text-zinc-500 text-xs">{r.user_email}</p>
                    </div>
                    <p className="text-zinc-400 text-xs">{formatDate(r.redeemed_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Button variant="outline" onClick={() => setShowRedemptions(null)} className="w-full border-zinc-700 text-zinc-400">
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CUSTOM PAGES TAB ====================
const PagesTab = ({ token }) => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Get current domain for preview
  const currentDomain = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(res.data);
    } catch (err) {
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pageId) => {
    try {
      await axios.delete(`${API}/admin/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page deleted');
      fetchPages();
      setDeleteConfirm(null);
    } catch (err) {
      toast.error('Failed to delete page');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Custom Pages</h2>
          <p className="text-zinc-500 text-sm mt-1">Create and manage custom pages with rich content</p>
        </div>
        <Button onClick={() => navigate('/admin/pages/new')} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" /> New Page
        </Button>
      </div>

      {/* Dynamic Parameters Info */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <h4 className="text-zinc-300 text-sm font-medium mb-2 flex items-center gap-2">
          <Code className="w-4 h-4" /> Dynamic Parameters (for Login Required pages)
        </h4>
        <div className="flex flex-wrap gap-2">
          {['{name}', '{email}', '{country}', '{coins}', '{tickets}'].map(param => (
            <span key={param} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs rounded-md font-mono">
              {param}
            </span>
          ))}
        </div>
      </div>

      {/* Pages List */}
      {pages.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No custom pages yet</p>
          <p className="text-zinc-600 text-sm mt-1">Create your first page to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map(page => (
            <motion.div
              key={page.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-semibold truncate">{page.title}</h3>
                    {page.visibility === 'login_required' ? (
                      <span className="px-2 py-0.5 bg-yellow-900/50 text-yellow-400 text-xs rounded flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Login Required
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded flex items-center gap-1">
                        <Unlock className="w-3 h-3" /> Public
                      </span>
                    )}
                    {!page.is_active && (
                      <span className="px-2 py-0.5 bg-zinc-700 text-zinc-400 text-xs rounded">
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Link className="w-3 h-3 text-zinc-500" />
                    <a 
                      href={`${currentDomain}/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-500 text-sm hover:text-cyan-400 truncate"
                    >
                      {currentDomain}/{page.slug}
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${currentDomain}/${page.slug}`);
                        toast.success('Link copied!');
                      }}
                      className="p-1 hover:bg-zinc-800 rounded"
                    >
                      <Copy className="w-3 h-3 text-zinc-500" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-zinc-600 text-xs">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Created: {formatDate(page.created_at)}
                    </span>
                    {page.updated_at && (
                      <span>Updated: {formatDate(page.updated_at)}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`${currentDomain}/${page.slug}`, '_blank')}
                    className="border-zinc-700 text-zinc-400"
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/admin/pages/${page.id}`)}
                    className="border-zinc-700 text-zinc-400"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteConfirm(page)}
                    className="border-cyan-900 text-cyan-500 hover:bg-cyan-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Page</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{deleteConfirm?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete(deleteConfirm.id);
              }}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ==================== SUPPORT TICKETS TAB ====================
// Convert file to base64
const fileToBase64Admin = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const SupportTicketsTab = ({ token, onStatsUpdate }) => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const messagesEndRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    // Poll for new tickets every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
      // Update parent component for sidebar badge
      if (onStatsUpdate) onStatsUpdate(res.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/admin/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketDetail(res.data.ticket);
      setMessages(res.data.messages);
      fetchStats(); // Refresh unread count
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    
    try {
      setSending(true);
      await axios.post(`${API}/admin/tickets/${selectedTicket}/messages`, {
        message: newMessage,
        attachments: attachments
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      setAttachments([]);
      fetchTicketDetail(selectedTicket);
      fetchTickets();
      fetchStats(); // Auto-update badge after reply
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API}/admin/tickets/${selectedTicket}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ticket status updated to ${status}`);
      fetchTicketDetail(selectedTicket);
      fetchTickets();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await axios.put(`${API}/admin/tickets/${selectedTicket}/priority?priority=${priority}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Priority updated to ${priority}`);
      fetchTicketDetail(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const base64 = await fileToBase64Admin(file);
      setAttachments(prev => [...prev, base64]);
      toast.success('Image added');
    } catch (error) {
      toast.error('Failed to process image');
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = React.useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) await handleImageUpload(file);
      }
    }
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [selectedTicket, handlePaste]);

  const openTicket = (ticketId) => {
    setSelectedTicket(ticketId);
    fetchTicketDetail(ticketId);
  };

  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'unread') return t.unread_by_admin;
    return t.status === filter;
  });

  const StatusBadge = ({ status }) => {
    const config = {
      open: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Open' },
      in_progress: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'In Progress' },
      resolved: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Resolved' },
      closed: { color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', label: 'Closed' }
    }[status] || { color: 'bg-zinc-500/20 text-zinc-400', label: status };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>{config.label}</span>;
  };

  const PriorityBadge = ({ priority }) => {
    const config = {
      low: { color: 'text-zinc-400', label: 'Low' },
      normal: { color: 'text-blue-400', label: 'Normal' },
      high: { color: 'text-orange-400', label: 'High' },
      urgent: { color: 'text-red-400', label: 'Urgent' }
    }[priority] || { color: 'text-zinc-400', label: priority };
    return <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const CategoryBadge = ({ category }) => {
    const config = {
      general: 'text-zinc-400',
      technical: 'text-purple-400',
      billing: 'text-green-400',
      other: 'text-orange-400'
    }[category] || 'text-zinc-400';
    return <span className={`text-xs ${config}`}>{category}</span>;
  };

  // Ticket Detail View
  if (selectedTicket && ticketDetail) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
          <button
            onClick={() => { setSelectedTicket(null); setTicketDetail(null); }}
            className="p-2 hover:bg-zinc-800 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <div className="flex-1">
            <h3 className="text-white font-semibold">{ticketDetail.subject}</h3>
            <div className="flex items-center gap-3 mt-1 text-sm">
              <span className="text-zinc-400">From: {ticketDetail.user_name}</span>
              <span className="text-zinc-600">|</span>
              <span className="text-zinc-500">{ticketDetail.user_email || ticketDetail.user_wallet?.slice(0, 8) + '...'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Priority */}
            <Select value={ticketDetail.priority} onValueChange={handlePriorityChange}>
              <SelectTrigger className="w-28 bg-zinc-800 border-zinc-700 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            {/* Status */}
            <Select value={ticketDetail.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Messages */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 h-[500px] overflow-y-auto">
          {messages.map((msg) => {
            const isAdmin = msg.sender_type === 'admin';
            const isSystem = msg.sender_type === 'system';
            
            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center my-4">
                  <div className="px-4 py-2 bg-zinc-800/50 rounded-full text-xs text-zinc-500">
                    {msg.message}
                  </div>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} mb-4`}>
                <div className={`max-w-[70%]`}>
                  <div className="flex items-center gap-2 mb-1">
                    {!isAdmin && (
                      <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center">
                        <User className="w-3 h-3 text-zinc-400" />
                      </div>
                    )}
                    <span className={`text-xs ${isAdmin ? 'text-cyan-400' : 'text-zinc-400'}`}>
                      {msg.sender_name}
                    </span>
                    <span className="text-xs text-zinc-600">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                    {isAdmin && (
                      <div className="w-6 h-6 rounded-full bg-cyan-600 flex items-center justify-center">
                        <Shield className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  <div className={`rounded-2xl p-4 ${
                    isAdmin 
                      ? 'bg-cyan-600 text-white rounded-br-md' 
                      : 'bg-zinc-800 text-zinc-200 rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm">{msg.message}</p>
                    {msg.attachments?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {msg.attachments.map((url, idx) => (
                          <a 
                            key={idx} 
                            href={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block w-20 h-20 rounded-lg overflow-hidden border border-white/20"
                          >
                            <img 
                              src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} 
                              alt="attachment" 
                              className="w-full h-full object-cover" 
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Reply Input */}
        {ticketDetail.status !== 'closed' && (
          <div className="space-y-3 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((url, idx) => (
                  <div key={idx} className="relative w-16 h-16 rounded-lg overflow-hidden border border-zinc-700">
                    <img 
                      src={url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} 
                      alt="attachment" 
                      className="w-full h-full object-cover" 
                    />
                    <button
                      onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute top-1 right-1 p-0.5 bg-red-500 rounded-full"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleImageUpload(e.target.files?.[0])}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-zinc-400 hover:text-white"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </Button>
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply... (Ctrl+V to paste image)"
                className="flex-1 bg-zinc-800 border-zinc-700"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              />
              <Button
                onClick={handleSendMessage}
                disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Ticket List View
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'text-white' },
          { label: 'Open', value: stats.open, color: 'text-blue-400' },
          { label: 'In Progress', value: stats.in_progress, color: 'text-yellow-400' },
          { label: 'Resolved', value: stats.resolved, color: 'text-green-400' },
          { label: 'Closed', value: stats.closed, color: 'text-zinc-400' },
          { label: 'Unread', value: stats.unread, color: 'text-red-400', highlight: stats.unread > 0 }
        ].map((stat) => (
          <div 
            key={stat.label} 
            className={`p-4 rounded-xl border ${stat.highlight ? 'bg-red-500/10 border-red-500/30' : 'bg-zinc-900 border-zinc-800'}`}
          >
            <p className="text-xs text-zinc-500 uppercase">{stat.label}</p>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        {['all', 'unread', 'open', 'in_progress', 'resolved', 'closed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              filter === f
                ? 'bg-cyan-600 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'unread' && stats.unread > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {stats.unread}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Ticket List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <MessageCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No tickets found</p>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-800/50">
              <tr className="text-left text-xs text-zinc-500 uppercase">
                <th className="p-4">Ticket</th>
                <th className="p-4">User</th>
                <th className="p-4">Category</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Status</th>
                <th className="p-4">Updated</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filteredTickets.map((ticket) => (
                <tr 
                  key={ticket.id} 
                  className={`hover:bg-zinc-800/50 cursor-pointer transition ${ticket.unread_by_admin ? 'bg-cyan-500/5' : ''}`}
                  onClick={() => openTicket(ticket.id)}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {ticket.unread_by_admin && (
                        <span className="w-2 h-2 rounded-full bg-cyan-500 flex-shrink-0" />
                      )}
                      <span className="text-white font-medium truncate max-w-[200px]">{ticket.subject}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{ticket.message_count} messages</p>
                  </td>
                  <td className="p-4">
                    <p className="text-white text-sm">{ticket.user_name}</p>
                    <p className="text-xs text-zinc-500 truncate max-w-[150px]">
                      {ticket.user_email || ticket.user_wallet?.slice(0, 12) + '...'}
                    </p>
                  </td>
                  <td className="p-4">
                    <CategoryBadge category={ticket.category} />
                  </td>
                  <td className="p-4">
                    <PriorityBadge priority={ticket.priority} />
                  </td>
                  <td className="p-4">
                    <StatusBadge status={ticket.status} />
                  </td>
                  <td className="p-4 text-xs text-zinc-500">
                    {new Date(ticket.updated_at).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <ChevronRight className="w-5 h-5 text-zinc-600" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== SHIPPING TAB ====================
const ShippingTab = ({ token }) => {
  return (
    <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
      <Truck className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
      <p className="text-zinc-500">No shipping requests yet</p>
      <p className="text-zinc-600 text-sm mt-1">Shipping requests will appear here</p>
    </div>
  );
};


// ==================== SWAP MANAGEMENT TAB ====================
const SwapManagementTab = ({ token }) => {
  const [swaps, setSwaps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState('all');
  const [treasury, setTreasury] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [swapRes, statsRes, treasuryRes] = await Promise.all([
        axios.get(`${API}/api/admin/swaps`, { headers }),
        axios.get(`${API}/api/admin/swaps/stats`, { headers }),
        axios.get(`${API}/api/admin/swap/treasury`, { headers }),
      ]);
      setSwaps(swapRes.data);
      setStats(statsRes.data);
      setTreasury(treasuryRes.data);
    } catch (err) {
      toast.error('Failed to load swap data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAction = async (swapId, action) => {
    const swap = swaps.find(s => s.id === swapId);
    if (action === 'accept' && !treasury?.has_private_key) {
      toast.error('Treasury private key not configured! Go to Solana Settings first.');
      return;
    }
    if (action === 'accept') {
      if (!window.confirm(`Accept swap and send ${swap?.net_sol?.toFixed(6)} SOL to ${swap?.wallet_address}?`)) return;
    }
    setProcessing(swapId);
    try {
      const res = await axios.post(`${API}/api/admin/swaps/${action}`, { swap_id: swapId }, { headers });
      toast.success(res.data.message);
      if (res.data.tx_signature) {
        toast.success(`TX: ${res.data.tx_signature.slice(0, 20)}...`, { duration: 6000 });
      }
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} swap`);
    } finally {
      setProcessing(null);
    }
  };

  const filtered = filter === 'all' ? swaps : swaps.filter(s => s.status === filter);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Pending', value: stats?.pending || 0, color: 'text-amber-400', bg: 'bg-amber-500/10' },
          { label: 'Accepted', value: stats?.accepted || 0, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
          { label: 'Declined', value: stats?.declined || 0, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Total SOL Paid', value: `${(stats?.total_sol_paid || 0).toFixed(4)}`, color: 'text-purple-400', bg: 'bg-purple-500/10' },
          { label: 'Total Coins Swapped', value: (stats?.total_coins_swapped || 0).toLocaleString(), color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`${bg} rounded-xl p-3 border border-zinc-800`}>
            <p className="text-zinc-500 text-xs">{label}</p>
            <p className={`${color} text-lg font-bold`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Treasury Info */}
      {treasury && (
        <div className={`rounded-xl p-4 border ${treasury.has_private_key ? 'border-emerald-800 bg-emerald-900/10' : 'border-red-800 bg-red-900/10'}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-bold text-white flex items-center gap-2">
                {treasury.has_private_key ? <CheckCircle className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                Treasury Wallet
              </p>
              <p className="text-zinc-400 text-xs mt-1">
                Network: {treasury.network} | Fee: {treasury.swap_fee_percent}%
              </p>
              {treasury.treasury_wallet && (
                <p className="text-zinc-500 text-xs mt-0.5 font-mono">{treasury.treasury_wallet}</p>
              )}
            </div>
            {!treasury.has_private_key && (
              <p className="text-red-400 text-xs">Set private key in Solana Settings to enable auto-transfer</p>
            )}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'accepted', 'declined'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f ? 'bg-cyan-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f === 'pending' && stats?.pending > 0 && (
              <span className="ml-1.5 bg-amber-500 text-black text-[10px] px-1.5 py-0.5 rounded-full font-bold">{stats.pending}</span>
            )}
          </button>
        ))}
      </div>

      {/* Swap List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-zinc-800 rounded-xl">
          <RefreshCw className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">No {filter === 'all' ? '' : filter} swap requests</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(swap => {
            const isPending = swap.status === 'pending';
            const isAccepted = swap.status === 'accepted';
            const isDeclined = swap.status === 'declined';
            const isProcessingThis = processing === swap.id;

            return (
              <div
                key={swap.id}
                className={`rounded-xl border p-4 ${
                  isPending ? 'border-amber-800/30 bg-amber-900/5' :
                  isAccepted ? 'border-emerald-800/30 bg-emerald-900/5' :
                  'border-red-800/30 bg-red-900/5'
                }`}
                data-testid={`admin-swap-${swap.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                        isPending ? 'bg-amber-500/20 text-amber-400' :
                        isAccepted ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>{swap.status.toUpperCase()}</span>
                      <span className="text-zinc-500 text-xs">{new Date(swap.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-white text-sm font-bold">
                      {swap.user_name} ({swap.user_email || swap.wallet_address?.slice(0, 8) + '...'})
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                      <span className="text-amber-400 text-xs">{swap.coin_amount.toLocaleString()} Coins</span>
                      <span className="text-zinc-500 text-xs">Gross: {swap.gross_sol?.toFixed(6)} SOL</span>
                      <span className="text-red-400 text-xs">Fee: -{swap.fee_sol?.toFixed(6)} SOL</span>
                      <span className="text-emerald-400 text-xs font-bold">Net: {swap.net_sol?.toFixed(6)} SOL</span>
                    </div>
                    <p className="text-zinc-600 text-xs mt-1 font-mono">Wallet: {swap.wallet_address}</p>
                    {swap.tx_signature && (
                      <a
                        href={`https://solscan.io/tx/${swap.tx_signature}${treasury?.network === 'devnet' ? '?cluster=devnet' : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-500 text-xs hover:text-cyan-300 flex items-center gap-1 mt-1"
                      >
                        <ExternalLink className="w-3 h-3" /> {swap.tx_signature.slice(0, 24)}...
                      </a>
                    )}
                    {swap.admin_note && <p className="text-zinc-500 text-xs mt-1">{swap.admin_note}</p>}
                  </div>

                  {isPending && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleAction(swap.id, 'accept')}
                        disabled={isProcessingThis}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        data-testid={`swap-accept-${swap.id}`}
                      >
                        {isProcessingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Accept
                      </button>
                      <button
                        onClick={() => handleAction(swap.id, 'decline')}
                        disabled={isProcessingThis}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-bold transition-colors disabled:opacity-50"
                        data-testid={`swap-decline-${swap.id}`}
                      >
                        {isProcessingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ==================== DEVELOPER TAB ====================
// ==================== LANGUAGE MANAGEMENT SECTION ====================
const LanguageManagementSection = () => {
  const { t, i18n } = useTranslation();
  const [selectedLang, setSelectedLang] = useState('en');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDownloadLanguage = (langCode) => {
    const langData = exportLanguage(langCode);
    const blob = new Blob([langData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `language_${langCode}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${langCode}.json`);
  };

  const handleDownloadAll = () => {
    const allData = exportAllLanguages();
    const blob = new Blob([allData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_languages_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Downloaded all languages');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        setUploadedData(data);
        setShowUploadDialog(true);
      } catch (error) {
        toast.error('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImportLanguage = () => {
    if (!uploadedData) return;
    
    setUploading(true);
    try {
      // Check if it's a single language or multiple
      if (uploadedData.nav && uploadedData.common) {
        // Single language file - save to selected language
        saveCustomTranslations(selectedLang, uploadedData);
        toast.success(`Translations imported for ${selectedLang}`);
      } else {
        // Multiple languages file
        Object.keys(uploadedData).forEach(langCode => {
          if (uploadedData[langCode]) {
            saveCustomTranslations(langCode, uploadedData[langCode]);
          }
        });
        toast.success('All translations imported successfully');
      }
      setShowUploadDialog(false);
      setUploadedData(null);
    } catch (error) {
      toast.error('Failed to import translations');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
      <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
        <Languages className="w-5 h-5 text-violet-500" />
        Language Management
      </h3>
      
      <p className="text-zinc-500 text-sm mb-6">
        Download, edit, and upload language files to customize translations. 
        Supported languages: English (default), Japanese, Chinese, Vietnamese, Indonesian, Malay, Russian.
      </p>

      {/* Current Languages Grid */}
      <div className="mb-6">
        <h4 className="text-zinc-400 text-sm font-medium mb-3">Available Languages</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {SUPPORTED_LANGUAGES.map(lang => (
            <div 
              key={lang.code}
              className={`bg-zinc-800/50 rounded-lg p-3 cursor-pointer transition border-2 ${
                selectedLang === lang.code ? 'border-violet-500' : 'border-transparent hover:border-zinc-700'
              }`}
              onClick={() => setSelectedLang(lang.code)}
            >
              <div className="text-2xl mb-1">{lang.flag}</div>
              <p className="text-white text-sm font-medium">{lang.code.toUpperCase()}</p>
              <p className="text-zinc-500 text-xs truncate">{lang.nativeName}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Download/Upload Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Download Selected */}
        <div className="bg-zinc-800/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">Download Language</h4>
          <p className="text-zinc-500 text-sm mb-4">
            Download selected language ({selectedLang.toUpperCase()}) as JSON file.
          </p>
          <Button
            onClick={() => handleDownloadLanguage(selectedLang)}
            className="w-full bg-violet-600 hover:bg-violet-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Download {selectedLang.toUpperCase()}.json
          </Button>
        </div>

        {/* Download All */}
        <div className="bg-zinc-800/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">Export All Languages</h4>
          <p className="text-zinc-500 text-sm mb-4">
            Download all languages in a single JSON file.
          </p>
          <Button
            onClick={handleDownloadAll}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-2" />
            Export All
          </Button>
        </div>

        {/* Upload */}
        <div className="bg-zinc-800/30 rounded-lg p-4">
          <h4 className="text-white font-medium mb-2">Upload Language File</h4>
          <p className="text-zinc-500 text-sm mb-4">
            Upload edited JSON file to update translations.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full bg-green-600 hover:bg-green-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload JSON
          </Button>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 p-4 bg-violet-900/20 border border-violet-500/30 rounded-lg">
        <h4 className="text-violet-400 font-medium mb-2">How to Add/Edit Translations</h4>
        <ol className="text-violet-300/80 text-sm space-y-1 list-decimal list-inside">
          <li>Download the English (EN) file as a template</li>
          <li>Translate each text value in the JSON file (keep the keys in English)</li>
          <li>Save the file with the same structure</li>
          <li>Upload the file and select the target language</li>
          <li>Translations will be applied immediately</li>
        </ol>
      </div>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-500" />
              Import Translations
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400 text-sm mb-2">File preview:</p>
              <p className="text-white text-sm">
                {uploadedData && (uploadedData.nav ? 
                  `Single language file detected (${Object.keys(uploadedData).length} sections)` :
                  `Multi-language file detected (${Object.keys(uploadedData).length} languages)`
                )}
              </p>
            </div>

            {uploadedData?.nav && (
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Language</label>
                <Select value={selectedLang} onValueChange={setSelectedLang}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.flag} {lang.name} ({lang.nativeName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleImportLanguage}
                disabled={uploading}
                className="bg-green-600 hover:bg-green-700"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Import Translations
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== DEVELOPER TAB ====================
const DeveloperTab = ({ token }) => {
  const [dbInfo, setDbInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [password, setPassword] = useState('');
  const [importData, setImportData] = useState('');
  const [exportedData, setExportedData] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Migration state
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const [migrateStep, setMigrateStep] = useState('form'); // form, preview, migrating, done
  const [migrateForm, setMigrateForm] = useState({ target_connection: '', target_db_name: '', password: '' });
  const [migratePreview, setMigratePreview] = useState(null);
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrateLoading, setMigrateLoading] = useState(false);

  // Switch DB state
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [switchForm, setSwitchForm] = useState({ new_connection: '', new_db_name: '', password: '' });
  const [switchLoading, setSwitchLoading] = useState(false);

  useEffect(() => {
    fetchDbInfo();
  }, []);

  const fetchDbInfo = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/developer/db-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDbInfo(res.data);
    } catch (error) {
      toast.error('Failed to fetch database info');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setActionLoading('export');
      const res = await axios.post(`${API}/admin/developer/export`, 
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExportedData(res.data);
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `db_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Database exported successfully!');
      setShowPasswordDialog(false);
      setPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Export failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImport = async () => {
    try {
      const parsedData = JSON.parse(importData);
      if (!parsedData.data) {
        toast.error('Invalid import format. Expected { data: {...} }');
        return;
      }
      
      setActionLoading('import');
      const res = await axios.post(`${API}/admin/developer/import`, 
        { password, data: parsedData.data },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Database imported successfully!');
      setShowImportDialog(false);
      setShowPasswordDialog(false);
      setPassword('');
      setImportData('');
      fetchDbInfo();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON format');
      } else {
        toast.error(error.response?.data?.detail || 'Import failed');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDestroyAll = async () => {
    try {
      setActionLoading('destroy');
      const res = await axios.post(`${API}/admin/developer/destroy-all`, 
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('All data destroyed! New admin account created.');
      setShowPasswordDialog(false);
      setPassword('');
      
      // Show new credentials
      alert(`New Admin Credentials:\nEmail: ${res.data.new_admin.email}\nPassword: ${res.data.new_admin.password}`);
      
      // Logout and redirect to login
      window.location.href = '/admin/login';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Destroy failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteExampleData = async () => {
    try {
      setActionLoading('delete-example');
      await axios.post(`${API}/admin/developer/delete-example-data`, 
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Example data deleted successfully!');
      setShowPasswordDialog(false);
      setPassword('');
      fetchDbInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPoolsItems = async () => {
    try {
      setActionLoading('reset-pools');
      const res = await axios.post(`${API}/admin/developer/reset-pools-items`, { password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All pools, items, and inventories deleted!');
      console.log('Reset pools result:', res.data);
      setShowPasswordDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset pools & items');
    } finally {
      setActionLoading(null);
    }
  };

  // ========== Migration Functions ==========
  const handleMigratePreview = async () => {
    if (!migrateForm.target_connection || !migrateForm.target_db_name || !migrateForm.password) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      setMigrateLoading(true);
      const res = await axios.post(`${API}/admin/developer/migrate-preview`, {
        password: migrateForm.password,
        target_connection: migrateForm.target_connection,
        target_db_name: migrateForm.target_db_name
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setMigratePreview(res.data);
      if (res.data.connection_ok) {
        setMigrateStep('preview');
      } else {
        toast.error(`Connection failed: ${res.data.error}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setMigrateLoading(false);
    }
  };

  const handleMigrateExecute = async () => {
    try {
      setMigrateLoading(true);
      setMigrateStep('migrating');
      const res = await axios.post(`${API}/admin/developer/migrate-execute`, {
        password: migrateForm.password,
        target_connection: migrateForm.target_connection,
        target_db_name: migrateForm.target_db_name
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 });
      
      setMigrateResult(res.data);
      setMigrateStep('done');
      if (res.data.success) {
        toast.success('Database migration completed!');
      } else {
        toast.warning('Migration completed with some errors');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
      setMigrateStep('preview');
    } finally {
      setMigrateLoading(false);
    }
  };

  const resetMigrateDialog = () => {
    setShowMigrateDialog(false);
    setMigrateStep('form');
    setMigrateForm({ target_connection: '', target_db_name: '', password: '' });
    setMigratePreview(null);
    setMigrateResult(null);
  };

  const handleSwitchDatabase = async () => {
    if (!switchForm.new_connection || !switchForm.new_db_name || !switchForm.password) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      setSwitchLoading(true);
      const res = await axios.post(`${API}/admin/developer/switch-database`, {
        password: switchForm.password,
        new_connection: switchForm.new_connection,
        new_db_name: switchForm.new_db_name
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(res.data.message);
      setShowSwitchDialog(false);
      setSwitchForm({ new_connection: '', new_db_name: '', password: '' });
      
      // Show restart notice
      if (res.data.restart_required) {
        toast.info('Server restart required. The app will reload automatically.', { duration: 5000 });
        setTimeout(() => window.location.reload(), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Switch failed');
    } finally {
      setSwitchLoading(false);
    }
  };

  const executeAction = () => {
    switch (pendingAction) {
      case 'export':
        handleExport();
        break;
      case 'import':
        handleImport();
        break;
      case 'destroy':
        handleDestroyAll();
        break;
      case 'delete-example':
        handleDeleteExampleData();
        break;
      case 'reset-pools':
        handleResetPoolsItems();
        break;
      default:
        break;
    }
  };

  const openPasswordDialog = (action) => {
    setPendingAction(action);
    setPassword('');
    setShowPasswordDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-400 font-semibold">Developer Zone - Danger Area</h3>
            <p className="text-red-300/70 text-sm mt-1">
              Actions in this section can permanently delete data. Make sure you have a backup before proceeding.
              All destructive actions require password confirmation.
            </p>
          </div>
        </div>
      </div>

      {/* Database Info */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-500" />
          Database Information
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase mb-1">Connection</p>
            <p className="text-white font-mono text-sm break-all">{dbInfo?.connection_string}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-lg p-4">
            <p className="text-zinc-500 text-xs uppercase mb-1">Database Name</p>
            <p className="text-white font-mono text-sm">{dbInfo?.database_name}</p>
          </div>
        </div>

        <h4 className="text-zinc-400 text-sm font-medium mb-3">Collections ({dbInfo?.total_collections})</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {dbInfo?.collections && Object.entries(dbInfo.collections).map(([name, count]) => (
            <div key={name} className="bg-zinc-800/30 rounded-lg p-3">
              <p className="text-zinc-400 text-xs truncate">{name}</p>
              <p className="text-white font-bold">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Export/Import Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-green-500" />
          Export & Import
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-800/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Export Database</h4>
            <p className="text-zinc-500 text-sm mb-4">Download all database data as JSON file.</p>
            <Button
              onClick={() => openPasswordDialog('export')}
              disabled={actionLoading === 'export'}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {actionLoading === 'export' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowDown className="w-4 h-4 mr-2" />
              )}
              Export Data
            </Button>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Import Database</h4>
            <p className="text-zinc-500 text-sm mb-4">Import data from exported JSON file.</p>
            <Button
              onClick={() => setShowImportDialog(true)}
              disabled={actionLoading === 'import'}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {actionLoading === 'import' ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowUp className="w-4 h-4 mr-2" />
              )}
              Import Data
            </Button>
          </div>
        </div>
      </div>

      {/* Language Management Section */}
      <LanguageManagementSection />

      {/* Database Migration Section */}
      <div className="bg-zinc-900 border border-cyan-800/30 rounded-xl p-6">
        <h3 className="text-white font-semibold text-lg mb-2 flex items-center gap-2">
          <Database className="w-5 h-5 text-cyan-500" />
          Database Migration
        </h3>
        <p className="text-zinc-500 text-sm mb-4">Migrate all data and indexes to a new database, or switch the active database connection.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-800/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Migrate Database</h4>
            <p className="text-zinc-500 text-sm mb-4">Copy all collections, documents, and indexes to a target database.</p>
            <Button
              data-testid="migrate-db-btn"
              onClick={() => setShowMigrateDialog(true)}
              className="w-full bg-cyan-600 hover:bg-cyan-700"
            >
              <ArrowUp className="w-4 h-4 mr-2 rotate-90" />
              Migrate to New DB
            </Button>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-4">
            <h4 className="text-white font-medium mb-2">Switch Database</h4>
            <p className="text-zinc-500 text-sm mb-4">Permanently change the active database connection (.env).</p>
            <Button
              data-testid="switch-db-btn"
              onClick={() => setShowSwitchDialog(true)}
              variant="outline"
              className="w-full border-yellow-600 text-yellow-400 hover:bg-yellow-600 hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Switch Active DB
            </Button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-zinc-900 border border-red-800/50 rounded-xl p-6">
        <h3 className="text-red-400 font-semibold text-lg mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Danger Zone
        </h3>
        
        <div className="space-y-4">
          {/* Reset Pools & Items */}
          <div className="bg-orange-900/10 border border-orange-800/30 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-orange-400 font-medium">Reset All Pools & Items</h4>
                <p className="text-zinc-500 text-sm mt-1">
                  Delete all gacha pools, all items in pools, all user inventory (cards owned by users), 
                  and all gacha-related transactions.
                  <span className="text-orange-400"> User accounts and coins will be preserved.</span>
                </p>
                <div className="mt-2 p-2 bg-zinc-800/50 rounded text-xs text-zinc-400">
                  <span className="text-orange-400">Will delete:</span> Pools, Items, Categories, User Inventory, Gacha/Sell Transactions
                </div>
              </div>
              <Button
                onClick={() => openPasswordDialog('reset-pools')}
                disabled={actionLoading === 'reset-pools'}
                variant="outline"
                className="border-orange-600 text-orange-400 hover:bg-orange-600 hover:text-white flex-shrink-0"
              >
                {actionLoading === 'reset-pools' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Reset Pools
              </Button>
            </div>
          </div>
          
          {/* Delete Example Data */}
          <div className="bg-red-900/10 border border-red-800/30 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-white font-medium">Delete Example Data</h4>
                <p className="text-zinc-500 text-sm mt-1">
                  Delete all user accounts (except admins), transactions, inventory, stakes, support tickets, and activity logs. 
                  <span className="text-yellow-500"> Website settings, pools, items, and shop products will be preserved.</span>
                </p>
              </div>
              <Button
                onClick={() => openPasswordDialog('delete-example')}
                disabled={actionLoading === 'delete-example'}
                variant="outline"
                className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white flex-shrink-0"
              >
                {actionLoading === 'delete-example' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Delete
              </Button>
            </div>
          </div>

          {/* Destroy All */}
          <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-red-400 font-medium">Destroy All Data</h4>
                <p className="text-zinc-500 text-sm mt-1">
                  <span className="text-red-400 font-semibold">PERMANENT!</span> Delete ALL data from ALL collections. 
                  A new admin account will be created with default credentials.
                </p>
                <div className="mt-2 p-2 bg-zinc-800/50 rounded text-xs text-zinc-400 font-mono">
                  New Admin: admin@fivehoster.com / Revaband01
                </div>
              </div>
              <Button
                onClick={() => openPasswordDialog('destroy')}
                disabled={actionLoading === 'destroy'}
                className="bg-red-600 hover:bg-red-700 flex-shrink-0"
              >
                {actionLoading === 'destroy' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2" />
                )}
                Destroy All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Password Confirmation Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-red-500" />
              Confirm Action
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-3">
              <p className="text-red-300 text-sm">
                {pendingAction === 'destroy' && 'You are about to DESTROY ALL DATA. This action cannot be undone!'}
                {pendingAction === 'delete-example' && 'You are about to delete all user data. This action cannot be undone!'}
                {pendingAction === 'reset-pools' && 'You are about to delete ALL pools, items, and user inventory. This action cannot be undone!'}
                {pendingAction === 'export' && 'Enter developer password to export database.'}
                {pendingAction === 'import' && 'Enter developer password to import data.'}
              </p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Developer Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter developer password"
                className="bg-zinc-800 border-zinc-700"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={executeAction}
              disabled={!password || actionLoading}
              className={pendingAction === 'destroy' || pendingAction === 'delete-example' || pendingAction === 'reset-pools'
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-cyan-600 hover:bg-cyan-700'
              }
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowUp className="w-5 h-5 text-blue-500" />
              Import Database
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Paste exported JSON data</label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder='{"data": {"users": [...], "settings": [...], ...}}'
                className="bg-zinc-800 border-zinc-700 min-h-[200px] font-mono text-xs"
              />
            </div>
            <p className="text-zinc-500 text-xs">
              Paste the entire content of the exported JSON file. The data will be merged with existing data.
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowImportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setShowImportDialog(false);
                openPasswordDialog('import');
              }}
              disabled={!importData.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Database Migration Dialog */}
      <Dialog open={showMigrateDialog} onOpenChange={(open) => { if (!open) resetMigrateDialog(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-500" />
              {migrateStep === 'form' && 'Database Migration'}
              {migrateStep === 'preview' && 'Migration Preview'}
              {migrateStep === 'migrating' && 'Migrating...'}
              {migrateStep === 'done' && 'Migration Complete'}
            </DialogTitle>
          </DialogHeader>

          {/* Step 1: Form */}
          {migrateStep === 'form' && (
            <div className="space-y-4 py-4">
              <div className="bg-cyan-900/20 border border-cyan-800/30 rounded-lg p-3">
                <p className="text-cyan-300 text-sm">
                  This will copy ALL data (collections, documents, and indexes) from the current database to the target database.
                  The target database will be overwritten if it has existing data in the same collections.
                </p>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Connection String</label>
                <Input
                  data-testid="migrate-target-connection"
                  value={migrateForm.target_connection}
                  onChange={(e) => setMigrateForm(prev => ({ ...prev, target_connection: e.target.value }))}
                  placeholder="mongodb://username:password@host:27017"
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Target Database Name</label>
                <Input
                  data-testid="migrate-target-db-name"
                  value={migrateForm.target_db_name}
                  onChange={(e) => setMigrateForm(prev => ({ ...prev, target_db_name: e.target.value }))}
                  placeholder="my_new_database"
                  className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Developer Password</label>
                <Input
                  data-testid="migrate-password"
                  type="password"
                  value={migrateForm.password}
                  onChange={(e) => setMigrateForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter developer password"
                  className="bg-zinc-800 border-zinc-700"
                  autoComplete="off"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={resetMigrateDialog}>Cancel</Button>
                <Button
                  data-testid="migrate-preview-btn"
                  onClick={handleMigratePreview}
                  disabled={migrateLoading || !migrateForm.target_connection || !migrateForm.target_db_name || !migrateForm.password}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  {migrateLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  Test Connection & Preview
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {migrateStep === 'preview' && migratePreview && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Source DB */}
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                  <h4 className="text-cyan-400 font-medium mb-2 text-sm">Source (Current DB)</h4>
                  <p className="text-white font-mono text-sm mb-1">{migratePreview.source?.db_name}</p>
                  <p className="text-zinc-400 text-xs">{migratePreview.source?.total_collections} collections | {migratePreview.source?.total_documents?.toLocaleString()} documents</p>
                  <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                    {migratePreview.source?.collections && Object.entries(migratePreview.source.collections).map(([name, count]) => (
                      <div key={name} className="flex justify-between text-xs">
                        <span className="text-zinc-400 truncate mr-2">{name}</span>
                        <span className="text-white font-mono">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Target DB */}
                <div className="bg-zinc-800/50 rounded-lg p-4 border border-green-800/30">
                  <h4 className="text-green-400 font-medium mb-2 text-sm">Target (New DB)</h4>
                  <p className="text-white font-mono text-sm mb-1">{migratePreview.target?.db_name}</p>
                  <p className="text-zinc-400 text-xs">
                    {migratePreview.target?.total_collections} collections | {migratePreview.target?.total_documents?.toLocaleString()} documents
                    {migratePreview.target?.is_empty && <span className="text-green-400 ml-1">(empty)</span>}
                  </p>
                  {!migratePreview.target?.is_empty && (
                    <div className="mt-2 bg-yellow-900/20 border border-yellow-800/30 rounded p-2">
                      <p className="text-yellow-400 text-xs">Target DB is not empty. Existing collections will be overwritten!</p>
                    </div>
                  )}
                  {migratePreview.target?.collections && Object.keys(migratePreview.target.collections).length > 0 && (
                    <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                      {Object.entries(migratePreview.target.collections).map(([name, count]) => (
                        <div key={name} className="flex justify-between text-xs">
                          <span className="text-zinc-400 truncate mr-2">{name}</span>
                          <span className="text-white font-mono">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-cyan-900/10 border border-cyan-800/20 rounded-lg p-3">
                <p className="text-cyan-300 text-sm">
                  Will migrate <span className="font-bold">{migratePreview.source?.total_documents?.toLocaleString()}</span> documents across <span className="font-bold">{migratePreview.source?.total_collections}</span> collections with indexes.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setMigrateStep('form')}>Back</Button>
                <Button
                  data-testid="migrate-execute-btn"
                  onClick={handleMigrateExecute}
                  disabled={migrateLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {migrateLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Execute Migration
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Migrating */}
          {migrateStep === 'migrating' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mb-4" />
              <p className="text-white font-medium">Migrating database...</p>
              <p className="text-zinc-500 text-sm mt-2">This may take a few minutes depending on data size.</p>
            </div>
          )}

          {/* Step 4: Done */}
          {migrateStep === 'done' && migrateResult && (
            <div className="space-y-4 py-4">
              <div className={`rounded-lg p-4 border ${migrateResult.success ? 'bg-green-900/20 border-green-800/30' : 'bg-yellow-900/20 border-yellow-800/30'}`}>
                <h4 className={`font-medium mb-2 ${migrateResult.success ? 'text-green-400' : 'text-yellow-400'}`}>
                  {migrateResult.success ? 'Migration Successful!' : 'Migration Completed with Errors'}
                </h4>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="bg-zinc-800/50 rounded p-2 text-center">
                    <p className="text-zinc-500 text-xs">Collections</p>
                    <p className="text-white font-bold">{migrateResult.total_collections}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded p-2 text-center">
                    <p className="text-zinc-500 text-xs">Documents</p>
                    <p className="text-white font-bold">{migrateResult.total_documents?.toLocaleString()}</p>
                  </div>
                  <div className="bg-zinc-800/50 rounded p-2 text-center">
                    <p className="text-zinc-500 text-xs">Indexes</p>
                    <p className="text-white font-bold">{migrateResult.total_indexes}</p>
                  </div>
                </div>
              </div>

              {/* Per-collection results */}
              <div className="max-h-60 overflow-y-auto space-y-1">
                {migrateResult.collections && Object.entries(migrateResult.collections).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between bg-zinc-800/30 rounded px-3 py-2 text-xs">
                    <span className="text-zinc-400">{name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-white">{info.documents} docs</span>
                      <span className="text-zinc-500">{info.indexes} idx</span>
                      {info.status === 'ok' ? (
                        <Check className="w-3 h-3 text-green-500" />
                      ) : (
                        <X className="w-3 h-3 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {migrateResult.errors?.length > 0 && (
                <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-3">
                  <h5 className="text-red-400 text-sm font-medium mb-1">Errors:</h5>
                  {migrateResult.errors.map((err, i) => (
                    <p key={i} className="text-red-300/70 text-xs">{err}</p>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={resetMigrateDialog}>Close</Button>
                <Button
                  onClick={() => {
                    setSwitchForm({
                      new_connection: migrateForm.target_connection,
                      new_db_name: migrateForm.target_db_name,
                      password: ''
                    });
                    resetMigrateDialog();
                    setShowSwitchDialog(true);
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Switch to This DB Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Switch Database Dialog */}
      <Dialog open={showSwitchDialog} onOpenChange={setShowSwitchDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-yellow-500" />
              Switch Active Database
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-3">
              <p className="text-yellow-300 text-sm">
                This will permanently update the .env file with the new database connection.
                The server will need to restart for changes to take effect.
              </p>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">New Connection String</label>
              <Input
                data-testid="switch-db-connection"
                value={switchForm.new_connection}
                onChange={(e) => setSwitchForm(prev => ({ ...prev, new_connection: e.target.value }))}
                placeholder="mongodb://username:password@host:27017"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">New Database Name</label>
              <Input
                data-testid="switch-db-name"
                value={switchForm.new_db_name}
                onChange={(e) => setSwitchForm(prev => ({ ...prev, new_db_name: e.target.value }))}
                placeholder="my_database"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Developer Password</label>
              <Input
                data-testid="switch-db-password"
                type="password"
                value={switchForm.password}
                onChange={(e) => setSwitchForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter developer password"
                className="bg-zinc-800 border-zinc-700"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowSwitchDialog(false)}>Cancel</Button>
            <Button
              data-testid="switch-db-confirm-btn"
              onClick={handleSwitchDatabase}
              disabled={switchLoading || !switchForm.new_connection || !switchForm.new_db_name || !switchForm.password}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {switchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Switch Database
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIN ADMIN COMPONENT ====================
export const Admin = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const { settings: siteSettings, loading: settingsLoading } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ticketStats, setTicketStats] = useState({ unread: 0 });

  // Fetch ticket stats for notification badge
  const fetchTicketStats = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/tickets/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketStats(res.data);
    } catch (error) {
      console.error('Failed to fetch ticket stats:', error);
    }
  };

  useEffect(() => {
    fetchTicketStats();
    // Poll for new tickets every 30 seconds
    const interval = setInterval(fetchTicketStats, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  const getTabTitle = () => {
    const allItems = menuGroups.flatMap(g => g.items);
    const item = allItems.find(i => i.id === activeTab);
    return item?.label || 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Sidebar - Fixed */}
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        ticketStats={ticketStats}
        siteSettings={siteSettings}
        settingsLoading={settingsLoading}
      />

      {/* Main Content - With left margin for sidebar */}
      <div className="lg:ml-64 min-h-screen flex flex-col">
        {/* Header - Fixed */}
        <div className="fixed top-0 right-0 left-0 lg:left-64 z-30">
          <AdminHeader 
            title={getTabTitle()}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
        </div>

        {/* Content Area - Scrollable with top padding for header */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 mt-16">
          {activeTab === 'dashboard' && <DashboardTab token={token} />}
          {activeTab === 'banners' && <BannersTab token={token} />}
          {activeTab === 'categories' && <CategoriesTab token={token} />}
          {activeTab === 'pools' && <PoolsTab token={token} />}
          {activeTab === 'shop' && <ShopTab token={token} />}
          {activeTab === 'pages' && <PagesTab token={token} />}
          {activeTab === 'users' && <UsersTab token={token} />}
          {activeTab === 'support-tickets' && <SupportTicketsTab token={token} onStatsUpdate={setTicketStats} />}
          {activeTab === 'deposits' && <DepositsTab token={token} />}
          {activeTab === 'vouchers' && <VouchersTab token={token} />}
          {activeTab === 'token-redemptions' && <TicketRedemptionsTab token={token} />}
          {activeTab === 'site' && <SiteSettingsTab token={token} />}
          {activeTab === 'levels' && <LevelsTab token={token} />}
          {activeTab === 'gacha' && <GachaSettingsTab token={token} />}
          {activeTab === 'seo' && <SEOTab token={token} />}
          {activeTab === 'solana' && <SolanaTab token={token} />}
          {activeTab === 'token' && <TokenSettingsTab token={token} />}
          {activeTab === 'storage' && <StorageTab token={token} />}
          {activeTab === 'staking' && <StakingTab token={token} />}
          {activeTab === 'nft' && <NFTSettingsTab token={token} />}
          {activeTab === 'mail' && <MailSettingsTab token={token} />}
          {activeTab === 'footer' && <FooterTab token={token} />}
          {activeTab === 'admins' && <AdminsTab token={token} />}
          {activeTab === 'developer' && <DeveloperTab token={token} />}
          {activeTab === 'shipping' && <ShippingTab token={token} />}
          {activeTab === 'swaps' && <SwapManagementTab token={token} />}
        </main>
      </div>
    </div>
  );
};

export default Admin;
